/* ---------------------------------------------------------------------------
   DEMONSTRATION DATA — PAKISTAN, FWO. NOT A REGISTER. DELETE THIS FILE.

   Everything in this file is FABRICATED, and it exists for one reason: to show
   the application with something in it to a Pakistani audience, before FWO has
   supplied a bridge list. It is temporary by agreement and this file is the
   whole of it — delete it, and the six call sites named at the bottom of this
   header, and the department is back to whatever FWO's own database holds,
   exactly as src/tenant/profiles/pk-fwo.ts describes.

   ── TWENTY-FIVE RECORDS IN THREE CLASSES, AND THEY ARE NOT EQUALLY REAL ──

   NINE NAMED STRUCTURES, scattered across six provinces and territories. These
   are real bridges in Pakistan: their NAME, the WATERCOURSE or feature they
   cross, the LOCALITY, the PROVINCE and — where the source carries one — the
   YEAR, the TOTAL LENGTH and the SUPERSTRUCTURE TYPE are taken from Wikipedia's
   "List of bridges in Pakistan" (read 2026-09-18). That is an encyclopaedia
   article, not an asset record: good enough to make a demonstration
   recognisable to somebody who knows the country, not good enough to quote
   anywhere else. Their COORDINATES are that article's own geotags, fetched
   through the MediaWiki API on the same day, for the seven of them carrying one;
   two do not, and theirs are taken from the nearest geotagged locality —
   Besham and the Karot project site — which puts them within a kilometre or
   two of the crossing rather than on it.

   FOURTEEN ON THE M-1, a single corridor from km 1 to km 148 of the
   Islamabad–Peshawar Motorway. These are WEAKER, and the banner above record 11
   sets out exactly how: what is real is the motorway, its length and opening
   year, the order of its interchanges, the districts it runs through and the
   feature each structure crosses — but NOT THE NAME. No source names the
   bridges on that road, so theirs are descriptions of a crossing. Their
   coordinates are points read off the motorway's line, not geotags. Read that
   banner before quoting anything from the corridor, including what a structure
   is called.

   AND TWO WHOLLY INVENTED ONES, PK-N5-1603 and PK-N5-1588, which carry
   the parts of a bridge's life the standing structures cannot (LC-70): the
   Indus Relief Bridge, PK-N5-1603, under construction — its programme, its
   works, its site record — and the N-5 overpass at km 1588, PK-N5-1588, built,
   handed over, inspected at handover and rated. Both are the one 3 × 25 m PSC
   design the model fixture draws, and so is Attock Bridge, which is now in
   service only. No work is recorded against BR-KHI-001 or any other structure
   FWO's own database holds.

   FABRICATED, which is everything else on every record in all three classes: the
   asset ids, the route codes, the chainages, the districts on the nine (the
   corridor's are real), the deck and carriageway widths, the span counts and
   span lengths, the deck levels and flood levels, the deck bearings, every
   inventory-sheet field (abutment and pier type, foundations,
   bearings, railings, movement joints, river training, running surface,
   services, designer, contractor), the traffic counts, the detours, the
   lifeline designations, EVERY INSPECTION, EVERY ELEMENT CONDITION RATING and
   EVERY DEFECT below. None of it was measured, surveyed, inspected or
   transcribed. It was invented to make the overview, the network summary, the
   route strips and the inventory sheet show what they look like when a register
   is populated.

   The deck levels deserve a specific warning, because they are the ones that
   look most like survey data: each is a plausible ground elevation at that
   crossing with a freeboard invented on top of it. A freeboard margin computed
   from them is arithmetic on fiction. The flood, scour and seismic screening the
   overview draws is the same — the SCREEN is real and does what it says, the
   INPUTS here are not.

   ── WHY THIS IS SAFE TO SHOW AND STILL SAFE TO REMOVE ──

   IT IS BEHIND A BUILD FLAG. `VITE_FWO_DEMO` has to be set for any of it to
   reach a screen — run-pakistan.bat sets it, and nothing else does. A build
   without it gets the empty register the profile describes, which is why
   `hasSeed('pk-fwo')` is still false under `npm test`, and why the
   empty-register regressions in scripts/smoke.ts still have a department to
   exercise. That was the deciding argument for the flag over a plain seed: the
   only coverage this codebase has of "a department on its first day" is
   `pk-fwo`, and a demonstration is not worth losing it.

   EVERY FIELD ON EVERY RECORD REPORTS ITS PROVENANCE AS `demonstration`, with a
   `D` mark beside it on the inventory sheet and a legend entry saying the value
   is invented — see DEMONSTRATION in src/data/provenance.ts. That is the one
   thing that must not be removed while the data stays: a fabricated register is
   defensible when it says so on its face and indefensible the moment it stops.

   NOTHING HERE IS WRITTEN TO D1. There is no seed migration for this department
   and there must not be one: the demonstration lives in the bundle, the
   production database stays clean, and removing it needs no migration to undo.
   The consequence is that a defect logged from the dialog against one of these
   structures has no row to attach itself to and the backend will refuse it —
   which is correct, and is the line between a demonstration and a register.

   ── IT IS ADDED TO D1'S ANSWER, NOT SUBSTITUTED FOR IT ──

   This was got wrong once and the correction is the interesting part. The first
   version relied on the ordinary seed fallback, which D1 overrides the moment
   the backend answers — the register's oldest rule, and the right one. That was
   fine only if FWO's database were empty. It is not: two real Karachi records
   (BR-KHI-001, BR-KHI-002) were entered on 2026-09-17, each carrying a defect.
   So the register showed ten structures before authentication and two after it,
   because signing in replaced the fallback with the database's own two rows.

   So `hydrate` in src/core/store.ts APPENDS these records to whatever D1
   returned, and `refreshAllDefects` does the same for the defect log. A real row
   always wins on id, so the two Karachi records are untouched and a structure
   FWO later imports under one of these ids replaces the fabricated twin rather
   than appearing beside it. The status bar then says "2 records from D1, plus 10
   demonstration records" rather than claiming twelve records from a database
   holding two — see `registerNote`, which exists for that sentence alone. (It
   said "plus 10" when this file held ten records and says "plus 23" now; the
   count is computed, not written down.)

   ── TO REMOVE ──

   Every site below carries a comment saying TEMPORARY and naming this file, so
   `grep -rn "fwo-demo" src scripts *.bat` finds the lot and nothing here can be
   deleted by halves.

     1. delete this file
     2. src/tenant/seeds.ts            — drop `bundled`; `seedFor` and `hasSeed`
                                         read SEEDS directly again
     3. src/core/store.ts              — four places: the merge in `hydrate`, the
                                         append in `refreshAllDefects`, the append
                                         and recomputed summary in
                                         `refreshProgress`, and `registerNote`,
                                         which collapses back to the two-line
                                         ternary it replaced
     3a. src/screens/works.ts          — the `isDemoProgress` import, the note
                                         above the entries, and the `photoUrl ??`
                                         branch on the thumbnail (with the alt
                                         text that goes with it)
     3d. src/core/store.ts             — a fifth place: the append and recomputed
                                         summary in `refreshWorks`, which is the
                                         period report rather than the record
     3e. src/screens/worksReport.ts    — the `isDemoProgress` import, the
                                         demonstration tag on section (a)'s
                                         head, and the `photoUrl` line at the
                                         top of `photoSrc`
     3b. src/data/types.ts             — drop `ProgressEntry.photoUrl` and `photoThumbUrl`
     3c. src/styles/app.css            — drop `.works__demo-note`
     4. src/data/provenance.ts         — drop DEMONSTRATION, DEMO_IDS and the
                                         branch that returns it
     5. src/tenant/profiles/pk-fwo.ts  — the two getters go back to being the
                                         plain strings in their `else` branches
     6. scripts/smoke.ts               — delete the five FWO-DEMO checks,
                                         `withDemo` and `withDemoAsync`
     7. run-pakistan.bat               — drop the VITE_FWO_DEMO line
     8. public/demo/                   — the construction photographs; see reliefPhotos
   --------------------------------------------------------------------------- */

import type {
  Bridge,
  ProgressEntry,
  ProgressSummary,
  RegisterDefect,
  SiteDocument,
  SiteIncident,
  SiteItem,
  SiteReturn,
  SiteTest,
} from './types.ts';
import type { Baseline, ScheduleMatch } from './variance.ts';
import { baselineFrom, baselineLinks, type SerialisedSchedule } from './programme.ts';
import type { WorkCalendar } from './calendar.ts';
import type { ContractExtension, ContractHeader } from './contract.ts';
import { idForLabel } from './componentTree.ts';
import type { Handover, HandoverFieldKey, HandoverValue } from './handover.ts';
import { failedTestTitle, itemRef, testPasses } from './siteQuality.ts';
import type { PhaseTransition, HandoverView } from '../core/api.ts';
import { DPWH_CBPLR, ratePcdgSimple, type PcdgInput } from '../cbplr/index.ts';
import type { RatingRecord } from '../rating/api.ts';
import { NO_LOSS, damageFromInspection } from '../rating/damage.ts';
import { postingTonnes } from './posting.ts';
/* The DPWH demonstration (Adlayan and Afga), read through this file's exports
   so the call sites stay the ones listed above. Every `dpwh` branch below goes
   with src/data/dpwh-demo.ts. */
import {
  dpwhDefectsFor,
  dpwhOwnsPhase,
  dpwhPhaseFor,
  dpwhProgressFor,
  dpwhProgressInPeriod,
  dpwhRatingsFor,
  dpwhScheduleFor,
  dpwhSiteRecordFor,
  isDpwhDemoField,
  withDpwhDemo,
} from './dpwh-demo.ts';

/**
 * Whether the demonstration data is in this build at all.
 *
 * Read from two places for one reason each, and wrapped the way `buildTenant()`
 * in src/tenant/registry.ts wraps its own: `import.meta.env` is the build's
 * answer and faults outside Vite, `process.env` is how the tests turn this on.
 *
 * DEFAULT OFF. A build that does not ask for demonstration data does not get it,
 * so the only way a fabricated record reaches a screen is a deliberate act.
 */
export function demoEnabled(): boolean {
  return on(readViteEnv()) || on(readNodeEnv());
}

function on(value: string | undefined): boolean {
  return value === '1' || value === 'true';
}

/** `VITE_FWO_DEMO` as the build baked it in. The browser path, and the real one. */
function readViteEnv(): string | undefined {
  try {
    return (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_FWO_DEMO;
  } catch {
    return undefined;
  }
}

/**
 * The same flag off `process.env`, which exists so the TESTS can turn the
 * demonstration on.
 *
 * `npm test` runs this module under Node with no Vite around it, so
 * `import.meta.env` is undefined there and the fabricated register would be
 * unreachable — which would leave the ten records, their screens and the
 * provenance marking checked by nothing at all. `process` is undefined in a
 * browser, so this branch costs a bundled build nothing but the try/catch it is
 * already wrapped in.
 */
function readNodeEnv(): string | undefined {
  try {
    return (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env
      ?.VITE_FWO_DEMO;
  } catch {
    return undefined;
  }
}

/* ------------------------ the twenty-five structures ---------------------- */

/**
 * The inventory-sheet fields no demonstration record pretends to hold.
 *
 * Much shorter than the equivalent in seed.ts, because the point of this file is
 * a POPULATED register — what is left null here is what a sheet genuinely leaves
 * blank on a structure of this kind.
 */
const BLANK = {
  overUnder: null,
  heightRestriction: null,
  leftBankDistrict: null,
  leftBankVdc: null,
  rightBankDistrict: null,
  rightBankVdc: null,
  roadSigns: null,
} as const;

/** The 0–4 condition scale every department in this build inspects on. */
const SCALE = '0-4 scale';

/**
 * Who made each fabricated record, one account per role (LC-72).
 *
 * The local development accounts `npm run dev` creates — see LOCAL_ROLE_ACCOUNTS
 * in scripts/vite-plugin-local-api.ts — so the separation of duties can be shown
 * on the demonstration and tried against it: the site agent records and the
 * resident engineer reviews, the inspector inspects and the engineer accepts,
 * the rater rates and the approver accepts, and never one person twice. Local
 * addresses on purpose, so nothing invented here is attributed to a person who
 * could be asked to stand behind it.
 */
export const DEMO_PEOPLE = {
  planner: 'planner@local',
  site: 'site@local',
  resident: 'resident@local',
  inspector: 'inspector@local',
  engineer: 'engineer@local',
  rater: 'rater@local',
  approver: 'approver@local',
} as const;
const PEOPLE = DEMO_PEOPLE;

/**
 * FWO's profile declares no `extras`, so the bag is empty on every record — a
 * key here would be one nothing reads. See the note on `tenantFields` in
 * src/data/types.ts for why it is spread rather than left off.
 */
const NO_EXTRAS = { tenantFields: {} as Readonly<Record<string, string | number | null>> };

export const FWO_DEMO_BRIDGES: Bridge[] = [
  /* ===== 1. Lansdowne Bridge — Indus at Sukkur–Rohri. 1889, 250 m, cantilever steel. ===== */
  {
    id: 'PK-N5-0621',
    name: 'Lansdowne Bridge',
    route: 'N-5',
    roadName: 'Karachi–Torkham (Grand Trunk) Road',
    km: 468.2,
    district: 'Sukkur',
    province: 'Sindh',
    river: 'Indus',
    type: 'Riveted steel cantilever truss',
    spans: 1,
    length: 250.0,
    deckWidth: 6.1,
    carriageway: 5.0,
    year: 1889,
    deckRL: 78.4,
    hfl: 71.2,
    lat: 27.6944,
    lng: 68.8865,
    deckBearing: 68.0,
    inventoryCode: 'FWO-SND-SUK-001',
    roadLength: 1819.0,
    divisionOffice: 'Maintenance Unit — Sukkur',
    loadRestriction: 15,
    designLoad: 'Original 1889 loading, never re-rated',
    spanLength: 250.0,
    abutmentType: 'Masonry gravity abutment on rock',
    abutmentFoundation: 'Spread footing on rock',
    pierType: 'Masonry tower pier',
    pierFoundation: 'Spread footing on rock',
    bearings: 'Steel rocker and roller, fixed at the Rohri tower',
    railings: 'Riveted steel lattice parapet',
    movement: 'Finger joint at both abutments',
    riverTraining: 'Stone pitching to both banks',
    runningSurface: 'Asphalt over steel trough deck',
    services: 'Water main on the downstream truss',
    designedBy: 'Sir Alexander Meadows Rendel',
    constructedBy: 'Westwood, Baillie & Co.',
    aadt: 9400,
    aadtYear: 2025,
    detourKm: 3.2,
    detourMinutes: 18,
    lifeline: true,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-0621-A',
        date: '2026-04-12',
        type: 'principal',
        rating: 3,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-04-28T09:12:00Z',
        note:
          'Section loss through the lower-chord gusset plates at panels 4 and 5, both trusses. ' +
          'Deck trough corroded through in three places over the Rohri tower. The 15 t load ' +
          'restriction is confirmed as still necessary.',
        elements: [
          { element: 'deck', rating: 3, extent: 2, urgency: 3, note: 'Trough plate perforated over the tower.' },
          { element: 'deck_surface', rating: 2, extent: 2, urgency: 2 },
          { element: 'bearings', rating: 3, extent: 1, urgency: 3, note: 'Roller nest seized on the Sukkur side.' },
          { element: 'parapet', rating: 2, extent: 3, urgency: 1 },
          { element: 'joints', rating: 2, extent: 2, urgency: 2 },
          { element: 'abutment', rating: 1, extent: 1, urgency: 0 },
          { element: 'pier', rating: 1, extent: 1, urgency: 0 },
          { element: 'protection', rating: 2, extent: 2, urgency: 2 },
          { element: 'river_training', rating: 'N' },
          { element: 'approach', rating: 1, extent: 1, urgency: 1 },
        ],
      },
      {
        id: 'INSP-PK-0621-B',
        date: '2025-03-30',
        type: 'routine',
        rating: 2,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2025-04-09T11:40:00Z',
        note: 'Paint system failing on the windward face. No new section loss since the 2023 visit.',
        elements: [
          { element: 'deck', rating: 2, extent: 2, urgency: 2 },
          { element: 'bearings', rating: 2, extent: 1, urgency: 2 },
          { element: 'parapet', rating: 2, extent: 3, urgency: 1 },
        ],
      },
    ],
  },

  /* ===== 2. Kotri Bridge — Indus at Kotri–Hyderabad. 1900, steel truss. ===== */
  {
    id: 'PK-N5-0154',
    name: 'Kotri Bridge',
    route: 'N-5',
    roadName: 'Karachi–Torkham (Grand Trunk) Road',
    km: 165.4,
    district: 'Jamshoro',
    province: 'Sindh',
    river: 'Indus',
    type: 'Riveted steel through truss, multi-span',
    spans: 11,
    length: 690.0,
    deckWidth: 7.6,
    carriageway: 6.4,
    year: 1900,
    deckRL: 27.8,
    hfl: 23.1,
    lat: 25.3727,
    lng: 68.3131,
    deckBearing: 112.0,
    inventoryCode: 'FWO-SND-JAM-002',
    roadLength: 1819.0,
    divisionOffice: 'Maintenance Unit — Hyderabad',
    loadRestriction: 20,
    designLoad: 'Original 1900 loading, never re-rated',
    spanLength: 62.0,
    abutmentType: 'Masonry gravity abutment',
    abutmentFoundation: 'Well foundation',
    pierType: 'Masonry pier on twin wells',
    pierFoundation: 'Well foundation',
    bearings: 'Steel rocker and roller',
    railings: 'Steel lattice parapet, partly replaced in mild steel',
    movement: 'Sliding plate joint at each pier',
    riverTraining: 'Spurs upstream of the left bank',
    runningSurface: 'Asphalt over reinforced concrete deck slab',
    services: 'Low-tension power on the upstream face',
    designedBy: 'North Western State Railway',
    constructedBy: null,
    aadt: 14200,
    aadtYear: 2025,
    detourKm: 11.8,
    detourMinutes: 35,
    lifeline: true,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-0154-A',
        date: '2026-08-02',
        type: 'routine',
        rating: 2,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-08-14T08:05:00Z',
        note:
          'Deck slab delaminating in the right-hand wheel track between piers 4 and 7. The joint ' +
          'at pier 6 is packed with silt and no longer moving.',
        elements: [
          { element: 'deck_surface', rating: 3, extent: 2, urgency: 3 },
          { element: 'deck', rating: 2, extent: 2, urgency: 2 },
          { element: 'joints', rating: 3, extent: 1, urgency: 3, note: 'Pier 6 joint seized.' },
          { element: 'bearings', rating: 2, extent: 2, urgency: 2 },
          { element: 'pier', rating: 2, extent: 1, urgency: 1 },
          { element: 'abutment', rating: 1, extent: 1, urgency: 0 },
          { element: 'protection', rating: 2, extent: 2, urgency: 2 },
          { element: 'parapet', rating: 2, extent: 2, urgency: 1 },
        ],
      },
      {
        id: 'INSP-PK-0154-B',
        date: '2026-02-19',
        type: 'underwater',
        rating: 2,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-03-04T10:22:00Z',
        note:
          'Dive survey of piers 3 to 8 at low flow. Local scour 2.4 m below bed level at pier 5, ' +
          'within the well curb. No undermining found elsewhere.',
        elements: [
          { element: 'pier', rating: 2, extent: 1, urgency: 2, note: 'Pier 5 scour hole, 2.4 m.' },
          { element: 'protection', rating: 3, extent: 2, urgency: 3 },
        ],
      },
      {
        id: 'INSP-PK-0154-C',
        date: '2023-11-08',
        type: 'principal',
        rating: 2,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2023-11-30T14:00:00Z',
        note:
          'Full principal inspection with access equipment. Truss steelwork sound; the deck slab ' +
          'is the weakness and will govern the next intervention.',
      },
    ],
  },

  /* ===== 3. Jinnah Bridge — China Creek at Karachi. 2007, cable-stayed. ===== */
  {
    id: 'PK-KHI-0007',
    name: 'Jinnah Bridge',
    /* NO ROUTE CODE. The Karachi Port approach is a city road and this register
       holds no national code for it, so the road classification groups it under
       *not recorded* rather than inventing one. Worth seeing on a demonstration:
       it is what the application does with a gap. */
    route: null,
    roadName: 'Mai Kolachi Bypass (Karachi Port access)',
    km: 3.4,
    district: 'Karachi South',
    province: 'Sindh',
    river: 'China Creek',
    type: 'Cable-stayed, single pylon',
    spans: 3,
    length: 195.0,
    deckWidth: 24.0,
    carriageway: 21.0,
    year: 2007,
    deckRL: 14.2,
    hfl: 4.6,
    lat: 24.8427,
    lng: 66.9903,
    deckBearing: 35.0,
    inventoryCode: 'FWO-SND-KHI-003',
    roadLength: 8.4,
    divisionOffice: 'Maintenance Unit — Karachi',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 95.0,
    abutmentType: 'Reinforced concrete wall abutment',
    abutmentFoundation: 'Bored pile',
    pierType: 'Reinforced concrete pylon',
    pierFoundation: 'Bored pile',
    bearings: 'Pot bearings, guided and free',
    railings: 'Steel post and rail with pedestrian mesh',
    movement: 'Modular expansion joint at both abutments',
    riverTraining: 'Rock revetment to both creek banks',
    runningSurface: 'Asphalt over reinforced concrete deck',
    services: 'Street lighting and a communications duct',
    designedBy: null,
    constructedBy: null,
    aadt: 38600,
    aadtYear: 2026,
    detourKm: 6.5,
    detourMinutes: 40,
    lifeline: true,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-0007-A',
        date: '2026-06-24',
        type: 'routine',
        rating: 1,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-07-02T09:30:00Z',
        note:
          'Structure in good order. Chloride staining beginning at the deck drainage outlets — ' +
          'a marine-exposure problem to watch rather than a defect yet.',
        elements: [
          { element: 'deck', rating: 1, extent: 1, urgency: 1 },
          { element: 'deck_surface', rating: 1, extent: 1, urgency: 0 },
          { element: 'bearings', rating: 1, extent: 0, urgency: 0 },
          { element: 'joints', rating: 1, extent: 1, urgency: 1 },
          { element: 'pier', rating: 1, extent: 1, urgency: 1 },
          { element: 'abutment', rating: 0, extent: 0, urgency: 0 },
          { element: 'parapet', rating: 1, extent: 1, urgency: 0 },
          { element: 'protection', rating: 1, extent: 1, urgency: 1 },
          { element: 'approach', rating: 1, extent: 1, urgency: 1 },
        ],
      },
      {
        id: 'INSP-PK-0007-B',
        date: '2024-05-16',
        type: 'principal',
        rating: 1,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2024-06-01T13:45:00Z',
        note: 'Stay-cable anchorages opened and inspected. No water ingress. Next principal due 2029.',
      },
    ],
  },

  /* ===== 4. Attock Bridge — Indus at Attock Khurd. Three 25 m PSC I-girder spans, 75 m. =====
     DESCRIBED AS THE MODEL IMPORTED AGAINST IT, not as the 1883 rail-over-road
     truss of the same name: the rating and the 3D view read that model, so the
     register says what they show. IN SERVICE ONLY (LC-70) — built 1998,
     inspected and rated in 2024, restricted by that rating. Its construction
     story is the Indus Relief Bridge's, below. */
  {
    id: 'PK-N5-1602',
    name: 'Attock Bridge',
    route: 'N-5',
    roadName: 'Karachi–Torkham (Grand Trunk) Road',
    km: 1602.5,
    district: 'Attock',
    province: 'Punjab',
    river: 'Indus',
    type: 'PSC I-girder, simply supported',
    spans: 3,
    length: 75.0,
    deckWidth: 11.0,
    carriageway: 7.6,
    year: 1998,
    deckRL: 341.6,
    hfl: 334.2,
    lat: 33.8725,
    lng: 72.2367,
    deckBearing: 22.0,
    inventoryCode: 'FWO-PNJ-ATK-004',
    roadLength: 1819.0,
    divisionOffice: 'Maintenance Unit — Attock',
    /* The accepted rating's posting, set where that rating is defined (LC-23). */
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 25.0,
    abutmentType: 'RC cantilever abutment with bearing beam',
    abutmentFoundation: 'Spread footing on rock',
    pierType: 'Two RC columns with bent cap',
    pierFoundation: 'Two four-pile caps on bored piles',
    bearings: 'Laminated elastomeric, one under each girder end',
    railings: 'Concrete Jersey barrier with footway each side',
    movement: 'Strip-seal expansion joint at each pier and abutment',
    riverTraining: 'None — rock gorge',
    runningSurface: '50 mm asphalt over RC deck slab',
    services: 'None',
    designedBy: null,
    constructedBy: null,
    aadt: 6800,
    aadtYear: 2024,
    detourKm: 4.1,
    detourMinutes: 12,
    lifeline: true,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-1602-A',
        date: '2024-10-15',
        type: 'principal',
        rating: 3,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2024-10-29T11:00:00Z',
        note:
          'Longitudinal cracking and spalling along the soffit of exterior girder G1 at midspan of ' +
          'span 2, with nine of its eighteen strands exposed and corroded through. The load ' +
          'restriction was brought down from 15 t on the strength of this visit and the rating that followed it.',
        elements: [
          { element: 'deck', rating: 3, extent: 2, urgency: 3 },
          { element: 'deck_surface', rating: 3, extent: 3, urgency: 3 },
          { element: 'bearings', rating: 3, extent: 2, urgency: 3 },
          { element: 'joints', rating: 2, extent: 2, urgency: 2 },
          { element: 'pier', rating: 1, extent: 1, urgency: 0 },
          { element: 'abutment', rating: 1, extent: 1, urgency: 0 },
          { element: 'parapet', rating: 3, extent: 3, urgency: 2 },
          { element: 'river_training', rating: 'N' },
          { element: 'protection', rating: 'N' },
        ],
      },
      {
        /* THE ROUTINE VISIT THAT WENT WITH THAT PRINCIPAL, and it is here for a
           reason worth keeping. Without it this record's routine cycle runs from
           the year of completion — and the screen then correctly reports the
           first routine inspection as having been due in January 1999, which is
           true, is what the register should say about a structure with no
           routine visit ever recorded, and reads on a demonstration like a
           broken date. Raikot below is the record that deliberately keeps that
           state; one of them is a feature and two are a bug report. */
        id: 'INSP-PK-1602-B',
        date: '2024-10-15',
        type: 'routine',
        rating: 3,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2024-10-29T11:00:00Z',
        note:
          'Walked with the principal inspection party the same day. Wearing course broken up ' +
          'over the deck slab for most of span 3.',
      },
    ],
  },

  /* ===== 4a. Indus Relief Bridge (invented) — under construction beside Attock. =====
     WHOLLY INVENTED, name and all, and it says so in the name (LC-70). It is
     the construction story Attock used to carry as a second life: the same
     3 × 25 m PSC design the model fixture draws, 140 days into a 245-day
     contract, with the programme, the works, the daily returns, incidents,
     documents, tests and register that go with it (RELIEF_WORKS and below).
     Nothing on it is in service, so it has no inspection and no rating. */
  {
    id: 'PK-N5-1603',
    name: 'Indus Relief Bridge',
    route: 'N-5',
    roadName: 'Karachi–Torkham (Grand Trunk) Road',
    km: 1603.4,
    district: 'Attock',
    province: 'Punjab',
    river: 'Indus',
    type: 'PSC I-girder, simply supported',
    spans: 3,
    length: 75.0,
    deckWidth: 11.0,
    carriageway: 7.6,
    year: null,
    deckRL: 342.1,
    hfl: 334.2,
    lat: 33.8683,
    lng: 72.2411,
    deckBearing: 22.0,
    inventoryCode: 'FWO-PNJ-IRB-001',
    roadLength: 1819.0,
    divisionOffice: 'Maintenance Unit — Attock',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 25.0,
    abutmentType: 'RC cantilever abutment with bearing beam',
    abutmentFoundation: 'Spread footing',
    pierType: 'Two RC columns with bent cap',
    pierFoundation: 'Two pile caps of 4 piles, 1.0 m diameter, 12.5 m long',
    bearings: 'Laminated elastomeric, one under each girder end',
    railings: 'Concrete Jersey barrier with footway each side',
    movement: 'Strip-seal expansion joint at each pier and abutment',
    riverTraining: 'Scour protection at P1 and P2, bank pitching at A1 and A2',
    runningSurface: '50 mm asphalt over RC deck slab',
    services: 'None',
    designedBy: null,
    constructedBy: 'Indus Bridgeworks JV',
    aadt: 6800,
    aadtYear: 2024,
    detourKm: 4.1,
    detourMinutes: 12,
    lifeline: false,
    ...BLANK,
    ...NO_EXTRAS,
    /* Under construction since its first baseline was accepted — the phase row
       is DEMO_PHASES'; the day it moved is slid with the works (demoBridgesFor). */
    phase: 'construction',
    restricted: false,
    inspections: [],
  },

  /* ===== 4b. N-5 Overpass at km 1588 (invented) — built, handed over, rated. =====
     WHOLLY INVENTED too, and the rest of the life in a compact form (LC-70):
     an accepted baseline, a dozen accepted works, a handover prepared by the
     resident engineer and accepted by the approver with the as-built model of
     record, the baseline inspection on the handover date, and a first rating
     that posts nothing. The same 3 × 25 m design over a road, not a river. */
  {
    id: 'PK-N5-1588',
    name: 'N-5 Overpass at km 1588',
    route: 'N-5',
    roadName: 'Karachi–Torkham (Grand Trunk) Road',
    km: 1588.2,
    district: 'Attock',
    province: 'Punjab',
    river: null,
    type: 'PSC I-girder flyover, simply supported',
    spans: 3,
    length: 75.0,
    deckWidth: 11.0,
    carriageway: 7.6,
    year: 2025,
    deckRL: 355.8,
    hfl: null,
    lat: 33.8321,
    lng: 72.3546,
    deckBearing: 64.0,
    inventoryCode: 'FWO-PNJ-OVP-1588',
    roadLength: 1819.0,
    divisionOffice: 'Maintenance Unit — Attock',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 25.0,
    abutmentType: 'RC cantilever abutment with bearing beam',
    abutmentFoundation: 'Spread footing',
    pierType: 'Two RC columns with bent cap',
    pierFoundation: 'Two pile caps of 4 piles, 1.0 m diameter, 12.5 m long',
    bearings: 'Alternating pin and roller',
    railings: 'Concrete Jersey barrier with footway each side',
    movement: 'Strip-seal expansion joint at each pier and abutment',
    riverTraining: 'None — grade separation',
    runningSurface: '50 mm asphalt over RC deck slab',
    services: 'Street lighting on both footways',
    designedBy: null,
    constructedBy: 'Margalla Civil Works',
    aadt: 9200,
    aadtYear: 2025,
    detourKm: 2.6,
    detourMinutes: 7,
    lifeline: false,
    ...BLANK,
    ...NO_EXTRAS,
    phase: 'in-service',
    restricted: false,
    phaseAt: '2025-10-24T11:00:00Z',
    phaseBy: PEOPLE.approver,
    /* The handover that took it into service, and the as-built model it made the
       model of record — OVERPASS_HANDOVER below is the whole of it. */
    handover: {
      id: 'demo-site-handover-1588',
      completedOn: '2025-10-15',
      dlpEndsOn: '2026-10-15',
      acceptedAt: '2025-10-24T11:00:00Z',
      acceptedBy: PEOPLE.approver,
      modelId: 'demo-model-1588-asbuilt',
      modelFields: ['spans', 'length', 'spanLength', 'deckWidth', 'girders', 'bearings', 'abutmentFoundation', 'pierFoundation'],
      enteredFields: [],
      girders: 5,
    },
    modelOfRecord: {
      modelId: 'demo-model-1588-asbuilt',
      kind: 'as-built',
      setAt: '2025-10-24T11:00:00Z',
      setBy: PEOPLE.approver,
    },
    inspections: [
      {
        id: 'INSP-PK-1588-A',
        date: '2025-10-15',
        type: 'baseline',
        rating: 0,
        scale: SCALE,
        state: 'accepted',
        inspector: PEOPLE.inspector,
        reviewer: PEOPLE.engineer,
        reviewedAt: '2025-10-28T09:40:00Z',
        note:
          'Baseline inspection at handover. Every element as built; bearings seated, joints sealed, ' +
          'no cracking found on any girder soffit.',
        elements: [
          { element: 'deck', rating: 0, extent: 0, urgency: 0 },
          { element: 'deck_surface', rating: 0, extent: 0, urgency: 0 },
          { element: 'bearings', rating: 0, extent: 0, urgency: 0 },
          { element: 'joints', rating: 0, extent: 0, urgency: 0 },
          { element: 'pier', rating: 0, extent: 0, urgency: 0 },
          { element: 'abutment', rating: 0, extent: 0, urgency: 0 },
          { element: 'parapet', rating: 0, extent: 0, urgency: 0 },
          { element: 'river_training', rating: 'N' },
          { element: 'protection', rating: 'N' },
        ],
      },
    ],
  },

  /* ===== 5. Indus River Bridge — the M-1's Indus crossing, near Ghazi. ===== */
  /* THIS RECORD WAS CALLED "NEW KHAIRABAD BRIDGE" AND IT SHOULD NOT HAVE BEEN.
     New Khairabad Bridge is a real 2004 structure over the Indus at Khairabad,
     beside the old Attock Bridge — and it is on the N-5, not on this motorway.
     Put on the M-1 with the Attock geotag it carried, it sat WEST of the
     structure at the chainage before it, so the corridor's markers ran
     backwards on the map the moment there were enough of them to see the
     order — the M-1 does not go near Khairabad.

     So it keeps its (fabricated) dimensions and its inspections and loses the
     borrowed name: the M-1 does cross the Indus, near Ghazi below Tarbela, and
     that is the crossing this record now describes. It is therefore one of the
     corridor records below by provenance — feature real, NAME DESCRIPTIVE —
     and no longer one of the nine carrying a real bridge's name. Its id and
     chainage moved with it; nothing else on it changed. */
  {
    id: 'PK-M1-0086',
    name: 'Indus River Bridge',
    route: 'M-1',
    roadName: 'Islamabad–Peshawar Motorway',
    km: 86.3,
    district: 'Swabi',
    province: 'Khyber Pakhtunkhwa',
    river: 'Indus',
    type: 'PSC single-cell box girder, balanced cantilever',
    spans: 7,
    length: 701.0,
    deckWidth: 12.2,
    carriageway: 11.0,
    year: 2007,
    deckRL: 348.9,
    hfl: 339.4,
    /* WHERE THE MOTORWAY'S CENTRELINE MEETS THE INDUS, measured off the road
       rather than estimated — see WHERE THE POSITIONS COME FROM in the M-1
       corridor banner below. Not a geotag: there is none to have. */
    lat: 33.9954,
    lng: 72.4241,
    deckBearing: 140.0,
    inventoryCode: 'FWO-KPK-SWB-005',
    roadLength: 155.0,
    divisionOffice: 'Maintenance Unit — Swabi',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 110.0,
    abutmentType: 'Reinforced concrete wall abutment',
    abutmentFoundation: 'Bored pile',
    pierType: 'Reinforced concrete twin-leaf pier',
    pierFoundation: 'Bored pile',
    bearings: 'Pot bearings, fixed at pier 4',
    railings: 'Reinforced concrete barrier with steel rail',
    movement: 'Modular expansion joint at both abutments',
    riverTraining: 'Gabion apron at both abutments',
    runningSurface: 'Asphalt wearing course on waterproofing',
    services: 'Motorway communications duct inside the box',
    designedBy: null,
    constructedBy: null,
    aadt: 21500,
    aadtYear: 2025,
    detourKm: 9.4,
    detourMinutes: 22,
    lifeline: true,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-0086-A',
        date: '2026-05-21',
        type: 'routine',
        rating: 1,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-06-03T07:55:00Z',
        note:
          'Transverse cracking in the deck soffit either side of the pier 4 diaphragm, widest ' +
          '0.4 mm. Drainage outlets at piers 2 and 5 blocked.',
        elements: [
          { element: 'deck', rating: 1, extent: 1, urgency: 1 },
          { element: 'deck_surface', rating: 1, extent: 1, urgency: 1 },
          { element: 'bearings', rating: 1, extent: 1, urgency: 1 },
          { element: 'joints', rating: 2, extent: 1, urgency: 2 },
          { element: 'pier', rating: 1, extent: 1, urgency: 0 },
          { element: 'abutment', rating: 1, extent: 1, urgency: 0 },
          { element: 'protection', rating: 2, extent: 2, urgency: 2 },
          { element: 'parapet', rating: 1, extent: 1, urgency: 0 },
          { element: 'approach', rating: 1, extent: 1, urgency: 1 },
        ],
      },
      {
        id: 'INSP-PK-0086-B',
        date: '2022-09-14',
        type: 'principal',
        rating: 1,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2022-10-05T12:00:00Z',
        note:
          'Box interior walked end to end. Tendon ducts sound and no grout voids found on the ' +
          'sample survey.',
      },
    ],
  },

  /* ===== 6. Kohala Bridge — Jhelum at Kohala. Masonry arch, year not recorded. ===== */
  {
    id: 'PK-N75-0088',
    name: 'Kohala Bridge',
    route: 'N-75',
    roadName: 'Islamabad–Muzaffarabad Road',
    km: 88.3,
    district: 'Rawalpindi',
    province: 'Punjab',
    river: 'Jhelum',
    type: 'Masonry arch, single span',
    spans: 1,
    length: 64.0,
    deckWidth: 6.4,
    carriageway: 5.5,
    /* THE SOURCE GIVES NO YEAR, so the register carries none — and the
       inspection-standing card then has to say so rather than dating the gap
       from a year nobody wrote down. */
    year: null,
    deckRL: 612.4,
    hfl: 606.8,
    lat: 34.0961,
    lng: 73.499,
    deckBearing: 95.0,
    inventoryCode: 'FWO-PNJ-RWP-006',
    roadLength: 138.0,
    divisionOffice: 'Maintenance Unit — Murree',
    loadRestriction: 10,
    designLoad: null,
    spanLength: 64.0,
    abutmentType: 'Masonry springing on rock',
    abutmentFoundation: 'Spread footing on rock',
    pierType: 'None — single span',
    pierFoundation: 'None — single span',
    bearings: 'None — arch',
    railings: 'Masonry parapet wall',
    movement: 'None',
    riverTraining: 'None — rock gorge',
    runningSurface: 'Asphalt over arch fill',
    services: 'Low-tension power on timber poles',
    designedBy: null,
    constructedBy: null,
    aadt: 3100,
    aadtYear: 2023,
    detourKm: 42.0,
    detourMinutes: 95,
    lifeline: true,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-0088-A',
        date: '2025-06-11',
        type: 'routine',
        rating: 3,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2025-06-25T10:15:00Z',
        note:
          'Mortar loss through the arch barrel over roughly a third of the intrados, and two ' +
          'voussoirs dropped at the left-bank springing. The parapet is displaced outward by ' +
          'about 60 mm at mid-span.',
        elements: [
          { element: 'deck', rating: 3, extent: 3, urgency: 3, note: 'Arch barrel mortar loss.' },
          { element: 'deck_surface', rating: 2, extent: 2, urgency: 2 },
          { element: 'abutment', rating: 3, extent: 2, urgency: 3 },
          { element: 'parapet', rating: 3, extent: 2, urgency: 3 },
          { element: 'approach', rating: 2, extent: 2, urgency: 2 },
          { element: 'bearings', rating: 'N' },
          { element: 'joints', rating: 'N' },
          { element: 'pier', rating: 'N' },
          { element: 'river_training', rating: 'N' },
        ],
      },
    ],
  },

  /* ===== 7. Karot Bridge — Jhelum at Kahuta–Hollar. 2019, 330 m, PSC box girder. ===== */
  {
    id: 'PK-AJK-0219',
    name: 'Karot Bridge',
    /* Kahuta–Hollar is a local road with no national code on this register. */
    route: null,
    roadName: 'Kahuta–Hollar Road',
    km: 27.6,
    district: 'Rawalpindi',
    province: 'Punjab',
    river: 'Jhelum',
    type: 'PSC single-cell box girder',
    spans: 3,
    length: 330.0,
    deckWidth: 11.6,
    carriageway: 10.0,
    year: 2019,
    deckRL: 468.2,
    hfl: 461.9,
    /* No geotag on the bridge article — this is the Karot project site, about a
       kilometre from the crossing. See the COORDINATES note in the header. */
    lat: 33.5998,
    lng: 73.6012,
    deckBearing: 128.0,
    inventoryCode: 'FWO-PNJ-RWP-007',
    roadLength: 46.0,
    divisionOffice: 'Maintenance Unit — Kahuta',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 150.0,
    abutmentType: 'Reinforced concrete wall abutment',
    abutmentFoundation: 'Spread footing on rock',
    pierType: 'Reinforced concrete solid shaft pier',
    pierFoundation: 'Spread footing on rock',
    bearings: 'Pot bearings, fixed at pier 1',
    railings: 'Reinforced concrete barrier',
    movement: 'Finger joint at both abutments',
    riverTraining: 'Rock revetment to the right bank',
    runningSurface: 'Asphalt wearing course on waterproofing',
    services: 'Street lighting',
    designedBy: null,
    constructedBy: null,
    aadt: 2400,
    aadtYear: 2024,
    detourKm: 58.0,
    detourMinutes: 130,
    lifeline: false,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-0219-A',
        date: '2026-09-04',
        type: 'routine',
        rating: 1,
        scale: SCALE,
        /* RECORDED A FORTNIGHT AGO AND NOT YET SIGNED OFF, so the review
           workflow has something in it on the record screen. */
        state: 'review',
        note:
          'First visit since handover. Shrinkage cracking in the right-bank abutment wing wall, ' +
          'no movement. Bearing plinths clean and dry.',
        elements: [
          { element: 'deck', rating: 0, extent: 0, urgency: 0 },
          { element: 'deck_surface', rating: 1, extent: 1, urgency: 0 },
          { element: 'bearings', rating: 0, extent: 0, urgency: 0 },
          { element: 'joints', rating: 1, extent: 1, urgency: 1 },
          { element: 'abutment', rating: 1, extent: 1, urgency: 1 },
          { element: 'pier', rating: 0, extent: 0, urgency: 0 },
          { element: 'protection', rating: 1, extent: 1, urgency: 1 },
          { element: 'parapet', rating: 0, extent: 0, urgency: 0 },
        ],
      },
    ],
  },

  /* ===== 8. Kund Bridge — Indus at Besham. 188 m, PSC box girder, year not recorded. ===== */
  {
    id: 'PK-N35-0182',
    name: 'Kund Bridge',
    route: 'N-35',
    roadName: 'Karakoram Highway (Hasan Abdal–Khunjerab)',
    km: 182.5,
    district: 'Shangla',
    province: 'Khyber Pakhtunkhwa',
    river: 'Indus',
    type: 'PSC single-cell box girder',
    spans: 3,
    length: 188.0,
    deckWidth: 11.0,
    carriageway: 9.0,
    year: null,
    deckRL: 614.3,
    hfl: 608.1,
    /* Besham locality geotag, not the crossing — see the header. */
    lat: 34.9375,
    lng: 72.8792,
    deckBearing: 74.0,
    inventoryCode: 'FWO-KPK-SHG-008',
    roadLength: 806.0,
    divisionOffice: 'Maintenance Unit — Besham',
    loadRestriction: null,
    designLoad: null,
    spanLength: 76.0,
    abutmentType: 'Reinforced concrete wall abutment',
    abutmentFoundation: 'Spread footing on rock',
    pierType: 'Reinforced concrete solid shaft pier',
    pierFoundation: 'Spread footing on rock',
    bearings: 'Elastomeric bearings, unrestrained',
    railings: 'Steel post and rail',
    movement: 'Compression seal joint at both abutments',
    riverTraining: 'Gabion apron to the left bank',
    runningSurface: 'Asphalt wearing course',
    services: 'None',
    designedBy: null,
    constructedBy: null,
    aadt: 4700,
    aadtYear: 2024,
    detourKm: 210.0,
    detourMinutes: 420,
    lifeline: true,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-0182-A',
        date: '2025-09-27',
        type: 'special',
        rating: 3,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2025-10-08T06:40:00Z',
        note:
          'Called out after the August flood peak. The left-bank gabion apron is stripped over ' +
          'about 35 m and the abutment toe is exposed. Superstructure undamaged.',
        elements: [
          { element: 'protection', rating: 4, extent: 3, urgency: 4, note: 'Apron gone, toe exposed.' },
          { element: 'abutment', rating: 2, extent: 1, urgency: 3 },
          { element: 'river_training', rating: 3, extent: 3, urgency: 4 },
          { element: 'deck', rating: 1, extent: 0, urgency: 0 },
          { element: 'bearings', rating: 1, extent: 1, urgency: 1 },
        ],
      },
      {
        id: 'INSP-PK-0182-B',
        date: '2024-04-18',
        type: 'routine',
        rating: 2,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2024-05-02T09:00:00Z',
        note: 'Rockfall damage to the parapet on the northern approach. No structural defects noted.',
      },
    ],
  },

  /* ===== 9. Earthquake Memorial Bridge — Jhelum at Muzaffarabad. 2014, 473 m, extradosed. ===== */
  {
    id: 'PK-AJK-0044',
    name: 'Earthquake Memorial Bridge',
    route: null,
    roadName: 'Muzaffarabad–Mansehra Road',
    km: 2.8,
    district: 'Muzaffarabad',
    province: 'Azad Jammu & Kashmir',
    river: 'Jhelum',
    type: 'Extradosed prestressed concrete, twin pylon',
    spans: 3,
    length: 473.0,
    deckWidth: 13.0,
    carriageway: 11.0,
    year: 2014,
    deckRL: 705.5,
    hfl: 699.2,
    lat: 34.3479,
    lng: 73.4638,
    deckBearing: 58.0,
    inventoryCode: 'FWO-AJK-MZD-009',
    roadLength: 118.0,
    divisionOffice: 'Maintenance Unit — Muzaffarabad',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 190.0,
    abutmentType: 'Reinforced concrete wall abutment',
    abutmentFoundation: 'Bored pile',
    pierType: 'Reinforced concrete pylon',
    pierFoundation: 'Bored pile',
    bearings: 'Pot bearings with seismic restrainers',
    railings: 'Reinforced concrete barrier with steel rail',
    movement: 'Modular expansion joint at both abutments',
    riverTraining: 'Rock revetment to both banks',
    runningSurface: 'Asphalt wearing course on waterproofing',
    services: 'Street lighting and a water main',
    designedBy: null,
    constructedBy: null,
    aadt: 17800,
    aadtYear: 2025,
    detourKm: 5.2,
    detourMinutes: 25,
    lifeline: true,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-0044-A',
        date: '2026-07-30',
        type: 'routine',
        rating: 1,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-08-11T11:05:00Z',
        note: 'Stay saddle covers intact. Minor spalling at the north approach slab joint.',
        elements: [
          { element: 'deck', rating: 1, extent: 1, urgency: 1 },
          { element: 'deck_surface', rating: 1, extent: 1, urgency: 1 },
          { element: 'joints', rating: 2, extent: 1, urgency: 2 },
          { element: 'bearings', rating: 1, extent: 1, urgency: 1 },
          { element: 'pier', rating: 1, extent: 1, urgency: 0 },
          { element: 'abutment', rating: 1, extent: 1, urgency: 1 },
          { element: 'approach', rating: 2, extent: 1, urgency: 2 },
          { element: 'protection', rating: 1, extent: 1, urgency: 1 },
          { element: 'parapet', rating: 1, extent: 1, urgency: 0 },
        ],
      },
      {
        id: 'INSP-PK-0044-B',
        date: '2023-03-09',
        type: 'principal',
        rating: 1,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2023-03-27T08:30:00Z',
        note: 'Seismic restrainers checked and re-torqued. Extradosed cables inspected from the deck.',
      },
    ],
  },

  /* ===== 10. Raikot Bridge — Indus in Diamer. 2013, steel arch. NEVER INSPECTED. ===== */
  {
    id: 'PK-N35-0334',
    name: 'Raikot Bridge',
    route: 'N-35',
    roadName: 'Karakoram Highway (Hasan Abdal–Khunjerab)',
    km: 334.7,
    district: 'Diamer',
    province: 'Gilgit-Baltistan',
    river: 'Indus',
    type: 'Tied steel arch',
    spans: 1,
    length: 140.0,
    deckWidth: 8.0,
    carriageway: 7.0,
    year: 2013,
    deckRL: 1212.0,
    hfl: 1205.4,
    lat: 35.4931,
    lng: 74.592,
    deckBearing: 160.0,
    inventoryCode: 'FWO-GB-DIA-010',
    roadLength: 806.0,
    divisionOffice: 'Maintenance Unit — Chilas',
    loadRestriction: 25,
    designLoad: null,
    spanLength: 140.0,
    abutmentType: 'Reinforced concrete wall abutment on rock',
    abutmentFoundation: 'Spread footing on rock',
    pierType: 'None — single span',
    pierFoundation: 'None — single span',
    bearings: 'Pot bearings, fixed at the south abutment',
    railings: 'Steel post and rail',
    movement: 'Compression seal joint at both abutments',
    riverTraining: 'None — rock gorge',
    runningSurface: 'Asphalt over steel orthotropic deck',
    services: 'None',
    designedBy: null,
    constructedBy: null,
    /* NO TRAFFIC COUNT AND NO DETOUR WORKED OUT. One record with an empty
       consequence block, so the overview's coverage tile reads 22 of 23 rather
       than a suspiciously complete 23 — which is what a real register looks
       like, and what the tile exists to report. */
    aadt: null,
    aadtYear: null,
    detourKm: null,
    detourMinutes: null,
    lifeline: null,
    ...BLANK,
    ...NO_EXTRAS,
    /* NEVER INSPECTED. Every register has one, and the inspection-standing card
       has to be able to say so without inventing a date. */
    inspections: [],
  },

  /* =========================================================================
     THE M-1 CORRIDOR — thirteen more structures on one motorway.

     WHY A CORRIDOR AND NOT THIRTEEN MORE PINS ON A NATIONAL MAP. The first ten
     records are one famous bridge per province, which shows the register and
     the map and shows nothing else: the network screen draws a ROUTE STRIP per
     road, and a strip with one marker on it is a diagram of nothing. Worse, a
     road is broken into separate strips wherever the register holds nothing for
     more than STRIP_BREAK_KM — 25 km, see `routeStrips` in src/data/derive.ts —
     so the three N-5 structures, over a thousand kilometres apart, draw as
     three strips of one.

     These thirteen sit between km 1 and km 148 of the 155 km Islamabad–Peshawar
     Motorway, never more than 23 km apart, so the whole M-1 draws as ONE strip
     with FOURTEEN markers on it — the fourteenth being record 5 above, the
     motorway's Indus crossing, which was already here. That is what the route
     strip, the road rollup, the corridor extent and the spread of condition
     along a single road were all built to show, and until now this department
     had nothing to show them with.

     ── PROVENANCE, WHICH IS WEAKER HERE THAN ABOVE, AND SAYS SO ──

     The first ten records carry real bridge NAMES from an encyclopaedia
     article. These thirteen do not, because no such list exists for this road:
     Wikipedia's "M-1 motorway (Pakistan)" (read 2026-09-21) gives the length
     (155 km), the opening (30 October 2007), the sequence of interchanges, the
     districts, and the sentence "There are three major bridges along the route
     along the Haro, Indus and Kabul rivers, 18 flyovers, 27 small bridges, 137
     underpasses and 571 culverts" — and it names not one of them.

     REAL, and only this: the MOTORWAY and its length and opening year, the
     ORDER OF ITS INTERCHANGES, the DISTRICTS and PROVINCES it runs through, and
     the FEATURE each structure crosses. The Kabul, the Indus, the Haro and the
     Ghazi Barotha power channel are that article's own four; the Kalpani, the
     Budhni Nala and the Karachi–Peshawar main line are the alignment's own.

     ── WHERE THE POSITIONS COME FROM, AND WHY THEY WERE WRONG FIRST TIME ──

     COORDINATES, CHAINAGES AND DECK BEARINGS ARE MEASURED OFF THE REAL ROAD.
     The motorway's carriageways were fetched from OpenStreetMap through the
     Overpass API on 2026-09-21 — `way[highway=motorway][ref~"M.?1$"]` over the
     corridor's bounding box, 159 ways and 1,850 nodes — the two carriageways
     averaged into one centreline, and chainage measured along it from the
     Peshawar end: 150.5 km end to end, against the 155 km the article
     publishes. Every structure below then sits at a REAL POINT ON THAT ROAD —
     an interchange, a river crossing or a railway crossing, each from that same
     query or its companions for waterways and railways — and carries the
     motorway's own bearing there.

     THE FIRST VERSION OF THIS CORRIDOR WAS PLACED BY HAND, from the interchange
     names and a mental map, and it was wrong by 1 to 9 km — worst at the
     eastern end, where the markers left the road altogether. It read perfectly
     well as a list and was obviously wrong the moment anybody opened the map,
     which is the whole argument for measuring rather than estimating even a
     fabricated position. The chainages moved with the coordinates: the Indus
     crossing is at km 86, not 85; the Haro at 113, not 106; the Ghazi Barotha
     channel at 103, not 95.

     WHAT IS STILL NOT SURVEYED: a structure sits where the centreline meets the
     feature, which is the middle of the crossing to within a few tens of metres.
     That is not an abutment coordinate and not a position to set out from.
     OpenStreetMap is contributed data, not a national survey: good enough to
     put a marker on the right bridge, and good for nothing beyond that.

     INVENTED is everything the first ten invent — asset ids, chainages,
     dimensions, deck and flood levels, every inventory-sheet field, every
     inspection, rating and defect — plus ONE THING MORE: the NAME. "Haro River
     Bridge", "Wali Interchange Flyover" and the rest are DESCRIPTIONS of a
     crossing, not names any source gives it; FWO's own list will call them
     something else, most likely a structure number. Nothing on these records
     may be quoted, and that now includes what they are called.
     ========================================================================= */

  /* ===== 11. Peshawar Ring Road Flyover — M-1 km 1. Grade separation, western end. ===== */
  {
    id: 'PK-M1-0001',
    name: 'Peshawar Ring Road Flyover',
    route: 'M-1',
    roadName: 'Islamabad–Peshawar Motorway',
    km: 1.4,
    district: 'Peshawar',
    province: 'Khyber Pakhtunkhwa',
    /* NO WATERCOURSE AND NO FLOOD LEVEL, WHICH IS THE RECORD BEING HONEST
       rather than the record being incomplete. A flyover over a road has no
       high flood level, and a plausible number here would feed a fabricated
       freeboard into the flood screen for a structure that crosses no water.
       Every grade separation below is null the same way. */
    river: null,
    type: 'PSC I-girder flyover, simply supported',
    spans: 4,
    length: 96.0,
    deckWidth: 12.2,
    carriageway: 11.0,
    year: 2007,
    deckRL: 341.6,
    hfl: null,
    lat: 34.0259,
    lng: 71.6413,
    deckBearing: 84.0,
    inventoryCode: 'FWO-KPK-PEW-011',
    roadLength: 155.0,
    divisionOffice: 'Maintenance Unit — Peshawar',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 24.0,
    abutmentType: 'Reinforced concrete wall abutment',
    abutmentFoundation: 'Spread footing',
    pierType: 'Reinforced concrete single-column pier with hammerhead',
    pierFoundation: 'Spread footing',
    bearings: 'Elastomeric, laminated',
    railings: 'Reinforced concrete barrier with steel rail',
    movement: 'Strip seal joint at both abutments',
    riverTraining: null,
    runningSurface: 'Asphalt wearing course on waterproofing',
    services: 'Motorway communications duct in the verge',
    designedBy: null,
    constructedBy: null,
    aadt: 24800,
    aadtYear: 2025,
    /* A GRADE SEPARATION HAS A SHORT DETOUR, and saying so is half the reason
       the field is held: the consequence screen has to be able to separate the
       flyover you can drive round from the river crossing you cannot. */
    detourKm: 2.6,
    detourMinutes: 9,
    lifeline: false,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-M1-001-A',
        date: '2026-03-18',
        type: 'routine',
        rating: 1,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-04-02T06:40:00Z',
        note:
          'Approach slab settled about 15 mm at the Peshawar abutment, felt as a bump at speed. ' +
          'Everything else sound.',
        elements: [
          { element: 'approach', rating: 2, extent: 1, urgency: 2, note: 'Step at the abutment joint.' },
          { element: 'deck', rating: 0, extent: 0, urgency: 0 },
          { element: 'deck_surface', rating: 1, extent: 1, urgency: 1 },
          { element: 'bearings', rating: 1, extent: 1, urgency: 0 },
          { element: 'joints', rating: 1, extent: 1, urgency: 1 },
          { element: 'pier', rating: 0, extent: 0, urgency: 0 },
          { element: 'abutment', rating: 1, extent: 1, urgency: 0 },
          { element: 'parapet', rating: 1, extent: 1, urgency: 0 },
          { element: 'river_training', rating: 'N' },
          { element: 'protection', rating: 'N' },
        ],
      },
    ],
  },

  /* ===== 12. Kabul River Bridge — M-1 km 14. One of the article's three major crossings. ===== */
  {
    id: 'PK-M1-0014',
    name: 'Kabul River Bridge',
    route: 'M-1',
    roadName: 'Islamabad–Peshawar Motorway',
    km: 14.3,
    district: 'Charsadda',
    province: 'Khyber Pakhtunkhwa',
    river: 'Kabul',
    type: 'PSC single-cell box girder, balanced cantilever',
    spans: 9,
    length: 540.0,
    deckWidth: 12.2,
    carriageway: 11.0,
    year: 2007,
    deckRL: 306.8,
    hfl: 299.4,
    lat: 34.0956,
    lng: 71.7397,
    deckBearing: 31.0,
    inventoryCode: 'FWO-KPK-CHD-012',
    roadLength: 155.0,
    divisionOffice: 'Maintenance Unit — Peshawar',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 60.0,
    abutmentType: 'Reinforced concrete wall abutment',
    abutmentFoundation: 'Bored pile',
    pierType: 'Reinforced concrete twin-leaf pier',
    pierFoundation: 'Bored pile in well casing',
    bearings: 'Pot bearings, fixed at pier 5',
    railings: 'Reinforced concrete barrier with steel rail',
    movement: 'Modular expansion joint at both abutments',
    riverTraining: 'Spur dykes upstream of both abutments, stone pitched',
    runningSurface: 'Asphalt wearing course on waterproofing',
    services: 'Motorway communications duct inside the box',
    designedBy: null,
    constructedBy: null,
    aadt: 23900,
    aadtYear: 2025,
    detourKm: 21.5,
    detourMinutes: 38,
    lifeline: true,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-M1-014-A',
        date: '2026-02-24',
        type: 'principal',
        rating: 2,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-03-16T08:20:00Z',
        note:
          'Efflorescence and rust staining on the soffit below the pier 3 and pier 6 diaphragms, ' +
          'tracked back to blocked deck drainage. The left bank spur dyke has lost pitching over ' +
          'about 18 m after the August flows.',
        elements: [
          { element: 'deck', rating: 2, extent: 2, urgency: 2, note: 'Soffit staining at two diaphragms.' },
          { element: 'deck_surface', rating: 1, extent: 1, urgency: 1 },
          { element: 'bearings', rating: 1, extent: 1, urgency: 1 },
          { element: 'joints', rating: 2, extent: 2, urgency: 2 },
          { element: 'pier', rating: 1, extent: 1, urgency: 1 },
          { element: 'abutment', rating: 1, extent: 1, urgency: 0 },
          { element: 'protection', rating: 2, extent: 2, urgency: 2 },
          { element: 'river_training', rating: 3, extent: 2, urgency: 3, note: 'Pitching lost, left bank spur.' },
          { element: 'parapet', rating: 1, extent: 1, urgency: 0 },
          { element: 'approach', rating: 1, extent: 1, urgency: 1 },
        ],
      },
      {
        id: 'INSP-PK-M1-014-B',
        date: '2024-11-08',
        type: 'routine',
        rating: 1,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2024-11-25T10:05:00Z',
        note: 'Nothing new. Drainage outlets rodded at piers 3 and 6; they were already slow then.',
      },
    ],
  },

  /* ===== 13. Charsadda Interchange Flyover — M-1 km 20. NEVER INSPECTED. ===== */
  {
    id: 'PK-M1-0020',
    name: 'Charsadda Interchange Flyover',
    route: 'M-1',
    roadName: 'Islamabad–Peshawar Motorway',
    km: 19.9,
    district: 'Charsadda',
    province: 'Khyber Pakhtunkhwa',
    river: null,
    type: 'PSC I-girder flyover, simply supported',
    spans: 3,
    length: 72.0,
    deckWidth: 11.6,
    carriageway: 10.4,
    year: 2007,
    deckRL: 304.8,
    hfl: null,
    lat: 34.1179,
    lng: 71.7852,
    deckBearing: 72.0,
    inventoryCode: 'FWO-KPK-CHD-013',
    roadLength: 155.0,
    divisionOffice: 'Maintenance Unit — Peshawar',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 24.0,
    abutmentType: 'Reinforced concrete wall abutment',
    abutmentFoundation: 'Spread footing',
    pierType: 'Reinforced concrete single-column pier with hammerhead',
    pierFoundation: 'Spread footing',
    bearings: 'Elastomeric, laminated',
    railings: 'Reinforced concrete barrier with steel rail',
    movement: 'Strip seal joint at both abutments',
    riverTraining: null,
    runningSurface: 'Asphalt wearing course on waterproofing',
    services: 'Motorway communications duct in the verge',
    designedBy: null,
    constructedBy: null,
    aadt: 22400,
    aadtYear: 2025,
    detourKm: 3.1,
    detourMinutes: 11,
    lifeline: false,
    ...BLANK,
    ...NO_EXTRAS,
    /* A SECOND STRUCTURE WITH NO INSPECTION, and deliberately one in the middle
       of a corridor rather than at the far end of the country. Raikot above is
       easy to read as remote; a flyover 23 km outside Peshawar that nobody has
       been to is what an inspection-standing report is actually for, and it is
       the gap the corridor strip should make visible at a glance. */
    inspections: [],
  },

  /* ===== 14. Kalpani Bridge — M-1 km 43. Kalpani river, beside Rashakai interchange. ===== */
  {
    id: 'PK-M1-0043',
    name: 'Kalpani Bridge',
    route: 'M-1',
    roadName: 'Islamabad–Peshawar Motorway',
    km: 42.9,
    district: 'Nowshera',
    province: 'Khyber Pakhtunkhwa',
    river: 'Kalpani',
    type: 'Reinforced concrete T-beam, simply supported',
    spans: 6,
    length: 138.0,
    deckWidth: 11.6,
    carriageway: 10.4,
    year: 2007,
    deckRL: 297.2,
    hfl: 292.6,
    lat: 34.1059,
    lng: 72.0276,
    deckBearing: 104.0,
    inventoryCode: 'FWO-KPK-NOW-014',
    roadLength: 155.0,
    divisionOffice: 'Maintenance Unit — Nowshera',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 23.0,
    abutmentType: 'Reinforced concrete wall abutment',
    abutmentFoundation: 'Spread footing on gravel',
    pierType: 'Reinforced concrete wall pier',
    pierFoundation: 'Spread footing on gravel',
    bearings: 'Elastomeric, laminated',
    railings: 'Reinforced concrete barrier with steel rail',
    movement: 'Strip seal joint at both abutments',
    riverTraining: 'Stone pitching to both banks, gabion toe',
    runningSurface: 'Asphalt wearing course on waterproofing',
    services: 'Motorway communications duct in the verge',
    designedBy: null,
    constructedBy: null,
    aadt: 22100,
    aadtYear: 2025,
    detourKm: 7.8,
    detourMinutes: 19,
    lifeline: false,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-M1-043-A',
        date: '2026-06-09',
        type: 'principal',
        rating: 2,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-06-30T09:45:00Z',
        note:
          'Spalling to the soffit of beams 2 and 3 in span 4, with reinforcement exposed. ' +
          'Carbonation suspected rather than chloride: cover measured at 22 mm against the ' +
          '40 mm specified.',
        elements: [
          { element: 'deck', rating: 3, extent: 2, urgency: 3, note: 'Spalled soffit, span 4, two beams.' },
          { element: 'deck_surface', rating: 2, extent: 2, urgency: 1 },
          { element: 'bearings', rating: 2, extent: 2, urgency: 1 },
          { element: 'joints', rating: 2, extent: 2, urgency: 2 },
          { element: 'pier', rating: 1, extent: 1, urgency: 1 },
          { element: 'abutment', rating: 1, extent: 1, urgency: 1 },
          { element: 'protection', rating: 2, extent: 2, urgency: 2 },
          { element: 'river_training', rating: 2, extent: 2, urgency: 2 },
          { element: 'parapet', rating: 1, extent: 1, urgency: 0 },
          { element: 'approach', rating: 2, extent: 1, urgency: 2 },
        ],
      },
      {
        id: 'INSP-PK-M1-043-B',
        date: '2025-05-19',
        type: 'routine',
        rating: 2,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2025-06-02T11:15:00Z',
        note: 'Soffit cracking mapped in span 4. No spalling yet; a principal inspection was asked for.',
        elements: [
          { element: 'deck', rating: 2, extent: 2, urgency: 2 },
          { element: 'deck_surface', rating: 2, extent: 2, urgency: 1 },
          { element: 'joints', rating: 2, extent: 1, urgency: 2 },
        ],
      },
    ],
  },

  /* ===== 15. Wali Interchange Flyover — M-1 km 52. ===== */
  {
    id: 'PK-M1-0052',
    name: 'Wali Interchange Flyover',
    route: 'M-1',
    roadName: 'Islamabad–Peshawar Motorway',
    km: 52.1,
    district: 'Swabi',
    province: 'Khyber Pakhtunkhwa',
    river: null,
    type: 'PSC I-girder flyover, simply supported',
    spans: 3,
    length: 78.0,
    deckWidth: 11.6,
    carriageway: 10.4,
    year: 2007,
    deckRL: 302.7,
    hfl: null,
    lat: 34.0649,
    lng: 72.1102,
    deckBearing: 89.0,
    inventoryCode: 'FWO-KPK-SWB-015',
    roadLength: 155.0,
    divisionOffice: 'Maintenance Unit — Swabi',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 26.0,
    abutmentType: 'Reinforced concrete wall abutment',
    abutmentFoundation: 'Spread footing',
    pierType: 'Reinforced concrete single-column pier with hammerhead',
    pierFoundation: 'Spread footing',
    bearings: 'Elastomeric, laminated',
    railings: 'Reinforced concrete barrier with steel rail',
    movement: 'Strip seal joint at both abutments',
    riverTraining: null,
    runningSurface: 'Asphalt wearing course on waterproofing',
    services: 'Motorway communications duct in the verge',
    designedBy: null,
    constructedBy: null,
    aadt: 20600,
    aadtYear: 2025,
    detourKm: 2.9,
    detourMinutes: 10,
    lifeline: false,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-M1-052-A',
        date: '2026-03-19',
        type: 'routine',
        rating: 1,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-04-02T06:55:00Z',
        note: 'Sound. The wearing course is beginning to ravel over the joints at both abutments.',
        elements: [
          { element: 'deck', rating: 0, extent: 0, urgency: 0 },
          { element: 'deck_surface', rating: 1, extent: 2, urgency: 1 },
          { element: 'bearings', rating: 1, extent: 1, urgency: 0 },
          { element: 'joints', rating: 1, extent: 2, urgency: 1 },
          { element: 'pier', rating: 0, extent: 0, urgency: 0 },
          { element: 'abutment', rating: 1, extent: 1, urgency: 0 },
          { element: 'parapet', rating: 1, extent: 1, urgency: 0 },
          { element: 'river_training', rating: 'N' },
          { element: 'protection', rating: 'N' },
          { element: 'approach', rating: 1, extent: 1, urgency: 1 },
        ],
      },
    ],
  },

  /* ===== 16. Kernal Sher Khan Interchange Flyover — M-1 km 62. Over the M-16. ===== */
  {
    id: 'PK-M1-0062',
    name: 'Kernal Sher Khan Interchange Flyover',
    route: 'M-1',
    roadName: 'Islamabad–Peshawar Motorway',
    km: 61.8,
    district: 'Swabi',
    province: 'Khyber Pakhtunkhwa',
    river: null,
    type: 'PSC I-girder flyover, simply supported',
    spans: 5,
    length: 168.0,
    deckWidth: 12.2,
    carriageway: 11.0,
    year: 2007,
    deckRL: 312.5,
    hfl: null,
    lat: 34.0685,
    lng: 72.2130,
    deckBearing: 88.0,
    inventoryCode: 'FWO-KPK-SWB-016',
    roadLength: 155.0,
    divisionOffice: 'Maintenance Unit — Swabi',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 33.6,
    abutmentType: 'Reinforced concrete wall abutment on reinforced earth',
    abutmentFoundation: 'Spread footing',
    pierType: 'Reinforced concrete single-column pier with hammerhead',
    pierFoundation: 'Bored pile',
    bearings: 'Pot bearings, fixed at pier 2',
    railings: 'Reinforced concrete barrier with steel rail',
    movement: 'Strip seal joint at both abutments',
    riverTraining: null,
    runningSurface: 'Asphalt wearing course on waterproofing',
    services: 'Motorway communications duct in the verge; HT cable on the northern parapet',
    designedBy: null,
    constructedBy: null,
    aadt: 19800,
    aadtYear: 2025,
    detourKm: 4.4,
    detourMinutes: 12,
    lifeline: false,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-M1-062-A',
        date: '2026-03-20',
        type: 'routine',
        rating: 1,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-04-02T07:10:00Z',
        note:
          'Reinforced earth panels behind the Peshawar abutment show a 20 mm step at one vertical ' +
          'joint. Marked and to be watched; nothing else to report.',
        elements: [
          { element: 'deck', rating: 0, extent: 0, urgency: 0 },
          { element: 'deck_surface', rating: 1, extent: 1, urgency: 1 },
          { element: 'bearings', rating: 1, extent: 1, urgency: 0 },
          { element: 'joints', rating: 1, extent: 1, urgency: 1 },
          { element: 'pier', rating: 0, extent: 0, urgency: 0 },
          { element: 'abutment', rating: 2, extent: 1, urgency: 2, note: 'Step at a panel joint.' },
          { element: 'parapet', rating: 1, extent: 1, urgency: 0 },
          { element: 'river_training', rating: 'N' },
          { element: 'protection', rating: 'N' },
          { element: 'approach', rating: 1, extent: 1, urgency: 1 },
        ],
      },
    ],
  },

  /* ===== 17. Budhni Nala Bridge — M-1 km 74. The worst standing on this corridor. ===== */
  {
    id: 'PK-M1-0074',
    name: 'Budhni Nala Bridge',
    route: 'M-1',
    roadName: 'Islamabad–Peshawar Motorway',
    km: 74.2,
    district: 'Swabi',
    province: 'Khyber Pakhtunkhwa',
    river: 'Budhni Nala',
    type: 'PSC I-girder, simply supported',
    spans: 4,
    length: 92.0,
    deckWidth: 11.6,
    carriageway: 10.4,
    year: 2007,
    deckRL: 324.7,
    hfl: 319.8,
    lat: 34.0701,
    lng: 72.3518,
    deckBearing: 92.0,
    inventoryCode: 'FWO-KPK-SWB-017',
    roadLength: 155.0,
    divisionOffice: 'Maintenance Unit — Swabi',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 23.0,
    abutmentType: 'Reinforced concrete wall abutment',
    abutmentFoundation: 'Spread footing on gravel',
    pierType: 'Reinforced concrete wall pier',
    pierFoundation: 'Spread footing on gravel',
    bearings: 'Elastomeric, laminated',
    railings: 'Reinforced concrete barrier with steel rail',
    movement: 'Strip seal joint at both abutments',
    riverTraining: 'Gabion apron to both banks, launching apron at pier 2',
    runningSurface: 'Asphalt wearing course on waterproofing',
    services: 'Motorway communications duct in the verge',
    designedBy: null,
    constructedBy: null,
    aadt: 19400,
    aadtYear: 2025,
    detourKm: 9.1,
    detourMinutes: 21,
    lifeline: false,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-M1-074-A',
        date: '2026-07-02',
        type: 'principal',
        rating: 3,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-07-21T05:50:00Z',
        note:
          'Scour at pier 2 opened up in the July flows. The footing underside is exposed over ' +
          'roughly a third of its width and the launching apron has gone. Traffic restricted to ' +
          '60 km/h over the structure pending bed protection.',
        elements: [
          { element: 'pier', rating: 3, extent: 2, urgency: 4, note: 'Pier 2 footing undermined.' },
          { element: 'protection', rating: 4, extent: 3, urgency: 4, note: 'Launching apron lost at pier 2.' },
          { element: 'river_training', rating: 3, extent: 3, urgency: 3 },
          { element: 'deck', rating: 1, extent: 1, urgency: 1 },
          { element: 'deck_surface', rating: 2, extent: 2, urgency: 1 },
          { element: 'bearings', rating: 2, extent: 1, urgency: 2 },
          { element: 'joints', rating: 2, extent: 2, urgency: 2 },
          { element: 'abutment', rating: 2, extent: 1, urgency: 2 },
          { element: 'parapet', rating: 1, extent: 1, urgency: 0 },
          { element: 'approach', rating: 2, extent: 1, urgency: 2 },
        ],
      },
      {
        /* A DIVED INSPECTION THAT FOLLOWS THE PRINCIPAL ONE rather than standing
           on its own, which is the order a scour finding actually produces: the
           July visit saw the apron gone and could not see the bed. Raikot above
           carries the register's other underwater inspection, so the scour
           screen now has two, on two different roads. Left in `review` — one
           record in this register waiting on a reviewer, so the review queue is
           not empty on a demonstration. */
        id: 'INSP-PK-M1-074-B',
        date: '2026-08-14',
        type: 'underwater',
        rating: 3,
        scale: SCALE,
        state: 'review',
        note:
          'Probed at pier 2 in 1.4 m of water. Scour hole 2.6 m below bed level at the upstream ' +
          'nose, undercutting the footing by about 0.9 m. Piers 1 and 3 sound. Bed protection ' +
          'estimated at 340 m³ of rip-rap.',
        elements: [
          { element: 'pier', rating: 3, extent: 2, urgency: 4 },
          { element: 'protection', rating: 4, extent: 3, urgency: 4 },
          { element: 'abutment', rating: 1, extent: 1, urgency: 1 },
        ],
      },
    ],
  },

  /* ===== 18. Ghazi Barotha Canal Bridge — M-1 km 103. Over the power channel. ===== */
  {
    id: 'PK-M1-0103',
    name: 'Ghazi Barotha Canal Bridge',
    route: 'M-1',
    roadName: 'Islamabad–Peshawar Motorway',
    km: 102.8,
    district: 'Attock',
    province: 'Punjab',
    /* THE FEATURE IS REAL AND IT IS NOT A RIVER. The article says the motorway
       "travels over the Ghazi Barotha canal", the power channel feeding the
       Ghazi-Barotha hydropower project. `river` is the register's word for
       whatever is crossed, and a lined channel with a controlled level is the
       one case on this corridor where the flood level below is a REGULATED
       level rather than a flood — which is why the inspection says so. */
    river: 'Ghazi Barotha power channel',
    type: 'PSC I-girder, simply supported',
    spans: 5,
    length: 132.0,
    deckWidth: 12.2,
    carriageway: 11.0,
    year: 2007,
    deckRL: 359.2,
    hfl: 353.6,
    lat: 33.8976,
    lng: 72.5431,
    deckBearing: 150.0,
    inventoryCode: 'FWO-PNJ-ATK-018',
    roadLength: 155.0,
    divisionOffice: 'Maintenance Unit — Attock',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 26.4,
    abutmentType: 'Reinforced concrete wall abutment',
    abutmentFoundation: 'Spread footing',
    pierType: 'Reinforced concrete wall pier',
    pierFoundation: 'Spread footing',
    bearings: 'Elastomeric, laminated',
    railings: 'Reinforced concrete barrier with steel rail',
    movement: 'Strip seal joint at both abutments',
    riverTraining: 'None — lined channel, concrete revetment both sides',
    runningSurface: 'Asphalt wearing course on waterproofing',
    services: 'Motorway communications duct in the verge',
    designedBy: null,
    constructedBy: null,
    aadt: 20900,
    aadtYear: 2025,
    detourKm: 12.7,
    detourMinutes: 26,
    lifeline: false,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-M1-103-A',
        date: '2026-04-23',
        type: 'routine',
        rating: 1,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-05-11T08:05:00Z',
        note:
          'Channel level is controlled, so there is no scour regime here and the banks are ' +
          'concrete. Deck and substructure sound. Bearing shims at pier 3 show light corrosion.',
        elements: [
          { element: 'deck', rating: 1, extent: 1, urgency: 0 },
          { element: 'deck_surface', rating: 1, extent: 1, urgency: 1 },
          { element: 'bearings', rating: 2, extent: 1, urgency: 1, note: 'Shim corrosion at pier 3.' },
          { element: 'joints', rating: 1, extent: 1, urgency: 1 },
          { element: 'pier', rating: 1, extent: 1, urgency: 0 },
          { element: 'abutment', rating: 1, extent: 1, urgency: 0 },
          { element: 'protection', rating: 1, extent: 1, urgency: 0 },
          { element: 'river_training', rating: 'N' },
          { element: 'parapet', rating: 1, extent: 1, urgency: 0 },
          { element: 'approach', rating: 1, extent: 1, urgency: 1 },
        ],
      },
    ],
  },

  /* ===== 19. Hazara Interchange Flyover — M-1 km 112. Over the M-15. ===== */
  {
    id: 'PK-M1-0112',
    name: 'Hazara Interchange Flyover',
    route: 'M-1',
    roadName: 'Islamabad–Peshawar Motorway',
    km: 112.1,
    district: 'Attock',
    province: 'Punjab',
    river: null,
    type: 'PSC I-girder flyover, simply supported',
    spans: 6,
    length: 186.0,
    deckWidth: 12.2,
    carriageway: 11.0,
    year: 2007,
    deckRL: 496.2,
    hfl: null,
    lat: 33.8474,
    lng: 72.6209,
    deckBearing: 152.0,
    inventoryCode: 'FWO-PNJ-ATK-019',
    roadLength: 155.0,
    divisionOffice: 'Maintenance Unit — Attock',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 31.0,
    abutmentType: 'Reinforced concrete wall abutment on reinforced earth',
    abutmentFoundation: 'Spread footing',
    pierType: 'Reinforced concrete single-column pier with hammerhead',
    pierFoundation: 'Bored pile',
    bearings: 'Pot bearings, fixed at pier 3',
    railings: 'Reinforced concrete barrier with steel rail',
    movement: 'Strip seal joint at both abutments',
    riverTraining: null,
    runningSurface: 'Asphalt wearing course on waterproofing',
    services: 'Motorway communications duct in the verge',
    designedBy: null,
    constructedBy: null,
    /* THE BUSIEST STRUCTURE ON THE CORRIDOR, because the M-15, the N-5 at
       Burhan and the main railway line all meet the motorway inside four
       kilometres here. A corridor whose traffic is flat along its whole length
       is the one thing a motorway register never looks like. */
    aadt: 27600,
    aadtYear: 2025,
    detourKm: 5.8,
    detourMinutes: 16,
    lifeline: false,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-M1-112-A',
        date: '2026-04-24',
        type: 'routine',
        rating: 1,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-05-11T08:20:00Z',
        note:
          'Rutting in the nearside lane over spans 2 and 3, about 14 mm under a 2 m straightedge. ' +
          'Structure itself sound.',
        elements: [
          { element: 'deck_surface', rating: 2, extent: 2, urgency: 2, note: 'Rutting, nearside lane.' },
          { element: 'deck', rating: 1, extent: 1, urgency: 0 },
          { element: 'bearings', rating: 1, extent: 1, urgency: 0 },
          { element: 'joints', rating: 1, extent: 1, urgency: 1 },
          { element: 'pier', rating: 1, extent: 1, urgency: 0 },
          { element: 'abutment', rating: 1, extent: 1, urgency: 0 },
          { element: 'parapet', rating: 1, extent: 1, urgency: 0 },
          { element: 'river_training', rating: 'N' },
          { element: 'protection', rating: 'N' },
          { element: 'approach', rating: 1, extent: 1, urgency: 1 },
        ],
      },
    ],
  },

  /* ===== 20. Haro River Bridge — M-1 km 113. The third of the article's major crossings. ===== */
  {
    id: 'PK-M1-0113',
    name: 'Haro River Bridge',
    route: 'M-1',
    roadName: 'Islamabad–Peshawar Motorway',
    km: 113.3,
    district: 'Attock',
    province: 'Punjab',
    river: 'Haro',
    type: 'PSC single-cell box girder, span-by-span',
    spans: 7,
    length: 392.0,
    deckWidth: 12.2,
    carriageway: 11.0,
    year: 2007,
    deckRL: 486.3,
    hfl: 478.9,
    lat: 33.8373,
    lng: 72.6240,
    deckBearing: 158.0,
    inventoryCode: 'FWO-PNJ-ATK-020',
    roadLength: 155.0,
    divisionOffice: 'Maintenance Unit — Attock',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 56.0,
    abutmentType: 'Reinforced concrete wall abutment',
    abutmentFoundation: 'Bored pile',
    pierType: 'Reinforced concrete twin-leaf pier',
    pierFoundation: 'Bored pile',
    bearings: 'Pot bearings, fixed at pier 4',
    railings: 'Reinforced concrete barrier with steel rail',
    movement: 'Modular expansion joint at both abutments',
    riverTraining: 'Stone pitching to both banks, guide bund on the right bank',
    runningSurface: 'Asphalt wearing course on waterproofing',
    services: 'Motorway communications duct inside the box',
    designedBy: null,
    constructedBy: null,
    aadt: 21300,
    aadtYear: 2025,
    detourKm: 17.9,
    detourMinutes: 34,
    lifeline: true,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-M1-113-A',
        date: '2026-05-07',
        type: 'principal',
        rating: 2,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-05-26T09:30:00Z',
        note:
          'Right bank guide bund has lost its toe over about 30 m and the bank is retreating ' +
          'towards abutment B. Modular joint at abutment A has two broken seals and is passing ' +
          'water onto the bearing shelf below.',
        elements: [
          { element: 'joints', rating: 3, extent: 2, urgency: 3, note: 'Abutment A modular joint leaking.' },
          { element: 'river_training', rating: 3, extent: 2, urgency: 3, note: 'Guide bund toe lost, right bank.' },
          { element: 'bearings', rating: 2, extent: 1, urgency: 2, note: 'Shelf wet below the leaking joint.' },
          { element: 'deck', rating: 1, extent: 1, urgency: 1 },
          { element: 'deck_surface', rating: 2, extent: 2, urgency: 1 },
          { element: 'pier', rating: 1, extent: 1, urgency: 1 },
          { element: 'abutment', rating: 2, extent: 1, urgency: 2 },
          { element: 'protection', rating: 2, extent: 2, urgency: 2 },
          { element: 'parapet', rating: 1, extent: 1, urgency: 0 },
          { element: 'approach', rating: 1, extent: 1, urgency: 1 },
        ],
      },
      {
        id: 'INSP-PK-M1-113-B',
        date: '2023-10-16',
        type: 'principal',
        rating: 1,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2023-11-06T10:40:00Z',
        note: 'Box interior walked end to end. Joints were tight then and the guide bund was intact.',
      },
    ],
  },

  /* ===== 21. Burhan Rail Overbridge — M-1 km 115. Over the Karachi–Peshawar main line. ===== */
  {
    id: 'PK-M1-0115',
    name: 'Burhan Rail Overbridge',
    route: 'M-1',
    roadName: 'Islamabad–Peshawar Motorway',
    km: 115.2,
    district: 'Attock',
    province: 'Punjab',
    river: null,
    type: 'Steel plate girder with composite deck slab',
    spans: 3,
    length: 84.0,
    deckWidth: 12.2,
    carriageway: 11.0,
    year: 2007,
    deckRL: 502.4,
    hfl: null,
    lat: 33.8213,
    lng: 72.6298,
    deckBearing: 158.0,
    inventoryCode: 'FWO-PNJ-ATK-021',
    roadLength: 155.0,
    divisionOffice: 'Maintenance Unit — Attock',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 32.0,
    abutmentType: 'Reinforced concrete wall abutment',
    abutmentFoundation: 'Spread footing',
    pierType: 'Reinforced concrete portal frame pier',
    pierFoundation: 'Spread footing',
    bearings: 'Steel rocker, fixed at pier 1',
    railings: 'Reinforced concrete barrier with steel rail and anti-throw screen',
    movement: 'Finger joint at both abutments',
    riverTraining: null,
    runningSurface: 'Asphalt over composite slab',
    services: 'Motorway communications duct in the verge',
    designedBy: null,
    constructedBy: null,
    aadt: 21900,
    aadtYear: 2025,
    /* A LONG DETOUR FOR A GRADE SEPARATION, because the only other way across
       the main line within reach is the level crossing at Hasan Abdal. That is
       exactly the distinction the consequence screen exists to make, and giving
       every flyover on the corridor a tidy 3 km would hide it. */
    detourKm: 14.2,
    detourMinutes: 31,
    lifeline: false,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-M1-115-A',
        date: '2026-01-30',
        type: 'principal',
        rating: 2,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-02-19T07:30:00Z',
        note:
          'Paint system broken down along the bottom flange of both outer girders over the track, ' +
          'with surface corrosion but no measurable section loss. The rocker at pier 1 is tilted ' +
          'past its marked range, and the anti-throw screen is torn at two panels.',
        elements: [
          { element: 'deck', rating: 2, extent: 3, urgency: 2, note: 'Coating failure, outer girders.' },
          { element: 'deck_surface', rating: 1, extent: 1, urgency: 1 },
          { element: 'bearings', rating: 3, extent: 1, urgency: 3, note: 'Pier 1 rocker off its range.' },
          { element: 'joints', rating: 2, extent: 2, urgency: 2 },
          { element: 'pier', rating: 1, extent: 1, urgency: 0 },
          { element: 'abutment', rating: 1, extent: 1, urgency: 0 },
          { element: 'parapet', rating: 2, extent: 2, urgency: 2, note: 'Anti-throw screen torn.' },
          { element: 'river_training', rating: 'N' },
          { element: 'protection', rating: 'N' },
          { element: 'approach', rating: 1, extent: 1, urgency: 1 },
        ],
      },
    ],
  },

  /* ===== 22. Brahma Bahtar Interchange Flyover — M-1 km 126. ===== */
  {
    id: 'PK-M1-0126',
    name: 'Brahma Bahtar Interchange Flyover',
    route: 'M-1',
    roadName: 'Islamabad–Peshawar Motorway',
    km: 126.4,
    district: 'Attock',
    province: 'Punjab',
    river: null,
    type: 'PSC I-girder flyover, simply supported',
    spans: 4,
    length: 112.0,
    deckWidth: 12.2,
    carriageway: 11.0,
    year: 2007,
    deckRL: 512.9,
    hfl: null,
    lat: 33.7431,
    lng: 72.7053,
    deckBearing: 145.0,
    inventoryCode: 'FWO-PNJ-ATK-022',
    roadLength: 155.0,
    divisionOffice: 'Maintenance Unit — Attock',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 28.0,
    abutmentType: 'Reinforced concrete wall abutment on reinforced earth',
    abutmentFoundation: 'Spread footing',
    pierType: 'Reinforced concrete single-column pier with hammerhead',
    pierFoundation: 'Spread footing',
    bearings: 'Elastomeric, laminated',
    railings: 'Reinforced concrete barrier with steel rail',
    movement: 'Strip seal joint at both abutments',
    riverTraining: null,
    runningSurface: 'Asphalt wearing course on waterproofing',
    services: 'Motorway communications duct in the verge',
    designedBy: null,
    constructedBy: null,
    aadt: 25100,
    aadtYear: 2025,
    detourKm: 4.7,
    detourMinutes: 13,
    lifeline: false,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-M1-126-A',
        date: '2026-04-25',
        type: 'routine',
        rating: 0,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-05-11T08:35:00Z',
        /* ONE STRUCTURE IN THE REGISTER WITH NOTHING WRONG WITH IT. A
           demonstration where every record has a finding teaches a reader that
           the application is a defect log, and hides what a clean sheet looks
           like — which is the outcome most inspections actually have. */
        note: 'Nothing to report. Drainage clear, joints tight, no cracking found.',
        elements: [
          { element: 'deck', rating: 0, extent: 0, urgency: 0 },
          { element: 'deck_surface', rating: 0, extent: 0, urgency: 0 },
          { element: 'bearings', rating: 0, extent: 0, urgency: 0 },
          { element: 'joints', rating: 0, extent: 0, urgency: 0 },
          { element: 'pier', rating: 0, extent: 0, urgency: 0 },
          { element: 'abutment', rating: 0, extent: 0, urgency: 0 },
          { element: 'parapet', rating: 0, extent: 0, urgency: 0 },
          { element: 'river_training', rating: 'N' },
          { element: 'protection', rating: 'N' },
          { element: 'approach', rating: 0, extent: 0, urgency: 0 },
        ],
      },
    ],
  },

  /* ===== 23. Fateh Jang Interchange Flyover — M-1 km 148. The last but one before the M-2. ===== */
  {
    id: 'PK-M1-0148',
    name: 'Fateh Jang Interchange Flyover',
    route: 'M-1',
    roadName: 'Islamabad–Peshawar Motorway',
    km: 148.3,
    district: 'Attock',
    province: 'Punjab',
    river: null,
    type: 'PSC I-girder flyover, simply supported',
    spans: 5,
    length: 154.0,
    deckWidth: 12.2,
    carriageway: 11.0,
    year: 2007,
    deckRL: 508.7,
    hfl: null,
    lat: 33.6097,
    lng: 72.8593,
    deckBearing: 150.0,
    inventoryCode: 'FWO-PNJ-ATK-023',
    roadLength: 155.0,
    divisionOffice: 'Maintenance Unit — Attock',
    loadRestriction: null,
    designLoad: 'AASHTO HS20-44',
    spanLength: 30.8,
    abutmentType: 'Reinforced concrete wall abutment on reinforced earth',
    abutmentFoundation: 'Bored pile',
    pierType: 'Reinforced concrete single-column pier with hammerhead',
    pierFoundation: 'Bored pile',
    bearings: 'Pot bearings, fixed at pier 2',
    railings: 'Reinforced concrete barrier with steel rail',
    movement: 'Modular expansion joint at both abutments',
    riverTraining: null,
    runningSurface: 'Asphalt wearing course on waterproofing',
    services: 'Motorway communications duct in the verge; water main on the southern parapet',
    designedBy: null,
    constructedBy: null,
    aadt: 26400,
    aadtYear: 2025,
    detourKm: 7.3,
    detourMinutes: 24,
    lifeline: false,
    ...BLANK,
    ...NO_EXTRAS,
    inspections: [
      {
        id: 'INSP-PK-M1-148-A',
        date: '2026-04-26',
        type: 'routine',
        rating: 2,
        scale: SCALE,
        state: 'accepted',
        reviewer: PEOPLE.engineer,
        reviewedAt: '2026-05-11T08:50:00Z',
        note:
          'Water main on the southern parapet has been leaking for some time: the parapet face ' +
          'below it is stained and the concrete is scaling over about 4 m². Reported to the ' +
          'utility in March and still running.',
        elements: [
          { element: 'parapet', rating: 3, extent: 2, urgency: 3, note: 'Scaling below the leaking main.' },
          { element: 'deck', rating: 1, extent: 1, urgency: 1 },
          { element: 'deck_surface', rating: 1, extent: 1, urgency: 1 },
          { element: 'bearings', rating: 1, extent: 1, urgency: 0 },
          { element: 'joints', rating: 2, extent: 1, urgency: 2 },
          { element: 'pier', rating: 1, extent: 1, urgency: 1 },
          { element: 'abutment', rating: 1, extent: 1, urgency: 0 },
          { element: 'river_training', rating: 'N' },
          { element: 'protection', rating: 'N' },
          { element: 'approach', rating: 1, extent: 1, urgency: 1 },
        ],
      },
    ],
  },
];

/* --------------------------- the defect log --------------------------- */

/**
 * Fabricated defects across the twenty-five structures.
 *
 * WHY THESE ARE IN THE BUNDLE AND NOT IN D1. Defects are normally register rows
 * fetched from `/api/defects`, and seeding them would mean writing invented
 * findings into the production database — see the header. So the store falls
 * back to this list for `pk-fwo` when the query returns nothing, which is also
 * the only way the demonstration works on the Vite window, where there is no
 * backend at all.
 *
 * `modelDefectId` is the handle the vendored modeller keys on. Every row needs
 * one and they have to be distinct per structure; these are simply numbered from
 * 1 within each bridge, which is what the modeller's own log would have done.
 *
 * `pin` and `normal` are null on every row: none of these was placed by picking
 * a point on a 3D model, and a fabricated world coordinate would make the
 * modeller draw a decal somewhere nobody chose.
 */
export const FWO_DEMO_DEFECTS: RegisterDefect[] = [
  /* --- Lansdowne Bridge: the oldest structure and the worst standing --- */
  {
    id: 'dfk0001',
    clientId: 'demo-pk-0001',
    bridgeId: 'PK-N5-0621',
    element: 'deck',
    component: 'Span-1 Deck Trough',
    modelDefectId: 1,
    code: 'REE',
    severity: 'Critical',
    status: 'Open',
    note: 'Deck trough perforated over the Rohri tower. Main reinforcement of the infill slab exposed and rusting over roughly 2 m².',
    loggedAt: '2026-04-12',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: {
      method: 'measured',
      instrument: 'Tape and scale rule',
      areaM2: 2.1,
      count: 14,
      sectionLossPct: 25,
    },
  },
  {
    id: 'dfk0002',
    clientId: 'demo-pk-0002',
    bridgeId: 'PK-N5-0621',
    element: 'bearings',
    component: 'Bent-1 Bearing Nest',
    modelDefectId: 2,
    code: 'SET',
    severity: 'Severe',
    status: 'In Review',
    note: 'Roller nest on the Sukkur side seized. Deck no longer travelling; 9 mm differential measured against the 2023 datum mark.',
    loggedAt: '2026-04-12',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Level and staff', depthMm: 9 },
  },
  {
    id: 'dfk0003',
    clientId: 'demo-pk-0003',
    bridgeId: 'PK-N5-0621',
    element: 'parapet',
    component: 'Span-1 Parapet',
    modelDefectId: 3,
    code: 'CRK',
    severity: 'Minor',
    status: 'Open',
    note: 'Fatigue cracking at the lattice parapet connection, four panels on the upstream face.',
    loggedAt: '2025-03-30',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Crack width gauge', lengthM: 0.4, widthMm: 0.6 },
  },

  /* --- Kotri Bridge: deck slab and one scour hole --- */
  {
    id: 'dfk0004',
    clientId: 'demo-pk-0004',
    bridgeId: 'PK-N5-0154',
    element: 'deck_surface',
    component: 'Span-5 Deck Slab',
    modelDefectId: 1,
    code: 'SPA',
    severity: 'Severe',
    status: 'Open',
    note: 'Delamination and spalling in the right-hand wheel track between piers 4 and 7. Sounding gives about 18 m² hollow.',
    loggedAt: '2026-08-02',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Chain drag and tape', areaM2: 18.4, depthMm: 45 },
  },
  {
    id: 'dfk0005',
    clientId: 'demo-pk-0005',
    bridgeId: 'PK-N5-0154',
    element: 'pier',
    component: 'Bent-5 Well Curb',
    modelDefectId: 2,
    code: 'SCO',
    severity: 'Severe',
    status: 'In Review',
    note: 'Local scour at pier 5, measured by rod from the dive boat at low flow. Within the well curb, no undermining of the steining.',
    loggedAt: '2026-02-19',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Sounding rod', depthM: 2.4, lengthM: 11.0, volumeM3: 130 },
  },
  {
    id: 'dfk0006',
    clientId: 'demo-pk-0006',
    bridgeId: 'PK-N5-0154',
    element: 'joints',
    component: 'Bent-6 Deck Joint',
    modelDefectId: 3,
    code: 'SEP',
    severity: 'Moderate',
    status: 'Open',
    note: 'Sliding plate joint at pier 6 packed with silt and no longer moving. Gap read at 4 mm against a nominal 40 mm.',
    loggedAt: '2026-08-02',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Feeler gauge', widthMm: 4, lengthM: 7.6 },
  },
  {
    id: 'dfk0007',
    clientId: 'demo-pk-0007',
    bridgeId: 'PK-N5-0154',
    element: 'protection',
    component: 'Left Bank Apron',
    modelDefectId: 4,
    code: 'ERO',
    severity: 'Moderate',
    status: 'Resolved',
    note: 'Bank erosion upstream of the left abutment. Re-laid with gabion in the 2025-26 maintenance year and closed out.',
    loggedAt: '2025-07-14',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'estimated', areaM2: 220, volumeM3: 340 },
  },

  /* --- Jinnah Bridge: marine exposure, nothing structural --- */
  {
    id: 'dfk0008',
    clientId: 'demo-pk-0008',
    bridgeId: 'PK-KHI-0007',
    element: 'deck',
    component: 'Span-2 Deck Soffit',
    modelDefectId: 1,
    code: 'SCA',
    severity: 'Minor',
    status: 'Open',
    note: 'Surface scaling and chloride staining below the deck drainage outlets, both sides of the pylon.',
    loggedAt: '2026-06-24',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'photo-derived', areaM2: 6.2, depthMm: 5 },
  },
  {
    id: 'dfk0009',
    clientId: 'demo-pk-0009',
    bridgeId: 'PK-KHI-0007',
    element: 'approach',
    component: 'North Approach Slab',
    modelDefectId: 2,
    code: 'POH',
    severity: 'Minor',
    status: 'Resolved',
    note: 'Three potholes in the northern approach, patched the same week.',
    loggedAt: '2026-01-22',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Tape', count: 3, areaM2: 1.4, depthMm: 60 },
  },

  /* --- Attock Bridge: the one that drove a load restriction down --- */
  {
    id: 'dfk0010',
    clientId: 'demo-pk-0010',
    bridgeId: 'PK-N5-1602',
    element: 'deck',
    component: 'Span-2 Girder G1 Soffit',
    modelDefectId: 1,
    code: 'REE',
    severity: 'Critical',
    status: 'Open',
    note: 'Longitudinal cracking and spalling along the bottom flange of exterior girder G1 at midspan of span 2. Nine of the eighteen strands exposed and corroded through — 50.0% of the tendon section.',
    loggedAt: '2024-10-15',
    author: PEOPLE.inspector,
    /* On the soffit of G1 at midspan of span 2, in the model's own frame
       (metres, Y up, along x): where the rating applies the loss. No
       photograph — none was taken of a defect that was never seen. */
    pin: { x: 37.5, y: -2.054, z: 5.0 },
    normal: { x: 0, y: -1, z: 0 },
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Tape and strand count', areaM2: 4.6, sectionLossPct: 50, count: 9 },
  },
  {
    id: 'dfk0011',
    clientId: 'demo-pk-0011',
    bridgeId: 'PK-N5-1602',
    element: 'deck_surface',
    component: 'Span-3 Deck Surface',
    modelDefectId: 2,
    code: 'POH',
    severity: 'Severe',
    status: 'Open',
    note: 'Wearing course broken up over the deck slab for most of span 3. Eleven potholes through to the concrete.',
    loggedAt: '2024-10-15',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Tape', count: 11, areaM2: 9.8, depthMm: 80 },
  },
  {
    id: 'dfk0012',
    clientId: 'demo-pk-0012',
    bridgeId: 'PK-N5-1602',
    element: 'parapet',
    component: 'Span-3 Barrier',
    modelDefectId: 3,
    code: 'CRK',
    severity: 'Moderate',
    status: 'In Review',
    note: 'Cracked and spalled concrete barrier after a vehicle strike at the north end of span 3.',
    loggedAt: '2025-08-03',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Crack width gauge', lengthM: 1.2, widthMm: 2.5 },
  },

  /* --- Indus River Bridge, M-1: a modern structure in good order --- */
  {
    id: 'dfk0013',
    clientId: 'demo-pk-0013',
    bridgeId: 'PK-M1-0086',
    element: 'deck',
    component: 'Bent-4 Diaphragm',
    modelDefectId: 1,
    code: 'CRK',
    severity: 'Minor',
    status: 'Open',
    note: 'Transverse cracking in the deck soffit either side of the pier 4 diaphragm. Below the 0.3 mm threshold at every point read but one.',
    loggedAt: '2026-05-21',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Crack width gauge', lengthM: 8.4, widthMm: 0.4 },
  },
  {
    id: 'dfk0014',
    clientId: 'demo-pk-0014',
    bridgeId: 'PK-M1-0086',
    element: 'joints',
    component: 'Abutment-1 Expansion Joint',
    modelDefectId: 2,
    code: 'SEP',
    severity: 'Moderate',
    status: 'Open',
    note: 'Modular joint seal torn over about a third of its length at the Islamabad abutment. Water tracking onto the bearing shelf.',
    loggedAt: '2026-05-21',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Tape', widthMm: 85, lengthM: 4.1 },
  },
  {
    id: 'dfk0015',
    clientId: 'demo-pk-0015',
    bridgeId: 'PK-M1-0086',
    element: 'protection',
    component: 'Abutment-2 Apron',
    modelDefectId: 3,
    code: 'ERO',
    severity: 'Moderate',
    status: 'Open',
    note: 'Gabion apron at the Peshawar abutment settled and partly displaced. Wire baskets sound, fill migrating.',
    loggedAt: '2026-05-21',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'estimated', areaM2: 95, volumeM3: 70 },
  },

  /* --- Kohala Bridge: a masonry arch losing its mortar --- */
  {
    id: 'dfk0016',
    clientId: 'demo-pk-0016',
    bridgeId: 'PK-N75-0088',
    element: 'deck',
    component: 'Span-1 Arch Barrel',
    modelDefectId: 1,
    code: 'ERO',
    severity: 'Critical',
    status: 'Open',
    note: 'Mortar loss through the arch barrel over roughly a third of the intrados, and two voussoirs dropped at the left-bank springing.',
    loggedAt: '2025-06-11',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'estimated', areaM2: 42, volumeM3: 6.5 },
  },
  {
    id: 'dfk0017',
    clientId: 'demo-pk-0017',
    bridgeId: 'PK-N75-0088',
    element: 'parapet',
    component: 'Span-1 Parapet',
    modelDefectId: 2,
    code: 'SET',
    severity: 'Severe',
    status: 'Open',
    note: 'Masonry parapet displaced outward at mid-span, measured against a string line off both springings.',
    loggedAt: '2025-06-11',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'String line and rule', depthMm: 60 },
  },
  {
    id: 'dfk0018',
    clientId: 'demo-pk-0018',
    bridgeId: 'PK-N75-0088',
    element: 'abutment',
    component: 'Abutment-1 Springing',
    modelDefectId: 3,
    code: 'SPA',
    severity: 'Moderate',
    status: 'Open',
    note: 'Spalled masonry at the left-bank springing where the two voussoirs were lost.',
    loggedAt: '2025-06-11',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Tape', areaM2: 1.8, depthMm: 220 },
  },

  /* --- Karot Bridge: one shrinkage crack on a bridge six years old --- */
  {
    id: 'dfk0019',
    clientId: 'demo-pk-0019',
    bridgeId: 'PK-AJK-0219',
    element: 'abutment',
    component: 'Abutment-2 Wing Wall',
    modelDefectId: 1,
    code: 'CRK',
    severity: 'Minor',
    status: 'Open',
    note: 'Vertical shrinkage cracking in the right-bank wing wall. No differential movement across the crack at either telltale.',
    loggedAt: '2026-09-04',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Crack width gauge', lengthM: 2.6, widthMm: 0.2 },
  },

  /* --- Kund Bridge: what the flood took out --- */
  {
    id: 'dfk0020',
    clientId: 'demo-pk-0020',
    bridgeId: 'PK-N35-0182',
    element: 'protection',
    component: 'Abutment-1 Apron',
    modelDefectId: 1,
    code: 'SCO',
    severity: 'Critical',
    status: 'Open',
    note: 'Left-bank gabion apron stripped over about 35 m by the August flood and the abutment toe exposed. Rod soundings give 3.1 m below the original bed.',
    loggedAt: '2025-09-27',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Sounding rod', depthM: 3.1, lengthM: 35.0, volumeM3: 890 },
  },
  {
    id: 'dfk0021',
    clientId: 'demo-pk-0021',
    bridgeId: 'PK-N35-0182',
    element: 'river_training',
    component: 'Left Bank Training Work',
    modelDefectId: 2,
    code: 'ERO',
    severity: 'Severe',
    status: 'In Review',
    note: 'Training bund behind the apron cut back by about 4 m. Replacement gabion volume estimated from the survey section, not measured on the ground.',
    loggedAt: '2025-09-27',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'estimated', areaM2: 410, volumeM3: 1240 },
  },
  {
    id: 'dfk0022',
    clientId: 'demo-pk-0022',
    bridgeId: 'PK-N35-0182',
    element: 'parapet',
    component: 'North Approach Parapet',
    modelDefectId: 3,
    code: 'SPA',
    severity: 'Minor',
    status: 'Resolved',
    note: 'Rockfall damage to the approach parapet. Made good in the 2024-25 maintenance year.',
    loggedAt: '2024-04-18',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Tape', areaM2: 0.9, depthMm: 90 },
  },

  /* --- Earthquake Memorial Bridge: one joint, one approach slab --- */
  {
    id: 'dfk0023',
    clientId: 'demo-pk-0023',
    bridgeId: 'PK-AJK-0044',
    element: 'approach',
    component: 'North Approach Slab',
    modelDefectId: 1,
    code: 'SPA',
    severity: 'Moderate',
    status: 'Open',
    note: 'Spalling along the northern approach slab joint, both wheel tracks. Reinforcement not yet exposed.',
    loggedAt: '2026-07-30',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Tape', areaM2: 3.4, depthMm: 35 },
  },
  {
    id: 'dfk0024',
    clientId: 'demo-pk-0024',
    bridgeId: 'PK-AJK-0044',
    element: 'joints',
    component: 'Abutment-1 Expansion Joint',
    modelDefectId: 2,
    code: 'SEP',
    severity: 'Minor',
    status: 'Open',
    note: 'Modular joint running 12 mm wider than the installation record at 31 °C. Watching rather than acting.',
    loggedAt: '2026-07-30',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Tape', widthMm: 12, lengthM: 13.0 },
  },

  /* ======================= the M-1 corridor's defects =======================

     Nineteen more, against the thirteen structures added above. They are the
     findings the inspections there describe, written out as register rows so
     the works list, the severity rollup and the corridor's own condition
     profile have something on one road to work with.

     TWO STRUCTURES CARRY NONE, ON PURPOSE. The Charsadda flyover has never been
     inspected, so it cannot have a finding; Brahma Bahtar was inspected and
     nothing was wrong with it. A corridor where every structure has a defect is
     a corridor nobody would believe.
     ======================================================================== */

  /* --- Peshawar Ring Road Flyover: one settled approach slab --- */
  {
    id: 'dfk0025',
    clientId: 'demo-pk-0025',
    bridgeId: 'PK-M1-0001',
    element: 'approach',
    component: 'Peshawar Approach Slab',
    modelDefectId: 1,
    code: 'SET',
    severity: 'Minor',
    status: 'Open',
    note: 'Approach slab settled against the abutment. 15 mm step, felt as a bump at motorway speed.',
    loggedAt: '2026-03-18',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Straightedge and wedge gauge', depthMm: 15 },
  },

  /* --- Kabul River Bridge: drainage into the soffit, and a spur dyke losing pitching --- */
  {
    id: 'dfk0026',
    clientId: 'demo-pk-0026',
    bridgeId: 'PK-M1-0014',
    element: 'deck',
    component: 'Pier-3 Diaphragm Soffit',
    modelDefectId: 1,
    code: 'CRK',
    severity: 'Moderate',
    status: 'Open',
    note: 'Cracking with efflorescence and rust staining on the box soffit below the pier 3 diaphragm, tracked to a blocked deck outlet.',
    loggedAt: '2026-02-24',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Crack width gauge', lengthM: 3.2, widthMm: 0.4 },
  },
  {
    id: 'dfk0027',
    clientId: 'demo-pk-0027',
    bridgeId: 'PK-M1-0014',
    element: 'river_training',
    component: 'Left Bank Spur Dyke',
    modelDefectId: 2,
    code: 'ERO',
    severity: 'Severe',
    status: 'In Review',
    note: 'Stone pitching stripped from the upstream face of the left bank spur dyke over about 18 m of its length after the August flows.',
    loggedAt: '2026-02-24',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'estimated', instrument: 'Pacing and tape', areaM2: 54.0, volumeM3: 120.0 },
  },
  {
    id: 'dfk0028',
    clientId: 'demo-pk-0028',
    bridgeId: 'PK-M1-0014',
    element: 'joints',
    component: 'Abutment-A Expansion Joint',
    modelDefectId: 3,
    code: 'SEP',
    severity: 'Minor',
    status: 'Open',
    note: 'Modular joint at abutment A opened wider than its marked winter range; seal intact but the gap is passing grit.',
    loggedAt: '2026-02-24',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Feeler and rule', widthMm: 34, lengthM: 11.6 },
  },

  /* --- Kalpani Bridge: low cover on the span 4 beams, and a pitted approach --- */
  {
    id: 'dfk0029',
    clientId: 'demo-pk-0029',
    bridgeId: 'PK-M1-0043',
    element: 'deck',
    component: 'Span-4 Beam 2 Soffit',
    modelDefectId: 1,
    code: 'SPA',
    severity: 'Severe',
    status: 'Open',
    note: 'Soffit concrete spalled off beam 2 in span 4. Cover measured at 22 mm against 40 mm specified; carbonation suspected.',
    loggedAt: '2026-06-09',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Tape and depth gauge', areaM2: 1.8, depthMm: 45 },
  },
  {
    id: 'dfk0030',
    clientId: 'demo-pk-0030',
    bridgeId: 'PK-M1-0043',
    element: 'deck',
    component: 'Span-4 Beam 3 Soffit',
    modelDefectId: 2,
    code: 'REE',
    severity: 'Severe',
    status: 'Open',
    note: 'Six main bars exposed and rusting on the beam 3 soffit where the cover has come away. No loss of bar diameter measurable yet.',
    loggedAt: '2026-06-09',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: {
      method: 'measured',
      instrument: 'Tape and cover meter',
      areaM2: 0.9,
      count: 6,
      sectionLossPct: 10,
    },
  },
  {
    id: 'dfk0031',
    clientId: 'demo-pk-0031',
    bridgeId: 'PK-M1-0043',
    element: 'approach',
    component: 'Islamabad Approach',
    modelDefectId: 3,
    code: 'POH',
    severity: 'Minor',
    /* THE ONE CLOSED ROW ON THIS CORRIDOR. A defect log where nothing is ever
       resolved makes the status filter and the works list look ornamental. */
    status: 'Resolved',
    note: 'Potholing in the nearside wheel track on the Islamabad approach. Patched in the 2025-26 routine allocation.',
    loggedAt: '2025-05-19',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: {
      method: 'measured',
      instrument: 'Tape and depth gauge',
      count: 5,
      areaM2: 2.4,
      depthMm: 60,
    },
  },

  /* --- Budhni Nala Bridge: the corridor's one critical finding --- */
  {
    id: 'dfk0034',
    clientId: 'demo-pk-0034',
    bridgeId: 'PK-M1-0074',
    element: 'pier',
    component: 'Pier-2 Footing',
    modelDefectId: 1,
    code: 'SCO',
    severity: 'Critical',
    status: 'Open',
    note: 'Scour hole 2.6 m below bed level at the pier 2 upstream nose, undercutting the footing by about 0.9 m. Traffic restricted to 60 km/h over the structure.',
    loggedAt: '2026-08-14',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: {
      method: 'measured',
      instrument: 'Sounding rod, dived',
      depthM: 2.6,
      lengthM: 7.4,
      volumeM3: 340.0,
    },
  },
  {
    id: 'dfk0035',
    clientId: 'demo-pk-0035',
    bridgeId: 'PK-M1-0074',
    element: 'protection',
    component: 'Pier-2 Launching Apron',
    modelDefectId: 2,
    code: 'ERO',
    severity: 'Severe',
    status: 'Open',
    note: 'Launching apron at pier 2 has gone entirely. Nothing left to launch into the scour hole beside it.',
    loggedAt: '2026-07-02',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'estimated', instrument: 'Pacing and rod', areaM2: 96.0, volumeM3: 210.0 },
  },
  {
    id: 'dfk0036',
    clientId: 'demo-pk-0036',
    bridgeId: 'PK-M1-0074',
    element: 'river_training',
    component: 'Right Bank Gabion Apron',
    modelDefectId: 3,
    code: 'ERO',
    severity: 'Moderate',
    status: 'Open',
    note: 'Right bank gabions torn open over two baskets and the fill washed out. The bank behind them is starting to cut back.',
    loggedAt: '2026-07-02',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'estimated', instrument: 'Pacing and tape', areaM2: 140.0, volumeM3: 180.0 },
  },
  {
    id: 'dfk0037',
    clientId: 'demo-pk-0037',
    bridgeId: 'PK-M1-0074',
    element: 'deck_surface',
    component: 'Span-2 Wearing Course',
    modelDefectId: 4,
    code: 'CRK',
    severity: 'Minor',
    status: 'Open',
    note: 'Reflective cracking over the span 2 and 3 beam ends, following the beam lines.',
    loggedAt: '2026-07-02',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Crack width gauge', lengthM: 6.5, widthMm: 1.2 },
  },

  /* --- Ghazi Barotha Canal Bridge: one shim, dealt with --- */
  {
    id: 'dfk0038',
    clientId: 'demo-pk-0038',
    bridgeId: 'PK-M1-0103',
    element: 'bearings',
    component: 'Pier-3 Bearing Shim',
    modelDefectId: 1,
    code: 'SET',
    severity: 'Minor',
    status: 'Resolved',
    note: 'Light corrosion to the pier 3 bearing shims with 4 mm of differential against the datum. Cleaned and repainted in the 2025-26 routine allocation.',
    loggedAt: '2026-04-23',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Level and staff', depthMm: 4 },
  },

  /* --- Hazara Interchange Flyover: the busiest deck on the corridor, wearing out --- */
  {
    id: 'dfk0042',
    clientId: 'demo-pk-0042',
    bridgeId: 'PK-M1-0112',
    element: 'deck_surface',
    component: 'Span-2 Nearside Wheel Track',
    modelDefectId: 1,
    code: 'POH',
    severity: 'Minor',
    status: 'Open',
    note: 'Three potholes opening in the rutted nearside wheel track over spans 2 and 3. Rut depth 14 mm under a 2 m straightedge.',
    loggedAt: '2026-04-24',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: {
      method: 'measured',
      instrument: 'Straightedge and depth gauge',
      count: 3,
      areaM2: 0.6,
      depthMm: 55,
    },
  },

  /* --- Haro River Bridge: a leaking joint over a bearing shelf, and a retreating bank --- */
  {
    id: 'dfk0039',
    clientId: 'demo-pk-0039',
    bridgeId: 'PK-M1-0113',
    element: 'joints',
    component: 'Abutment-A Modular Joint',
    modelDefectId: 1,
    code: 'SEP',
    severity: 'Severe',
    status: 'Open',
    note: 'Two seals broken in the abutment A modular joint. The joint is passing water straight onto the bearing shelf below it.',
    loggedAt: '2026-05-07',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Feeler and rule', widthMm: 62, lengthM: 12.2 },
  },
  {
    id: 'dfk0040',
    clientId: 'demo-pk-0040',
    bridgeId: 'PK-M1-0113',
    element: 'river_training',
    component: 'Right Bank Guide Bund',
    modelDefectId: 2,
    code: 'ERO',
    severity: 'Severe',
    status: 'In Review',
    note: 'Right bank guide bund has lost its toe over about 30 m and the bank is retreating towards abutment B.',
    loggedAt: '2026-05-07',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'estimated', instrument: 'Pacing and tape', areaM2: 210.0, volumeM3: 480.0 },
  },
  {
    id: 'dfk0041',
    clientId: 'demo-pk-0041',
    bridgeId: 'PK-M1-0113',
    element: 'abutment',
    component: 'Abutment-A Bearing Shelf',
    modelDefectId: 3,
    code: 'CRK',
    severity: 'Minor',
    status: 'Open',
    note: 'Vertical crack in the abutment A bearing shelf below the leaking joint, with leaching down the face.',
    loggedAt: '2026-05-07',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Crack width gauge', lengthM: 1.8, widthMm: 0.5 },
  },

  /* --- Burhan Rail Overbridge: a rocker off its range over a live track --- */
  {
    id: 'dfk0032',
    clientId: 'demo-pk-0032',
    bridgeId: 'PK-M1-0115',
    element: 'bearings',
    component: 'Pier-1 Rocker Bearing',
    modelDefectId: 1,
    code: 'SET',
    severity: 'Severe',
    status: 'In Review',
    note: 'Rocker at pier 1 tilted 12 mm past the range marked on the bearing at commissioning. Deck movement is being taken somewhere else.',
    loggedAt: '2026-01-30',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Rule against the datum mark', depthMm: 12 },
  },
  {
    id: 'dfk0033',
    clientId: 'demo-pk-0033',
    bridgeId: 'PK-M1-0115',
    element: 'joints',
    component: 'Abutment-B Finger Joint',
    modelDefectId: 2,
    code: 'SEP',
    severity: 'Moderate',
    status: 'Open',
    note: 'Finger joint at abutment B opened to 48 mm with the fingers no longer overlapping at the kerb end.',
    loggedAt: '2026-01-30',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Feeler and rule', widthMm: 48, lengthM: 12.2 },
  },

  /* --- Fateh Jang Interchange Flyover: somebody else's water main --- */
  {
    id: 'dfk0043',
    clientId: 'demo-pk-0043',
    bridgeId: 'PK-M1-0148',
    element: 'parapet',
    component: 'Southern Parapet, Spans 3-4',
    modelDefectId: 1,
    code: 'SCA',
    severity: 'Moderate',
    status: 'Open',
    note: 'Concrete scaling to the parapet face below the water main, which has been leaking since at least March. Reported to the utility; still running at the time of inspection.',
    loggedAt: '2026-04-26',
    author: PEOPLE.inspector,
    pin: null,
    normal: null,
    hasPhoto: false,
    quantity: { method: 'measured', instrument: 'Tape and depth gauge', areaM2: 4.2, depthMm: 12 },
  },
];

/**
 * The demonstration structures for one department, or none.
 *
 * WHY THIS EXISTS SEPARATELY FROM `seedFor`. The seed is a FALLBACK — D1 wins
 * over it the moment the backend answers, which is the register's oldest and
 * most important rule and is not being touched. But this deployment's D1 already
 * holds two real `pk-fwo` records (the Karachi flyovers, entered 2026-09-17), so
 * on sign-in the fallback was discarded and the demonstration vanished: ten
 * structures before authentication, two after it.
 *
 * So `hydrate` ADDS these to whatever D1 returned rather than choosing between
 * them. A real row always wins on id, the two Karachi records stay exactly as
 * they are, and the ten fabricated ones sit beside them carrying the `D` mark
 * that says which is which. Nothing is written to the database.
 */
export function demoBridgesFor(tenant: string): Bridge[] {
  if (!demoEnabled() || tenant !== 'pk-fwo') return [];
  return FWO_DEMO_BRIDGES.map((b) => {
    /* The construction bridge's latest phase row is dated with its slid baseline. */
    const latest = b.id === reliefId() ? demoPhaseFor(tenant, b.id).at(-1) : undefined;
    return latest ? { ...b, phaseAt: latest.at, phaseBy: latest.by } : { ...b };
  });
}

/**
 * The register as D1 (or the seed) gave it, with the demonstration added: the
 * FWO structures appended where the register lacks them, and the two DPWH
 * records filled in where the listing leaves them empty. A real row wins.
 */
export function withDemoBridges(tenant: string, bridges: readonly Bridge[]): Bridge[] {
  if (tenant === 'ph-dpwh') return withDpwhDemo(tenant, bridges);
  const known = new Set(bridges.map((b) => b.id));
  return [...bridges, ...demoBridgesFor(tenant).filter((b) => !known.has(b.id))];
}

/** How many of the register's own records the demonstration filled in (DPWH only; FWO's are appended). */
export function demoFilledCount(tenant: string, bridges: readonly Bridge[]): number {
  return bridges.filter((b) => dpwhOwnsPhase(tenant, b.id)).length;
}

/** Whether a register field's value is the demonstration's (the `D` mark). */
export function isDemoField(bridgeId: string, field: string): boolean {
  return isDpwhDemoField(bridgeId, field);
}

/** Whether a structure's phase history is the demonstration's even where the register holds one. */
export function demoOwnsPhase(tenant: string, bridgeId: string): boolean {
  return dpwhOwnsPhase(tenant, bridgeId);
}

/**
 * The bundled defect log for one department, or an empty one.
 *
 * Copied on the way out, the same way `seedFor` copies the register and for the
 * same reason: this is a module constant, and a list handed out by reference is
 * one that an in-session edit could alter for every later read.
 *
 * Returns nothing at all unless the demonstration build flag is set, so the
 * default build has no fabricated defects to hand anybody.
 */
export function demoDefectsFor(tenant: string): RegisterDefect[] {
  if (tenant === 'ph-dpwh') return dpwhDefectsFor(tenant);
  if (!demoEnabled() || tenant !== 'pk-fwo') return [];
  return FWO_DEMO_DEFECTS.map((d) => ({ ...d }));
}

/* --------------------------- the construction record --------------------------- */

/** The prefix every fabricated entry's id carries — see `isDemoProgress`. */
const DEMO_PROGRESS_PREFIX = 'demo-works-';

/**
 * Whether one construction entry came out of this file rather than the register.
 *
 * Read off the id rather than off a field of its own, so `ProgressEntry` gains
 * nothing permanent for something temporary: a real id is 32 hex characters minted
 * by `insertProgress`, so this prefix cannot collide with one. The Works card uses
 * it to say how many of the rows below it are invented — the same promise the `D`
 * mark keeps on the inventory sheet, kept in the one other place this file's data
 * is now drawn.
 */
export function isDemoProgress(e: ProgressEntry): boolean {
  return e.id.startsWith(DEMO_PROGRESS_PREFIX);
}

/**
 * The two invented structures the construction record is written for (LC-70).
 * Functions rather than constants for the reason `reliefDay` is one: the works
 * below are built while FWO_DEMO_PROGRESS is being initialised.
 */
function reliefId(): string {
  return 'PK-N5-1603';
}
function overpassId(): string {
  return 'PK-N5-1588';
}

/**
 * The Indus Relief Bridge entries that carry a photograph, as `day:activity` —
 * unique in the table below, so each names one row.
 *
 * REAL PHOTOGRAPHS OF OTHER BRIDGES. The structure is wholly this file's own,
 * and the demonstration of Monitoring's progress photographs needs pictures that
 * look like a site. They are bridge-construction photographs from Wikimedia
 * Commons, each chosen to match its entry's stage of work, and NONE OF THEM IS
 * OF EITHER BRIDGE — public/demo/progress/SOURCES.md names every one, with its
 * author and licence. The files keep the `atk-` prefix they were named under
 * when this record sat on Attock. Delete that folder with this file.
 *
 * A function because `reliefWorks` reads it while FWO_DEMO_PROGRESS below is
 * being initialised — see `reliefDay`.
 */
function reliefPhotos(): ReadonlySet<string> {
  return new Set([
    '2:survey-check', '12:setting-out', '14:setting-out', '17:excavation', '20:hold-point',
    '21:excavation', '23:rebar-fixed', '24:dewatering', '26:excavation', '27:footing-poured',
    '28:pile-concreted', '29:pile-concreted', '30:pile-concreted', '33:pile-concreted', '36:pile-concreted',
    '38:excavation', '42:hold-point', '43:pile-concreted', '49:stoppage', '50:pilecap-poured',
    '50:stoppage', '52:girder-cast', '55:dewatering', '56:excavation', '57:formwork-erected',
    '59:concrete-poured', '66:footing-poured', '70:formwork-erected', '73:pilecap-poured', '80:concrete-poured',
    '82:concrete-poured', '101:non-conformance', '99:hold-point', '108:bearing-set', '124:bearing-set',
    '126:hold-point', '127:girder-erected', '128:girder-erected', '131:rebar-fixed', '134:bearing-set',
    '137:diaphragm-cast', '138:concrete-poured', '140:hold-point',
    '20:excavation', '106:survey-check', '126:formwork-struck', '134:formwork-erected',
  ]);
}

/**
 * Every fabricated work entry: the Indus Relief Bridge's first 140 days, slid
 * onto today, and the overpass's completed record, which is not.
 *
 * No work is recorded against BR-KHI-001 any longer (LC-70). It is a structure
 * FWO's own database holds, and six invented entries on a real row were the one
 * place this file wrote fiction onto a record that is not its own.
 */
const FWO_DEMO_PROGRESS: ProgressEntry[] = [...reliefWorks(), ...overpassWorks()];

/**
 * The first 140 days of a 245-day contract building the Indus Relief Bridge,
 * PK-N5-1603.
 *
 * EVERY FIELD IS INVENTED, on a structure that is itself this file's own, so
 * there is no real row under it. It is written to the model fixture — three
 * 25 m PSC girder spans on two piers, each pier two columns on two four-pile
 * caps, the abutments on spread footings (scripts/fixtures/multispan-overpass
 * .cloudmodel.json) — so that the construction playback
 * (src/data/buildSequence.ts) and the Scheduler's variance (RELIEF_PROGRAMME
 * below) both have something to read. Each entry's component is the name the
 * programme's match uses, and names the place the playback reads ("Pier P1 …",
 * "Abutment A2 …", "Span 1 …"); where it is one part of the model it carries
 * that part's id as well (LC-14), so "pile F1-3 at P2" lights one pile.
 *
 * PACED BY WHAT CONCRETE AND STEEL ALLOW, not by what makes a lively playback:
 *
 *   · a bored pile is bored, caged and concreted in one cycle — one a day on the
 *     one rig — and integrity-tested no sooner than seven days after the last;
 *   · a column is not started until its pile cap is three days old, and a bent
 *     cap's falsework not until its columns are seven;
 *   · bent-cap falsework is struck at fourteen days, bearings are set at three
 *     weeks, and no girder is landed on a cap younger than twenty-eight days;
 *   · the girders are PRETENSIONED, as the model says: the strands are stressed
 *     on the bed before the pour, released into the girder at five days once the
 *     cubes show the transfer strength (80% of f′c, the model's figure), and the
 *     girder is lifted no sooner than a week after that. There are no ducts;
 *   · every structural pour is wet-cured for seven days, and it says so.
 *
 * AND IT RUNS LATE, as contracts do. The Indus rising on snowmelt overtopped
 * the P2 cofferdam in the middle of its piling; prestressing strand arrived a
 * week late; the P2 bent cap failed its hold point on cover and was reworked.
 * So on the last day recorded the substructure is complete, span 1's girders
 * are up, and the span 2 lift has just been released on the P2 bent cap's
 * 28-day cubes — about half the programme, and behind it.
 *
 * Dated as day numbers from `reliefDay(0)` and ending on DEMO_ANCHOR, so the run
 * slides and its last day is always today. The site agent records every entry
 * and the resident engineer reviews it (LC-72).
 */
function reliefWorks(): ProgressEntry[] {
  type Row = [
    day: number,
    activity: string,
    element: string | null,
    component: string | null,
    quantity: number | null,
    unit: string | null,
    note: string,
    componentId?: string,
  ];
  const pile = (pier: number, cap: number, n: number) => `Bent-${pier}/Foundation-${cap}/Pile-${n}`;
  const rows: Row[] = [
    /* --- mobilisation --- */
    [2, 'survey-check', null, 'Site establishment', null, null, 'Joint survey of the site, the access road and the yard area with the Engineer.'],
    [12, 'setting-out', null, 'Casting yard', null, null, 'Casting yard beds and the pretensioning line set out on the A1 bank.'],
    [14, 'setting-out', null, 'All supports', null, null, 'Centre lines of both abutments and both piers set out from the control stations.'],
    [16, 'survey-check', null, 'All supports', null, null, 'Setting out checked by the Resident Engineer; worst closure 6 mm.'],
    [20, 'hold-point', null, 'Site establishment', null, null, 'Site offices, stores, batching plant and the access road to the P1 bank inspected and accepted.'],

    /* --- abutment A1, on rock on the near bank --- */
    [17, 'excavation', 'abutment', 'Abutment A1 excavation', 240, 'm³', 'Excavation for the A1 footing begun in weathered rock.'],
    [20, 'excavation', 'abutment', 'Abutment A1 excavation', 140, 'm³', 'A1 excavation taken down to formation; foundation approved by the Engineer.'],
    [23, 'rebar-fixed', 'abutment', 'Abutment A1 footing', 11.5, 't', 'A1 footing reinforcement fixed on blinding.', 'Abutment-1/Foundation'],
    [25, 'formwork-erected', 'abutment', 'Abutment A1 footing', 64, 'm²', 'A1 footing edge shutters erected.', 'Abutment-1/Foundation'],
    [27, 'footing-poured', 'abutment', 'Abutment A1 footing', 145, 'm³', 'A1 footing poured, C30, 06:00 to 11:30.', 'Abutment-1/Foundation'],
    [34, 'concrete-cured', 'abutment', 'Abutment A1 footing', null, null, 'Seven days wet curing of the A1 footing completed; hessian lifted.', 'Abutment-1/Foundation'],
    [36, 'rebar-fixed', 'abutment', 'Abutment A1 front wall', 9.2, 't', 'A1 front wall starter bars and cage fixed.', 'Abutment-1/FrontWall'],
    [40, 'formwork-erected', 'abutment', 'Abutment A1 front wall', 118, 'm²', 'A1 front wall shutters erected and plumbed.', 'Abutment-1/FrontWall'],
    [43, 'concrete-poured', 'abutment', 'Abutment A1 front wall', 86, 'm³', 'A1 front wall poured in one lift, started 04:30 for the heat.', 'Abutment-1/FrontWall'],
    [50, 'concrete-cured', 'abutment', 'Abutment A1 front wall', null, null, 'A1 front wall cured seven days under wet hessian.', 'Abutment-1/FrontWall'],
    [51, 'rebar-fixed', 'abutment', 'Abutment A1 bearing beam', 3.1, 't', 'A1 bearing beam and plinth reinforcement fixed.', 'Abutment-1/BearingBeam'],
    [52, 'formwork-erected', 'abutment', 'Abutment A1 bearing beam', 36, 'm²', 'A1 bearing beam shutters erected.', 'Abutment-1/BearingBeam'],
    [54, 'concrete-poured', 'abutment', 'Abutment A1 bearing beam', 22, 'm³', 'A1 bearing beam and plinths poured.', 'Abutment-1/BearingBeam'],
    [58, 'rebar-fixed', 'abutment', 'Abutment A1 wing walls', 5.8, 't', 'Both A1 wing wall cages fixed.'],
    [61, 'formwork-erected', 'abutment', 'Abutment A1 wing walls', 142, 'm²', 'A1 wing wall shutters erected.'],
    [63, 'concrete-poured', 'abutment', 'Abutment A1 wing walls', 58, 'm³', 'Both A1 wing walls poured.'],
    [70, 'concrete-cured', 'abutment', 'Abutment A1 wing walls', null, null, 'A1 wing walls cured seven days.'],
    [85, 'bearing-set', 'bearings', 'Abutment A1 bearings', 5, 'nos', 'Five elastomeric bearings set on the A1 plinths on epoxy mortar.'],

    /* --- pier P1, on the near margin, in a cofferdam --- */
    [21, 'excavation', 'pier', 'Pier P1 cofferdam', 180, 'm³', 'Sheet-pile cofferdam at P1 closed; excavation to pile cap soffit begun.'],
    [24, 'dewatering', 'pier', 'Pier P1 cofferdam', null, null, 'P1 cofferdam pumped down; two 6-inch pumps running continuously.'],
    [26, 'excavation', 'pier', 'Pier P1 cofferdam', 110, 'm³', 'P1 excavation at formation. Piling rig on the platform.'],
    [28, 'pile-concreted', 'pier', 'Pier P1 F-1 piles', 10.4, 'm³', 'Pile F1-1 bored to 12.4 m, rock at 10.6 m; cage lowered and concreted by tremie.', pile(1, 1, 1)],
    [29, 'pile-concreted', 'pier', 'Pier P1 F-1 piles', 10.2, 'm³', 'Pile F1-2 bored to 12.2 m and concreted.', pile(1, 1, 2)],
    [30, 'pile-concreted', 'pier', 'Pier P1 F-1 piles', 10.6, 'm³', 'Pile F1-3 bored to 12.5 m and concreted.', pile(1, 1, 3)],
    [31, 'stoppage', 'pier', 'Pier P1 F-1 piles', null, null, 'Kelly bar winch failed at 09:00; rig stood for the day waiting on a fitter from Rawalpindi.'],
    [32, 'pile-concreted', 'pier', 'Pier P1 F-1 piles', 10.3, 'm³', 'Pile F1-4 bored to 12.3 m and concreted.', pile(1, 1, 4)],
    [33, 'pile-concreted', 'pier', 'Pier P1 F-2 piles', 10.5, 'm³', 'Pile F2-1 bored to 12.4 m and concreted.', pile(1, 2, 1)],
    [34, 'pile-concreted', 'pier', 'Pier P1 F-2 piles', 10.1, 'm³', 'Pile F2-2 bored to 12.1 m and concreted.', pile(1, 2, 2)],
    [35, 'pile-concreted', 'pier', 'Pier P1 F-2 piles', 10.4, 'm³', 'Pile F2-3 bored to 12.3 m and concreted.', pile(1, 2, 3)],
    [36, 'pile-concreted', 'pier', 'Pier P1 F-2 piles', 10.2, 'm³', 'Pile F2-4 bored to 12.2 m and concreted. Rig to P2.', pile(1, 2, 4)],
    [44, 'pile-test', 'pier', 'Pier P1 pile tests', null, null, 'Low-strain integrity tests on all eight P1 piles, eight days after the last; no anomaly.'],
    [46, 'rebar-fixed', 'pier', 'Pier P1 F-1 pile cap', 7.8, 't', 'P1 F-1 pile heads trimmed to cut-off level and cap reinforcement fixed.', 'Bent-1/Foundation-1'],
    [48, 'formwork-erected', 'pier', 'Pier P1 F-1 pile cap', 44, 'm²', 'P1 F-1 cap shutters erected.', 'Bent-1/Foundation-1'],
    [50, 'pilecap-poured', 'pier', 'Pier P1 F-1 pile cap', 31, 'm³', 'P1 F-1 pile cap poured.', 'Bent-1/Foundation-1'],
    [49, 'rebar-fixed', 'pier', 'Pier P1 F-2 pile cap', 7.8, 't', 'P1 F-2 pile heads trimmed and cap reinforcement fixed.', 'Bent-1/Foundation-2'],
    [51, 'formwork-erected', 'pier', 'Pier P1 F-2 pile cap', 44, 'm²', 'P1 F-2 cap shutters erected.', 'Bent-1/Foundation-2'],
    [53, 'pilecap-poured', 'pier', 'Pier P1 F-2 pile cap', 31, 'm³', 'P1 F-2 pile cap poured.', 'Bent-1/Foundation-2'],
    [57, 'concrete-cured', 'pier', 'Pier P1 F-1 pile cap', null, null, 'P1 F-1 cap cured seven days.', 'Bent-1/Foundation-1'],
    [60, 'concrete-cured', 'pier', 'Pier P1 F-2 pile cap', null, null, 'P1 F-2 cap cured seven days.', 'Bent-1/Foundation-2'],
    [55, 'rebar-fixed', 'pier', 'Pier P1 C-1 column', 4.4, 't', 'P1 C-1 column cage fixed on the cap starters, five days after the cap pour.', 'Bent-1/Column-1'],
    [57, 'formwork-erected', 'pier', 'Pier P1 C-1 column', 58, 'm²', 'P1 C-1 steel column form erected and plumbed.', 'Bent-1/Column-1'],
    [59, 'concrete-poured', 'pier', 'Pier P1 C-1 column', 17, 'm³', 'P1 C-1 column poured to the underside of the bent cap.', 'Bent-1/Column-1'],
    [61, 'formwork-struck', 'pier', 'Pier P1 C-1 column', 58, 'm²', 'P1 C-1 column form struck at 48 hours; curing compound applied.', 'Bent-1/Column-1'],
    [58, 'rebar-fixed', 'pier', 'Pier P1 C-2 column', 4.4, 't', 'P1 C-2 column cage fixed.', 'Bent-1/Column-2'],
    [60, 'formwork-erected', 'pier', 'Pier P1 C-2 column', 58, 'm²', 'P1 C-2 column form erected.', 'Bent-1/Column-2'],
    [62, 'concrete-poured', 'pier', 'Pier P1 C-2 column', 17, 'm³', 'P1 C-2 column poured.', 'Bent-1/Column-2'],
    [64, 'formwork-struck', 'pier', 'Pier P1 C-2 column', 58, 'm²', 'P1 C-2 column form struck at 48 hours.', 'Bent-1/Column-2'],
    [64, 'non-conformance', 'pier', 'Pier P1 C-2 column', null, null, 'NCR-001: honeycombing over 0.4 m² at the base of P1 C-2 where the kicker joint leaked grout. Repair by approved polymer mortar proposed.', 'Bent-1/Column-2'],
    [67, 'hold-point', 'pier', 'Pier P1 C-2 column', null, null, 'P1 C-2 base repair inspected and NCR-001 closed by the Resident Engineer.', 'Bent-1/Column-2'],
    [70, 'formwork-erected', 'pier', 'Pier P1 bent cap', 118, 'm²', 'P1 bent cap falsework and soffit erected off the pile caps, eight days after the last column pour.', 'Bent-1/CapBeam'],
    [74, 'rebar-fixed', 'pier', 'Pier P1 bent cap', 12.6, 't', 'P1 bent cap reinforcement fixed.', 'Bent-1/CapBeam'],
    [79, 'hold-point', 'pier', 'Pier P1 bent cap', null, null, 'P1 bent cap reinforcement, cover and falsework released for concreting.', 'Bent-1/CapBeam'],
    [80, 'concrete-poured', 'pier', 'Pier P1 bent cap', 48, 'm³', 'P1 bent cap poured, C40, cubes taken for 7 and 28 days.', 'Bent-1/CapBeam'],
    [87, 'concrete-cured', 'pier', 'Pier P1 bent cap', null, null, 'P1 bent cap cured seven days; 7-day cubes 31.5 MPa.', 'Bent-1/CapBeam'],
    [94, 'formwork-struck', 'pier', 'Pier P1 bent cap', 118, 'm²', 'P1 bent cap soffit and falsework struck at fourteen days.', 'Bent-1/CapBeam'],
    [106, 'survey-check', 'bearings', 'Pier P1 bearings', null, null, 'Bearing plinth levels on P1 and A1 surveyed; worst deviation 3 mm.'],
    [108, 'bearing-set', 'bearings', 'Pier P1 bearings', 10, 'nos', 'Ten bearings set on the P1 cap, both faces. 28-day cubes on the cap 44.2 MPa.'],

    /* --- pier P2, in the river channel --- */
    [38, 'excavation', 'pier', 'Pier P2 cofferdam', 190, 'm³', 'P2 cofferdam closed in the channel; excavation begun.'],
    [40, 'dewatering', 'pier', 'Pier P2 cofferdam', null, null, 'P2 cofferdam pumped down; seepage heavier than at P1, third pump brought in.'],
    [42, 'excavation', 'pier', 'Pier P2 cofferdam', 120, 'm³', 'P2 excavation at formation; rig walked onto the platform.'],
    [43, 'pile-concreted', 'pier', 'Pier P2 F-1 piles', 11.1, 'm³', 'Pile F1-1 bored to 13.0 m, rock at 11.4 m; concreted.', pile(2, 1, 1)],
    [44, 'pile-concreted', 'pier', 'Pier P2 F-1 piles', 10.9, 'm³', 'Pile F1-2 bored to 12.9 m and concreted.', pile(2, 1, 2)],
    [45, 'pile-concreted', 'pier', 'Pier P2 F-1 piles', 11.0, 'm³', 'Pile F1-3 bored to 13.0 m and concreted.', pile(2, 1, 3)],
    [46, 'pile-concreted', 'pier', 'Pier P2 F-1 piles', 11.2, 'm³', 'Pile F1-4 bored to 13.1 m and concreted.', pile(2, 1, 4)],
    [47, 'pile-concreted', 'pier', 'Pier P2 F-2 piles', 10.8, 'm³', 'Pile F2-1 bored to 12.8 m and concreted.', pile(2, 2, 1)],
    [48, 'pile-concreted', 'pier', 'Pier P2 F-2 piles', 11.0, 'm³', 'Pile F2-2 bored to 13.0 m and concreted.', pile(2, 2, 2)],
    [49, 'stoppage', 'pier', 'Pier P2 F-2 piles', null, null, 'Indus rising on snowmelt, 1.1 m in 24 hours. Rig withdrawn from the P2 platform to the bank.'],
    [50, 'stoppage', 'pier', 'Pier P2 cofferdam', null, null, 'P2 cofferdam overtopped at 02:00. Site kept off the channel.'],
    [53, 'stoppage', 'pier', 'Pier P2 cofferdam', null, null, 'River still high. No work in the channel.'],
    [55, 'dewatering', 'pier', 'Pier P2 cofferdam', null, null, 'River down 0.9 m. P2 cofferdam pumped out and the platform cleared of silt.'],
    [56, 'pile-concreted', 'pier', 'Pier P2 F-2 piles', 11.3, 'm³', 'Rig back on P2. Pile F2-3 bored to 13.1 m and concreted.', pile(2, 2, 3)],
    [57, 'pile-concreted', 'pier', 'Pier P2 F-2 piles', 11.1, 'm³', 'Pile F2-4 bored to 13.0 m and concreted. Rig demobilised.', pile(2, 2, 4)],
    [64, 'pile-test', 'pier', 'Pier P2 pile tests', null, null, 'Integrity tests on all eight P2 piles, seven days after the last; no anomaly.'],
    [66, 'rebar-fixed', 'pier', 'Pier P2 F-1 pile cap', 7.8, 't', 'P2 F-1 pile heads trimmed and cap reinforcement fixed.', 'Bent-2/Foundation-1'],
    [68, 'formwork-erected', 'pier', 'Pier P2 F-1 pile cap', 44, 'm²', 'P2 F-1 cap shutters erected.', 'Bent-2/Foundation-1'],
    [70, 'pilecap-poured', 'pier', 'Pier P2 F-1 pile cap', 31, 'm³', 'P2 F-1 pile cap poured.', 'Bent-2/Foundation-1'],
    [69, 'rebar-fixed', 'pier', 'Pier P2 F-2 pile cap', 7.8, 't', 'P2 F-2 pile heads trimmed and cap reinforcement fixed.', 'Bent-2/Foundation-2'],
    [71, 'formwork-erected', 'pier', 'Pier P2 F-2 pile cap', 44, 'm²', 'P2 F-2 cap shutters erected.', 'Bent-2/Foundation-2'],
    [73, 'pilecap-poured', 'pier', 'Pier P2 F-2 pile cap', 31, 'm³', 'P2 F-2 pile cap poured.', 'Bent-2/Foundation-2'],
    [77, 'concrete-cured', 'pier', 'Pier P2 F-1 pile cap', null, null, 'P2 F-1 cap cured seven days.', 'Bent-2/Foundation-1'],
    [80, 'concrete-cured', 'pier', 'Pier P2 F-2 pile cap', null, null, 'P2 F-2 cap cured seven days.', 'Bent-2/Foundation-2'],
    [75, 'rebar-fixed', 'pier', 'Pier P2 C-1 column', 4.4, 't', 'P2 C-1 column cage fixed.', 'Bent-2/Column-1'],
    [77, 'formwork-erected', 'pier', 'Pier P2 C-1 column', 58, 'm²', 'P2 C-1 column form erected.', 'Bent-2/Column-1'],
    [79, 'concrete-poured', 'pier', 'Pier P2 C-1 column', 17, 'm³', 'P2 C-1 column poured.', 'Bent-2/Column-1'],
    [81, 'formwork-struck', 'pier', 'Pier P2 C-1 column', 58, 'm²', 'P2 C-1 column form struck at 48 hours.', 'Bent-2/Column-1'],
    [78, 'rebar-fixed', 'pier', 'Pier P2 C-2 column', 4.4, 't', 'P2 C-2 column cage fixed.', 'Bent-2/Column-2'],
    [80, 'formwork-erected', 'pier', 'Pier P2 C-2 column', 58, 'm²', 'P2 C-2 column form erected.', 'Bent-2/Column-2'],
    [82, 'concrete-poured', 'pier', 'Pier P2 C-2 column', 17, 'm³', 'P2 C-2 column poured.', 'Bent-2/Column-2'],
    [84, 'formwork-struck', 'pier', 'Pier P2 C-2 column', 58, 'm²', 'P2 C-2 column form struck at 48 hours.', 'Bent-2/Column-2'],
    [90, 'formwork-erected', 'pier', 'Pier P2 bent cap', 118, 'm²', 'P2 bent cap falsework and soffit erected.', 'Bent-2/CapBeam'],
    [95, 'rebar-fixed', 'pier', 'Pier P2 bent cap', 12.6, 't', 'P2 bent cap reinforcement fixed.', 'Bent-2/CapBeam'],
    [101, 'non-conformance', 'pier', 'Pier P2 bent cap', null, null, 'NCR-002: hold point not released — cover to the bottom bars 25 to 30 mm against 50 mm specified over the south bearing shelf. Cage to be lifted and re-chaired.', 'Bent-2/CapBeam'],
    [107, 'hold-point', 'pier', 'Pier P2 bent cap', null, null, 'P2 bent cap cage re-chaired, cover checked at 50 mm; NCR-002 closed and pour released.', 'Bent-2/CapBeam'],
    [109, 'stoppage', null, null, null, null, 'Heavy monsoon rain from 04:00; P2 bent cap pour postponed. Site closed at 10:00.'],
    [112, 'concrete-poured', 'pier', 'Pier P2 bent cap', 48, 'm³', 'P2 bent cap poured, C40, cubes taken for 7 and 28 days.', 'Bent-2/CapBeam'],
    [119, 'concrete-cured', 'pier', 'Pier P2 bent cap', null, null, 'P2 bent cap cured seven days; 7-day cubes 30.8 MPa.', 'Bent-2/CapBeam'],
    [126, 'formwork-struck', 'pier', 'Pier P2 bent cap', 118, 'm²', 'P2 bent cap soffit and falsework struck at fourteen days.', 'Bent-2/CapBeam'],
    [134, 'bearing-set', 'bearings', 'Pier P2 bearings', 10, 'nos', 'Ten bearings set on the P2 cap at three weeks.'],

    /* --- abutment A2, on the far bank --- */
    [56, 'excavation', 'abutment', 'Abutment A2 excavation', 220, 'm³', 'A2 excavation begun on the far bank.'],
    [60, 'excavation', 'abutment', 'Abutment A2 excavation', 140, 'm³', 'A2 excavation at formation; foundation approved.'],
    [62, 'rebar-fixed', 'abutment', 'Abutment A2 footing', 11.5, 't', 'A2 footing reinforcement fixed.', 'Abutment-2/Foundation'],
    [64, 'formwork-erected', 'abutment', 'Abutment A2 footing', 64, 'm²', 'A2 footing edge shutters erected.', 'Abutment-2/Foundation'],
    [66, 'footing-poured', 'abutment', 'Abutment A2 footing', 145, 'm³', 'A2 footing poured.', 'Abutment-2/Foundation'],
    [73, 'concrete-cured', 'abutment', 'Abutment A2 footing', null, null, 'A2 footing cured seven days.', 'Abutment-2/Foundation'],
    [75, 'rebar-fixed', 'abutment', 'Abutment A2 front wall', 9.2, 't', 'A2 front wall cage fixed.', 'Abutment-2/FrontWall'],
    [79, 'formwork-erected', 'abutment', 'Abutment A2 front wall', 118, 'm²', 'A2 front wall shutters erected.', 'Abutment-2/FrontWall'],
    [83, 'concrete-poured', 'abutment', 'Abutment A2 front wall', 86, 'm³', 'A2 front wall poured.', 'Abutment-2/FrontWall'],
    [90, 'concrete-cured', 'abutment', 'Abutment A2 front wall', null, null, 'A2 front wall cured seven days.', 'Abutment-2/FrontWall'],
    [91, 'rebar-fixed', 'abutment', 'Abutment A2 bearing beam', 3.1, 't', 'A2 bearing beam reinforcement fixed.', 'Abutment-2/BearingBeam'],
    [92, 'formwork-erected', 'abutment', 'Abutment A2 bearing beam', 36, 'm²', 'A2 bearing beam shutters erected.', 'Abutment-2/BearingBeam'],
    [94, 'concrete-poured', 'abutment', 'Abutment A2 bearing beam', 22, 'm³', 'A2 bearing beam and plinths poured.', 'Abutment-2/BearingBeam'],
    [98, 'rebar-fixed', 'abutment', 'Abutment A2 wing walls', 5.8, 't', 'A2 wing wall cages fixed.'],
    [100, 'formwork-erected', 'abutment', 'Abutment A2 wing walls', 142, 'm²', 'A2 wing wall shutters erected.'],
    [103, 'concrete-poured', 'abutment', 'Abutment A2 wing walls', 58, 'm³', 'Both A2 wing walls poured, C30, cubes taken for 7 and 28 days.'],
    [110, 'concrete-cured', 'abutment', 'Abutment A2 wing walls', null, null, 'A2 wing walls cured seven days.'],
    [124, 'bearing-set', 'bearings', 'Abutment A2 bearings', 5, 'nos', 'Five bearings set on the A2 plinths.'],

    /* --- the girders, in the casting yard: two moulds on the pretensioning
       bed, a pair every six days. Strands stressed on the bed, the girder cast
       on them, and the strands released into it at transfer strength. --- */
    [42, 'hold-point', null, 'Casting yard', null, null, 'Casting yard beds, two steel moulds and the pretensioning abutments accepted by the Engineer.'],
    [52, 'girder-stressed', null, 'Span 1 girders (yard)', 2, 'nos', 'Eighteen 15.2 mm strands stressed on the bed for span 1 girders 1 and 2, a week behind programme: strand delivered late.'],
    [52, 'girder-cast', null, 'Span 1 girders (yard)', 2, 'nos', 'First pair of span 1 girders cast on the stressed strands.'],
    [57, 'hold-point', null, 'Span 1 girders (yard)', null, null, 'Span 1 girders 1 and 2 at five days, cubes 35.5 MPa against 33.1 for transfer; strands released and cut, girders to the stack.'],
    [58, 'girder-stressed', null, 'Span 1 girders (yard)', 2, 'nos', 'Strands stressed on the bed for span 1 girders 3 and 4.'],
    [58, 'girder-cast', null, 'Span 1 girders (yard)', 2, 'nos', 'Span 1 girders 3 and 4 cast.'],
    [63, 'hold-point', null, 'Span 1 girders (yard)', null, null, 'Span 1 girders 3 and 4 detensioned at transfer strength.'],
    [64, 'girder-stressed', null, 'Span 1 girders (yard)', 1, 'nos', 'Strands stressed for span 1 girder 5.'],
    [64, 'girder-cast', null, 'Span 1 girders (yard)', 1, 'nos', 'Span 1 girder 5 cast.'],
    [64, 'girder-stressed', null, 'Span 2 girders (yard)', 1, 'nos', 'Strands stressed in the second mould for span 2 girder 1.'],
    [64, 'girder-cast', null, 'Span 2 girders (yard)', 1, 'nos', 'Span 2 girder 1 cast in the second mould.'],
    [69, 'hold-point', null, 'Span 1 girders (yard)', null, null, 'Span 1 girder 5 detensioned. All five span 1 girders stacked.'],
    [69, 'hold-point', null, 'Span 2 girders (yard)', null, null, 'Span 2 girder 1 detensioned.'],
    [70, 'girder-stressed', null, 'Span 2 girders (yard)', 2, 'nos', 'Strands stressed on the bed for span 2 girders 2 and 3.'],
    [70, 'girder-cast', null, 'Span 2 girders (yard)', 2, 'nos', 'Span 2 girders 2 and 3 cast.'],
    [75, 'hold-point', null, 'Span 2 girders (yard)', null, null, 'Span 2 girders 2 and 3 detensioned.'],
    [76, 'girder-stressed', null, 'Span 2 girders (yard)', 2, 'nos', 'Strands stressed on the bed for span 2 girders 4 and 5.'],
    [76, 'girder-cast', null, 'Span 2 girders (yard)', 2, 'nos', 'Span 2 girders 4 and 5 cast.'],
    [81, 'hold-point', null, 'Span 2 girders (yard)', null, null, 'Span 2 girders 4 and 5 detensioned. All span 2 girders stacked.'],
    [82, 'girder-stressed', null, 'Span 3 girders (yard)', 2, 'nos', 'Strands stressed on the bed for span 3 girders 1 and 2.'],
    [82, 'girder-cast', null, 'Span 3 girders (yard)', 2, 'nos', 'Span 3 girders 1 and 2 cast.'],
    [87, 'hold-point', null, 'Span 3 girders (yard)', null, null, 'Span 3 girders 1 and 2 detensioned.'],
    [88, 'girder-stressed', null, 'Span 3 girders (yard)', 2, 'nos', 'Strands stressed on the bed for span 3 girders 3 and 4.'],
    [88, 'girder-cast', null, 'Span 3 girders (yard)', 2, 'nos', 'Span 3 girders 3 and 4 cast.'],
    [93, 'hold-point', null, 'Span 3 girders (yard)', null, null, 'Span 3 girders 3 and 4 detensioned.'],
    [94, 'girder-stressed', null, 'Span 3 girders (yard)', 1, 'nos', 'Strands stressed for span 3 girder 5, the last.'],
    [94, 'girder-cast', null, 'Span 3 girders (yard)', 1, 'nos', 'Span 3 girder 5 cast. Last girder.'],
    [99, 'hold-point', null, 'Span 3 girders (yard)', null, null, 'Span 3 girder 5 detensioned. All fifteen girders stacked in the yard.'],

    /* --- span 1 on, and the rest waiting on P2 --- */
    [126, 'hold-point', 'deck', 'Span 1 girders', null, null, '250 t crawler crane rigged on the A1 bank; lift plan and ground bearing accepted.'],
    [127, 'girder-erected', 'deck', 'Span 1 girders', 3, 'nos', 'Span 1 girders G-5 to G-3 landed on the A1 and P1 bearings. P1 cap 47 days old.'],
    [128, 'girder-erected', 'deck', 'Span 1 girders', 2, 'nos', 'Span 1 girders G-2 and G-1 landed. Span 1 complete.'],
    [131, 'rebar-fixed', 'deck', 'Span 1 diaphragms', 4.2, 't', 'Span 1 end and intermediate diaphragm reinforcement fixed between the girders.'],
    [134, 'formwork-erected', 'deck', 'Span 1 diaphragms', 96, 'm²', 'Span 1 diaphragm shutters erected.'],
    [137, 'diaphragm-cast', 'deck', 'Span 1 diaphragms', 12, 'nos', 'All twelve span 1 diaphragms poured.'],
    [132, 'rebar-fixed', 'abutment', 'Abutment A1 back wall', 2.8, 't', 'A1 back wall reinforcement fixed now span 1 girders are on.', 'Abutment-1/BackWall'],
    [135, 'formwork-erected', 'abutment', 'Abutment A1 back wall', 42, 'm²', 'A1 back wall shutters erected.', 'Abutment-1/BackWall'],
    [138, 'concrete-poured', 'abutment', 'Abutment A1 back wall', 19, 'm³', 'A1 back wall poured.', 'Abutment-1/BackWall'],
    [140, 'hold-point', 'deck', 'Span 2 girders', null, null, 'P2 bent cap 28 days old today; 28-day cubes 43.6 MPa against 40 specified. HP-001 released: span 2 lift tomorrow; crane walking to the P1 bank.'],
  ];
  const photos = reliefPhotos();
  const last = Math.max(...rows.map((r) => r[0]));
  return rows
    .map((row, order) => ({ row, order }))
    .sort((a, b) => a.row[0] - b.row[0] || a.order - b.order)
    .map(({ row: [day, activity, element, component, quantity, unit, note, componentId] }, i) => {
      const photo = photos.has(`${day}:${activity}`) ? reliefPhoto(day, activity) : null;
      const id = `${DEMO_PROGRESS_PREFIX}atk-${String(i + 1).padStart(3, '0')}`;
      const occurredOn = reliefDay(day);
      const created = `${occurredOn}T${String(11 + (i % 5)).padStart(2, '0')}:20:00Z`;
      /* The last four days are still with the site, the five before them with the engineer. */
      const state: ProgressEntry['state'] =
        last - day < 4 ? 'draft' : last - day < 9 ? 'review' : 'accepted';
      const reviewed = state === 'accepted' ? `${addDays(occurredOn, 1)}T05:30:00Z` : null;
      return {
        id,
        clientId: id,
        bridgeId: reliefId(),
        activity,
        element,
        component,
        componentId: componentId ?? null,
        quantity,
        unit,
        note,
        occurredOn,
        author: PEOPLE.site,
        state,
        reviewedBy: reviewed === null ? null : PEOPLE.resident,
        reviewedAt: reviewed,
        hasPhoto: photo !== null,
        ...(photo ?? {}),
        createdAt: created,
        updatedAt: reviewed ?? created,
      };
    });
}

/** Where one entry's photograph and its thumbnail are served from. */
function reliefPhoto(day: number, activity: string): { photoUrl: string; photoThumbUrl: string } {
  const name = `./demo/progress/atk-${String(day).padStart(3, '0')}-${activity}`;
  return { photoUrl: `${name}.jpg`, photoThumbUrl: `${name}-t.jpg` };
}

/**
 * Day `n` of the Indus Relief Bridge contract, before sliding — day 140 is
 * DEMO_ANCHOR. A function, not a constant, because `reliefWorks` is called
 * while FWO_DEMO_PROGRESS above is being initialised, and a `const` declared
 * down here would still be in its temporal dead zone.
 */
function reliefDay(n: number): string {
  return addDays('2026-05-03', n);
}

/**
 * The overpass's works, from its piles to its taking-over, and NOT slid.
 *
 * A dozen accepted entries rather than a whole record: enough for the
 * Scheduler's history and the playback to show a finished structure, and each
 * one on a part of the model by id. Dated where the programme dates them,
 * because a completed record has nothing to keep in the current week. Recorded
 * by the site agent, reviewed the next morning by the resident engineer.
 */
function overpassWorks(): ProgressEntry[] {
  const rows: [day: string, activity: string, element: string | null, component: string, quantity: number | null, unit: string | null, note: string, componentId: string | null][] = [
    ['2025-01-29', 'pile-concreted', 'pier', 'Piers P1 and P2 piles', 9.9, 'm³', 'First pile, P1 F-1 pile 1, bored to 12.5 m and concreted.', 'Bent-1/Foundation-1/Pile-1'],
    ['2025-02-10', 'footing-poured', 'abutment', 'Abutments A1 and A2 footings', 145, 'm³', 'A1 footing poured, C30.', 'Abutment-1/Foundation'],
    ['2025-02-17', 'pile-concreted', 'pier', 'Piers P1 and P2 piles', 9.8, 'm³', 'Sixteenth and last pile, P2 F-2 pile 4, concreted.', 'Bent-2/Foundation-2/Pile-4'],
    ['2025-03-06', 'pilecap-poured', 'pier', 'Piers P1 and P2 pile caps', 30.4, 'm³', 'P1 F-1 pile cap poured.', 'Bent-1/Foundation-1'],
    ['2025-03-12', 'girder-cast', null, 'Girders (yard)', 2, 'nos', 'First pair of girders cast on the pretensioning bed.', null],
    ['2025-04-02', 'concrete-poured', 'pier', 'Piers P1 and P2 columns and bent caps', 16.3, 'm³', 'P1 C-1 column poured.', 'Bent-1/Column-1'],
    ['2025-04-20', 'concrete-poured', 'abutment', 'Abutments A1 and A2 walls', 86, 'm³', 'A2 front wall poured in one lift.', 'Abutment-2/FrontWall'],
    ['2025-05-14', 'concrete-poured', 'pier', 'Piers P1 and P2 columns and bent caps', 48.4, 'm³', 'P2 bent cap poured, the last of the substructure.', 'Bent-2/CapBeam'],
    ['2025-06-20', 'girder-erected', 'deck', 'All girders', 1, 'nos', 'Span 2 girder G-1 landed, the last of fifteen.', 'Span-2/Girder-1'],
    ['2025-07-30', 'deck-poured', 'deck', 'Deck slab', 62, 'm³', 'Span 2 deck slab poured.', 'Span-2/Deck'],
    ['2025-09-02', 'parapet-built', 'deck', 'Barriers', 150, 'm', 'Jersey barriers slip-formed on both edges.', null],
    ['2025-09-24', 'wearing-course', 'deck', 'Wearing course', 825, 'm²', '50 mm wearing course laid over the three spans.', null],
    ['2025-10-15', 'hold-point', null, 'Handover', null, null, 'Taking-over inspection with the Resident Engineer; bearings, joints and drainage accepted.', null],
  ];
  return rows.map(([occurredOn, activity, element, component, quantity, unit, note, componentId], i) => {
    const id = `${DEMO_PROGRESS_PREFIX}ovp-${String(i + 1).padStart(3, '0')}`;
    const created = `${occurredOn}T14:10:00Z`;
    const reviewed = `${addDays(occurredOn, 1)}T05:30:00Z`;
    return {
      id,
      clientId: id,
      bridgeId: overpassId(),
      activity,
      element,
      component,
      componentId,
      quantity,
      unit,
      note,
      occurredOn,
      author: PEOPLE.site,
      state: 'accepted' as const,
      reviewedBy: PEOPLE.resident,
      reviewedAt: reviewed,
      hasPhoto: false,
      createdAt: created,
      updatedAt: reviewed,
    };
  });
}

/**
 * The day the construction table above is written around. Its dates are a
 * SHAPE, not dates.
 *
 * A demonstration that is empty is not a demonstration. The report the
 * Construction screen opens on is the current week, so entries with fixed dates
 * fall out of it the moment the week turns — which is what happened: six entries
 * sat in the week of the 17th, the report opened on the 21st, and the document
 * FWO would be shown said "No entries were recorded against this register in this
 * period" over a register that had six.
 *
 * So the spacing in the table is kept and the whole run is slid forward, newest
 * onto today. The overpass's completed record is not slid: nothing about a
 * finished structure belongs in the current week.
 *
 * AND IT IS ONE MORE WAY IN WHICH THESE ARE NOT RECORDS. A real entry is dated by
 * the person who did the work; these are dated by the clock of whoever opens the
 * page, which is the kind of thing no register may ever do. It is safe here for
 * the one reason everything in this file is safe: it is behind a build flag, and
 * the card and the report both say on their face that these rows are invented.
 */
const DEMO_ANCHOR = '2026-09-20';

/** Add whole days to an ISO date, keeping a timestamp's time of day if it has one. */
function addDays(iso: string, days: number): string {
  const at = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  const day = at.toISOString().slice(0, 10);
  return iso.length > 10 ? `${day}${iso.slice(10)}` : day;
}

/** How far the table has to slide for its newest entry to land today. */
function demoDayShift(): number {
  const today = `${new Date().toISOString().slice(0, 10)}T00:00:00Z`;
  return Math.round((Date.parse(today) - Date.parse(`${DEMO_ANCHOR}T00:00:00Z`)) / 86_400_000);
}

/** One entry, slid onto the week this page is being looked at — the construction bridge's only. */
function slid(e: ProgressEntry, days: number): ProgressEntry {
  if (e.bridgeId !== reliefId()) return { ...e };
  return {
    ...e,
    occurredOn: addDays(e.occurredOn, days),
    createdAt: addDays(e.createdAt, days),
    updatedAt: addDays(e.updatedAt, days),
    reviewedAt: e.reviewedAt === null ? null : addDays(e.reviewedAt, days),
  };
}

/**
 * The fabricated construction record for one structure, or nothing.
 *
 * Copied on the way out, the same as the register and the defect log above: this
 * is a module constant, and a list handed out by reference is one an in-session
 * edit could alter for every later read.
 *
 * Scoped to a bridge because this is read per structure — `refreshProgress` asks
 * about the record that is open, not about the department.
 */
export function demoProgressFor(tenant: string, bridgeId: string): ProgressEntry[] {
  if (tenant === 'ph-dpwh') return dpwhProgressFor(tenant, bridgeId);
  if (!demoEnabled() || tenant !== 'pk-fwo') return [];
  const days = demoDayShift();
  return FWO_DEMO_PROGRESS.filter((e) => e.bridgeId === bridgeId).map((e) => slid(e, days));
}

/**
 * The same entries, for a PERIOD rather than a structure.
 *
 * What the Construction screen's report is assembled from: it spans the whole
 * department over a range of days, so it selects by date and not by bridge. The
 * range ends are inclusive and compared as ISO strings, which sort correctly by
 * day — the same comparison the endpoints make in SQL.
 */
export function demoProgressInPeriod(tenant: string, from: string, to: string): ProgressEntry[] {
  if (tenant === 'ph-dpwh') return dpwhProgressInPeriod(tenant, from, to);
  if (!demoEnabled() || tenant !== 'pk-fwo') return [];
  const days = demoDayShift();
  return FWO_DEMO_PROGRESS.map((e) => slid(e, days)).filter(
    (e) => e.occurredOn >= from && e.occurredOn <= to,
  );
}

/**
 * What a merged list of entries adds up to.
 *
 * The server sends a summary with its entries (`summariseProgress`), and that
 * summary is the truth about what D1 holds — so once fabricated rows are added to
 * the rows it counted, it is no longer a summary of what is on screen. The card
 * leads with "N recorded" and with how many are unchecked, and those two numbers
 * disagreeing with the rows beneath them is exactly the small wrongness that makes
 * a reader stop trusting the rest.
 *
 * Recomputed by the server's own rules: quantities total PER ACTIVITY AND UNIT
 * (LC-72), never across either — cubic metres dug and cubic metres poured are
 * the same unit and different work, and formwork erected and struck are the
 * same square metres counted twice if they are added.
 *
 * It lives in this file rather than beside the real code for the reason everything
 * here does: it goes when this file goes, and nothing permanent has to be unpicked
 * to remove it.
 */
export function summariseWithDemo(events: readonly ProgressEntry[]): ProgressSummary {
  const totals = new Map<string, ProgressSummary['quantities'][number]>();
  const structures = new Set<string>();
  let draft = 0;

  for (const e of events) {
    structures.add(e.bridgeId);
    if (e.state === 'draft') draft += 1;
    if (e.quantity !== null && e.unit !== null) {
      const key = `${e.activity}\u0000${e.unit}`;
      const row = totals.get(key) ?? { activity: e.activity, unit: e.unit, total: 0 };
      row.total += e.quantity;
      totals.set(key, row);
    }
  }

  return { count: events.length, draft, structures: structures.size, quantities: [...totals.values()] };
}

/* --------------------------- the rest of the site record --------------------------- */

/** The prefix every fabricated return, incident and document carries — see `isDemoSiteRecord`. */
const DEMO_SITE_PREFIX = 'demo-site-';

/**
 * Whether a daily return, incident, document, test or register item came out of
 * this file.
 *
 * Read off the id for the reason `isDemoProgress` is: nothing permanent is added
 * to the register types for something temporary. The site record's pages count
 * these and say so, the promise the Works card keeps.
 */
export function isDemoSiteRecord(r: { id: string }): boolean {
  return r.id.startsWith(DEMO_SITE_PREFIX);
}

/**
 * A fabricated daily return for every day of the contract so far.
 *
 * EVERY FIGURE IS INVENTED. Written off the works rather than typed out: each
 * day's gang and plant follow from what the works say was done that day, so the
 * diary and the entries it explains cannot drift apart — the girder lifts are
 * the days the crawler crane is on the return, and the flood is three days with
 * the channel empty of men.
 *
 * The weather follows the season at Attock from early May: dry heat into late
 * June, then the monsoon. Days not listed in RELIEF_WEATHER were clear.
 */
const RELIEF_WEATHER: Readonly<Record<number, string>> = {
  6: 'overcast',
  33: 'extreme-heat',
  34: 'extreme-heat',
  39: 'extreme-heat',
  40: 'extreme-heat',
  41: 'extreme-heat',
  47: 'overcast',
  63: 'overcast',
  65: 'rain',
  71: 'overcast',
  72: 'rain',
  85: 'overcast',
  86: 'rain',
  97: 'rain',
  98: 'overcast',
  99: 'storm',
  104: 'rain',
  105: 'overcast',
  109: 'heavy-rain',
  110: 'heavy-rain',
  111: 'overcast',
  116: 'overcast',
  117: 'rain',
  118: 'overcast',
  121: 'rain',
  122: 'overcast',
  129: 'overcast',
};

/** What a day's activities put on site: trades, plant, and a head count. */
function gangFor(activities: readonly string[], day: number): { labour: number; trades: string; plant: string } {
  const has = (...ids: string[]) => activities.some((a) => ids.includes(a));
  /* Mobilising for the first fortnight; after that a standing establishment of
     general labour, drivers, the batching plant crew and security. */
  const trades: string[] = [];
  const plant: string[] = [];
  let labour = day < 14 ? 12 : 26;
  if (has('pile-concreted', 'pile-test')) {
    trades.push('piling crew 9');
    plant.push('Piling rig', 'crane 50 t', 'bentonite plant');
    labour += 11;
  }
  if (has('excavation', 'dewatering')) {
    trades.push('operators 3');
    plant.push('Excavator ×2', 'tipper ×3', 'pumps');
    labour += 5;
  }
  if (has('rebar-fixed')) {
    trades.push('steel fixers 12');
    labour += 12;
  }
  if (has('formwork-erected', 'formwork-struck')) {
    trades.push('carpenters 10');
    labour += 10;
  }
  if (has('concrete-poured', 'pilecap-poured', 'footing-poured', 'diaphragm-cast')) {
    trades.push('concrete gang 14');
    plant.push('Concrete pump', 'transit mixers ×5', 'vibrators');
    labour += 14;
  }
  if (has('girder-erected') || (day >= 126 && day <= 128)) {
    trades.push('riggers 8');
    plant.push('Crawler crane 250 t');
    labour += 10;
  }
  if (has('bearing-set')) {
    trades.push('fitters 5');
    labour += 5;
  }
  if (day >= 42 && day <= 99) {
    trades.push('casting yard 16');
    plant.push('Yard gantry', has('girder-stressed') ? 'stressing jack' : 'yard vibrators');
    labour += 16;
  }
  if (has('setting-out', 'survey-check')) {
    trades.push('surveyors 3');
    plant.push('Total station');
    labour += 3;
  }
  trades.push(`general labour ${labour - trades.length * 8 > 8 ? labour - trades.length * 8 : 8}`);
  plant.push('generator');
  return { labour, trades: trades.join(', '), plant: plant.join(', ') };
}

function reliefReturns(works: readonly ProgressEntry[]): SiteReturn[] {
  const days = 141;
  return Array.from({ length: days }, (_, n) => {
    const day = reliefDay(n);
    const id = `${DEMO_SITE_PREFIX}ret-atk-${String(n + 1).padStart(3, '0')}`;
    const weather = RELIEF_WEATHER[n] ?? 'clear';
    const done = works.filter((e) => e.occurredOn === day);
    const stopped = done.some((e) => e.activity === 'stoppage');
    const lost = weather === 'heavy-rain';
    const gang = gangFor(done.map((e) => e.activity), n);
    /* The last four days are still with the site, the five before them with the engineer. */
    const state: SiteReturn['state'] = days - 1 - n < 4 ? 'draft' : days - 1 - n < 9 ? 'review' : 'accepted';
    const reviewed = state === 'accepted' ? `${addDays(day, 1)}T05:45:00Z` : null;
    const note = lost
      ? 'Heavy rain; site closed by 10:00.'
      : stopped
        ? 'Work stopped for part of the day — see the works entry.'
        : weather === 'extreme-heat'
          ? 'Concreting only before 10:00; afternoon work on shaded faces.'
          : '';
    return {
      id,
      clientId: id,
      bridgeId: reliefId(),
      returnedOn: day,
      weather,
      labour: lost ? 9 : stopped ? Math.round(gang.labour * 0.6) : gang.labour,
      labourNote: lost ? 'watchmen 3, pump attendants 6' : gang.trades,
      plant: lost ? 'pumps, generator' : gang.plant,
      hours: lost ? 4 : weather === 'extreme-heat' || stopped ? 7 : 10,
      note: note.trim(),
      author: PEOPLE.site,
      state,
      reviewedBy: reviewed === null ? null : PEOPLE.resident,
      reviewedAt: reviewed,
      createdAt: `${day}T15:30:00Z`,
      updatedAt: reviewed ?? `${day}T15:30:00Z`,
    };
  });
}

/**
 * Five fabricated safety and environmental reports. EVERY ONE IS INVENTED.
 *
 * A slurry spill because piling beside a river produces one, an overtopped
 * cofferdam because the Indus rises every summer, a swinging girder because a
 * crane lift is where a bridge site nearly kills somebody, and one still open
 * because a register whose every incident is closed is one a reader stops
 * believing. The site agent reports each; the resident engineer closes them.
 */
function reliefIncidents(): SiteIncident[] {
  const rows: (Pick<SiteIncident, 'kind' | 'element' | 'description' | 'action'> & { day: number; closedDay: number | null })[] = [
    {
      day: 30,
      closedDay: 31,
      kind: 'environmental',
      element: 'pier',
      description:
        'Bentonite slurry overtopped the P1 settling pit overnight and ran onto the river margin.',
      action: 'Pit bund raised, slurry recovered by vacuum tanker, pit level added to the night watchman’s round.',
    },
    {
      day: 50,
      closedDay: 55,
      kind: 'dangerous-occurrence',
      element: 'pier',
      description:
        'P2 cofferdam overtopped by the rising river at 02:00. Nobody on the platform; the rig had been withdrawn the day before.',
      action: 'River gauge readings twice daily; work in the channel stops at a rise of 0.5 m in 12 hours.',
    },
    {
      day: 58,
      closedDay: 58,
      kind: 'first-aid',
      element: 'pier',
      description: 'Steel fixer’s palm punctured on an uncapped starter bar at P1.',
      action: 'Dressed on site. Mushroom caps fitted to every exposed bar end.',
    },
    {
      day: 127,
      closedDay: 128,
      kind: 'near-miss',
      element: 'deck',
      description:
        'Girder G-4 swung on the lift when a tag line was dropped; it passed within 2 m of the P1 cap crew.',
      action: 'Exclusion zone under every lift enforced by the banksman; two tag lines per girder.',
    },
    {
      day: 136,
      closedDay: null,
      kind: 'near-miss',
      element: 'deck',
      description:
        'Steel fixer found on the span 1 girder top flanges fixing diaphragm bars without a harness.',
      action: 'Static line along the girders ordered.',
    },
  ];
  return rows.map(({ day, closedDay, ...r }, i) => {
    const id = `${DEMO_SITE_PREFIX}inc-atk-${String(i + 1).padStart(3, '0')}`;
    const occurredOn = reliefDay(day);
    return {
      ...r,
      id,
      clientId: id,
      bridgeId: reliefId(),
      occurredOn,
      closed: closedDay !== null,
      closedOn: closedDay === null ? null : reliefDay(closedDay),
      closedBy: closedDay === null ? null : PEOPLE.resident,
      author: PEOPLE.site,
      createdAt: `${occurredOn}T16:10:00Z`,
      updatedAt: `${closedDay === null ? occurredOn : reliefDay(closedDay)}T16:10:00Z`,
    };
  });
}

/**
 * Ten fabricated documents, and what opening one gives you.
 *
 * NOT A CERTIFICATE. A filed document is fetched from /api/site-document, and
 * these have no object in the bucket, so each carries its own file instead — a
 * short text that says, first line, that it is a demonstration and certifies
 * nothing. Its size is its real size.
 */
function reliefDocuments(works: readonly ProgressEntry[]): SiteDocument[] {
  const workOn = (activity: string, day: string): string | null =>
    works.find((e) => e.activity === activity && e.occurredOn === day)?.id ?? null;
  const days = demoDayShift();
  return RELIEF_DOCUMENTS.map((r, i) => {
    const id = `${DEMO_SITE_PREFIX}doc-atk-${String(i + 1).padStart(3, '0')}`;
    const text = [
      r.title,
      `Indus Relief Bridge, ${reliefId()}`,
      '',
    ].join('\n');
    const datedOn = r.day === null ? null : reliefDay(r.day);
    return {
      id,
      clientId: id,
      bridgeId: reliefId(),
      /* The works handed in are already slid, so the link is made on a slid day. */
      eventId: r.work ? workOn(r.work[0], addDays(reliefDay(r.work[1]), days)) : null,
      kind: r.kind,
      title: r.title,
      datedOn,
      note: r.note,
      contentType: 'text/plain',
      bytes: new TextEncoder().encode(text).length,
      filename: `${r.title.replace(/[^\w\- ]+/g, '').replace(/\s+/g, ' ').trim()}.txt`,
      fileUrl: `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`,
      author: PEOPLE.site,
      createdAt: `${datedOn ?? reliefDay(100)}T12:00:00Z`,
    };
  });
}

/** The documents, in the order they were filed. The strand certificate is the fourth (see RELIEF_TESTS). */
const RELIEF_DOCUMENTS: readonly { kind: string; title: string; day: number | null; work?: [string, number]; note: string }[] = [
  { kind: 'method-statement', title: 'IRB-MS-001 Bored piling at piers P1 and P2', day: -8, note: 'Rig, tremie procedure, slurry management and the river-level trigger.' },
  { kind: 'test-certificate', title: 'Pile integrity test report — P1, piles F1-1 to F2-4', day: 44, work: ['pile-test', 44], note: '' },
  { kind: 'material-certificate', title: 'Prestressing strand certificate — 15.2 mm (139 mm²), Grade 270, lot 22-417', day: 49, note: 'Delivered a week after the programme date.' },
  { kind: 'ncr', title: 'NCR-001 Honeycombing at P1 C-2 column base', day: 64, work: ['non-conformance', 64], note: '' },
  { kind: 'test-certificate', title: 'Pile integrity test report — P2, piles F1-1 to F2-4', day: 64, work: ['pile-test', 64], note: '' },
  { kind: 'ncr', title: 'NCR-002 Cover to P2 bent cap bottom bars', day: 101, work: ['non-conformance', 101], note: '' },
  { kind: 'test-certificate', title: '28-day cube results — P1 bent cap', day: 108, work: ['concrete-poured', 80], note: '44.2 MPa mean against 40 MPa specified.' },
  { kind: 'method-statement', title: 'IRB-MS-006 Girder erection by 250 t crawler crane', day: 114, note: 'Lift plan, ground bearing and the exclusion zone.' },
  { kind: 'test-certificate', title: '7-day cube results — P2 bent cap', day: 119, work: ['concrete-poured', 112], note: '30.8 MPa mean.' },
  { kind: 'survey-record', title: 'Bearing plinth levels — A1 and P1', day: 106, work: ['survey-check', 106], note: '' },
];

/**
 * The test results (LC-53), every one invented: cubes from the pours the works
 * record, at the ages the works quote them, the transfer cubes that released
 * the first girders, both piers' integrity tests and the strand certificate.
 *
 * ONE FAILS. The A2 wing walls' 28-day cubes come in under their C30, and the
 * failure raises its NCR the way the register does (`ncr:<test id>`), in the
 * name of whoever recorded the result — NCR-003, still open. Recorded by the
 * site agent; a result is not reviewed (ROADMAP, 2026-09-29).
 */
function reliefTests(works: readonly ProgressEntry[]): SiteTest[] {
  const workOn = (activity: string, day: number, component: RegExp): string | null =>
    works.find((e) => e.activity === activity && e.occurredOn === addDays(reliefDay(day), demoDayShift()) && component.test(e.component ?? ''))?.id ?? null;
  type Row = Partial<SiteTest> & { kind: string; day: number; pour?: [string, number, RegExp]; complies?: boolean };
  const rows: Row[] = [
    { kind: 'concrete-cube', day: 34, pour: ['footing-poured', 27, /A1 footing/], component: 'Abutment A1 footing', componentId: 'Abutment-1/Foundation', mix: 'C30', ageDays: 7, strengthMpa: 23.1, requiredMpa: 20 },
    { kind: 'pile-integrity', day: 44, component: 'Pier P1 pile tests', method: 'low-strain', resultClass: 'sound', documentId: `${DEMO_SITE_PREFIX}doc-atk-002` },
    { kind: 'strand-cert', day: 49, grade: 'Grade 270, 15.2 mm (139 mm²)', lot: '22-417', complies: true, documentId: `${DEMO_SITE_PREFIX}doc-atk-003` },
    { kind: 'concrete-cube', day: 55, pour: ['footing-poured', 27, /A1 footing/], component: 'Abutment A1 footing', componentId: 'Abutment-1/Foundation', mix: 'C30', ageDays: 28, strengthMpa: 36.4, requiredMpa: 30 },
    { kind: 'concrete-cube', day: 57, pour: ['girder-cast', 52, /Span 1/], component: 'Span 1 girders (yard)', mix: 'C40 girder', ageDays: 5, strengthMpa: 35.5, requiredMpa: 33.1, note: 'Transfer strength, 80% of f′c.' },
    { kind: 'pile-integrity', day: 64, component: 'Pier P2 pile tests', method: 'low-strain', resultClass: 'sound', documentId: `${DEMO_SITE_PREFIX}doc-atk-005` },
    { kind: 'concrete-cube', day: 87, pour: ['concrete-poured', 80, /P1 bent cap/], component: 'Pier P1 bent cap', componentId: 'Bent-1/CapBeam', mix: 'C40', ageDays: 7, strengthMpa: 31.5, requiredMpa: 28 },
    { kind: 'concrete-cube', day: 108, pour: ['concrete-poured', 80, /P1 bent cap/], component: 'Pier P1 bent cap', componentId: 'Bent-1/CapBeam', mix: 'C40', ageDays: 28, strengthMpa: 44.2, requiredMpa: 40, documentId: `${DEMO_SITE_PREFIX}doc-atk-007` },
    { kind: 'concrete-cube', day: 110, pour: ['concrete-poured', 103, /A2 wing walls/], component: 'Abutment A2 wing walls', mix: 'C30', ageDays: 7, strengthMpa: 20.6, requiredMpa: 20 },
    { kind: 'concrete-cube', day: 119, pour: ['concrete-poured', 112, /P2 bent cap/], component: 'Pier P2 bent cap', componentId: 'Bent-2/CapBeam', mix: 'C40', ageDays: 7, strengthMpa: 30.8, requiredMpa: 28, documentId: `${DEMO_SITE_PREFIX}doc-atk-009` },
    { kind: 'concrete-cube', day: 131, pour: ['concrete-poured', 103, /A2 wing walls/], component: 'Abutment A2 wing walls', mix: 'C30', ageDays: 28, strengthMpa: 26.8, requiredMpa: 30, note: 'Three cubes, 26.1 to 27.4 MPa. Cores ordered.' },
    { kind: 'concrete-cube', day: 140, pour: ['concrete-poured', 112, /P2 bent cap/], component: 'Pier P2 bent cap', componentId: 'Bent-2/CapBeam', mix: 'C40', ageDays: 28, strengthMpa: 43.6, requiredMpa: 40 },
  ];
  return rows.map(({ day, pour, complies, ...r }, i) => {
    const id = `${DEMO_SITE_PREFIX}test-atk-${String(i + 1).padStart(3, '0')}`;
    const testedOn = reliefDay(day);
    const test: SiteTest = {
      id,
      clientId: id,
      bridgeId: reliefId(),
      testedOn,
      eventId: pour ? workOn(...pour) : null,
      component: null,
      componentId: null,
      documentId: null,
      mix: null,
      ageDays: null,
      strengthMpa: null,
      requiredMpa: null,
      method: null,
      resultClass: null,
      testLoadKn: null,
      settlementMm: null,
      allowedMm: null,
      lot: null,
      grade: null,
      ncrId: null,
      ncrRef: null,
      note: '',
      author: PEOPLE.site,
      createdAt: `${testedOn}T13:00:00Z`,
      ...r,
      passed: false,
    };
    test.passed = testPasses({ ...test, complies: complies ?? null }) === true;
    if (!test.passed) {
      test.ncrId = `${DEMO_SITE_PREFIX}item-atk-ncr-003`;
      test.ncrRef = itemRef('ncr', 3);
    }
    return test;
  });
}

/**
 * The register of hold points, NCRs and RFIs (LC-54), every one invented.
 *
 * The two NCRs the works raised and closed at P1 C-2 and the P2 bent cap; the
 * third, raised by the failed A2 wing wall cubes and still open; the hold point
 * the site called before the span 2 lift, released by the resident engineer on
 * the P2 cap's 28-day cubes; and an RFI on the A2 joint recess, answered and not
 * yet closed.
 */
function reliefItems(tests: readonly SiteTest[]): SiteItem[] {
  const failed = tests.find((t) => !t.passed)!;
  const at = (day: number, time: string) => `${reliefDay(day)}T${time}:00Z`;
  const item = (over: Partial<SiteItem> & Pick<SiteItem, 'id' | 'kind' | 'seq' | 'title' | 'status' | 'raisedBy' | 'raisedAt'>): SiteItem => ({
    clientId: over.id,
    bridgeId: reliefId(),
    ref: itemRef(over.kind as 'ncr' | 'rfi' | 'hold-point', over.seq),
    detail: '',
    owner: PEOPLE.resident,
    dueOn: null,
    activityId: null,
    activityName: null,
    component: null,
    componentId: null,
    eventId: null,
    testId: null,
    answer: null,
    answeredBy: null,
    answeredAt: null,
    closeNote: null,
    closedBy: null,
    closedAt: null,
    updatedAt: over.closedAt ?? over.answeredAt ?? over.raisedAt,
    ...over,
  });
  return [
    item({
      id: `${DEMO_SITE_PREFIX}item-atk-ncr-001`, kind: 'ncr', seq: 1, status: 'closed',
      title: 'Honeycombing at P1 C-2 column base',
      detail: 'Honeycombing over 0.4 m² where the kicker joint leaked grout. Repair by approved polymer mortar.',
      component: 'Pier P1 C-2 column', componentId: 'Bent-1/Column-2',
      raisedBy: PEOPLE.resident, raisedAt: at(64, '15:00'),
      closeNote: 'Repair inspected and accepted.', closedBy: PEOPLE.resident, closedAt: at(67, '11:00'),
    }),
    item({
      id: `${DEMO_SITE_PREFIX}item-atk-ncr-002`, kind: 'ncr', seq: 2, status: 'closed',
      title: 'Cover to P2 bent cap bottom bars',
      detail: 'Cover 25 to 30 mm against 50 mm specified over the south bearing shelf. Cage to be lifted and re-chaired.',
      component: 'Pier P2 bent cap', componentId: 'Bent-2/CapBeam',
      raisedBy: PEOPLE.resident, raisedAt: at(101, '10:30'),
      closeNote: 'Cage re-chaired; cover checked at 50 mm.', closedBy: PEOPLE.resident, closedAt: at(107, '09:00'),
    }),
    item({
      id: failed.ncrId!, kind: 'ncr', seq: 3, status: 'open',
      title: failedTestTitle(failed),
      detail: 'Cores to be taken from both A2 wing walls and tested before the backfill.',
      dueOn: reliefDay(145),
      component: failed.component, testId: failed.id, eventId: failed.eventId,
      raisedBy: failed.author, raisedAt: failed.createdAt,
    }),
    item({
      id: `${DEMO_SITE_PREFIX}item-atk-hp-001`, kind: 'hold-point', seq: 1, status: 'closed',
      title: 'Span 2 girder lift',
      detail: 'P2 bearings set, crane rigged on the P1 bank, lift plan re-issued for the channel side.',
      activityId: 'A1720', activityName: 'Girders — erect', component: 'Span 2 girders',
      dueOn: reliefDay(140),
      raisedBy: PEOPLE.site, raisedAt: at(138, '16:00'),
      closeNote: 'P2 bent cap 28-day cubes 43.6 MPa against 40. Released for the lift.', closedBy: PEOPLE.resident, closedAt: at(140, '15:30'),
    }),
    item({
      id: `${DEMO_SITE_PREFIX}item-atk-rfi-001`, kind: 'rfi', seq: 1, status: 'answered',
      title: 'Expansion joint recess depth at A2',
      detail: 'Drawing IRB-DR-214 shows an 80 mm recess; the joint supplier asks for 100 mm.',
      component: 'Abutment A2 back wall',
      raisedBy: PEOPLE.site, raisedAt: at(118, '14:00'),
      answer: 'Form the recess to 100 mm. IRB-DR-214 Rev B issued.', answeredBy: PEOPLE.resident, answeredAt: at(121, '10:00'),
    }),
  ];
}

/* --------------------------- the programme --------------------------- */

/**
 * The contract programme for the Indus Relief Bridge, frozen before work began,
 * and a warning.
 *
 * EVERY DATE IS INVENTED. It is what Construction Scheduler shows for this
 * structure in place of a programme generated from the modeller's tree: it is
 * written to the same model as the works above, activity for activity, and
 * planned with the same curing rules the works obey — so the variance it
 * produces is the work running late, not the plan being impossible.
 *
 * AND IT IS SOLVED, NOT TYPED (LC-44). Each row gives a duration in working
 * days and its predecessors with their type and lag; the dates, the float and
 * the critical path are what src/data/programme.ts makes of them on the site's
 * seven-day calendar, exactly as a baseline taken from a model is frozen. So the
 * forecast re-solves the same logic from what has happened, and the slip it
 * reports is the P2 bent cap's nine days coming through the lifts, the span 3
 * deck and its 28 days, not a number written here.
 *
 * 245 days from mobilisation to handover. The critical path is the piling rig
 * through both piers, the P2 bent cap and its 28 days, the span 2 and span 3
 * lifts, the span 3 deck and its 28 days before the wearing course. The
 * approaches and the river training are pinned to the low-water season after
 * the monsoon, which is when work in and beside the Indus can be done.
 *
 * Columns: id, parent, name, kind, working days, logic, the component its match
 * joins to, and the day it was declared complete (null where it is not). Logic
 * is space-separated: `A1120` is finish-to-start, `A1490+13` with a lag,
 * `A1310:SS+13` start-to-start, `A2050:FF+4` finish-to-finish, and `@150` a
 * start no earlier than day 150.
 */
type ProgrammeLeaf = [id: string, parent: string, name: string, kind: string, days: number, logic: string, component: string, doneOn: number | null];
type ProgrammeRow = [id: string, parent: string | null, name: string] | ProgrammeLeaf;

function isLeafRow(r: ProgrammeRow): r is ProgrammeLeaf {
  return r.length > 3;
}

const RELIEF_PROGRAMME: readonly ProgrammeRow[] = [
  ['A1000', null, 'Indus Relief Bridge'],
  ['A1100', 'A1000', 'Preliminaries'],
  ['A1110', 'A1100', 'Mobilisation and site establishment', 'Preliminaries', 21, '', 'Site establishment', 20],
  ['A1120', 'A1100', 'Setting out', 'Survey', 3, 'A1110:SS+14', 'All supports', 16],
  ['A1130', 'A1100', 'Casting yard and moulds', 'Preliminaries', 31, 'A1110:SS+10', 'Casting yard', 42],

  ['A1200', 'A1000', 'Abutment A1'],
  ['A1210', 'A1200', 'Excavation', 'Excavation', 6, 'A1120', 'Abutment A1 excavation', 20],
  ['A1220', 'A1200', 'Footing', 'Footing', 8, 'A1210', 'Abutment A1 footing', 27],
  ['A1230', 'A1200', 'Front wall', 'Wall', 11, 'A1220+7', 'Abutment A1 front wall', 43],
  ['A1240', 'A1200', 'Bearing beam', 'Wall', 8, 'A1230', 'Abutment A1 bearing beam', 54],
  ['A1250', 'A1200', 'Wing walls', 'Wing Wall', 11, 'A1240+3', 'Abutment A1 wing walls', 63],
  ['A1260', 'A1200', 'Bearings', 'Bearing', 3, 'A1240+23', 'Abutment A1 bearings', 85],
  ['A1270', 'A1200', 'Back wall', 'Wall', 11, 'A1620+4', 'Abutment A1 back wall', null],
  ['A1280', 'A1200', 'Backfill', 'Earthworks', 9, 'A1270+13', 'Abutment A1 backfill', null],
  ['A1290', 'A1200', 'Approach slab', 'Approach Slab', 8, 'A1280 A2020:SS+5', 'Abutment A1 approach slab', null],

  ['A1300', 'A1000', 'Pier P1'],
  ['A1310', 'A1300', 'Cofferdam and excavation', 'Excavation', 6, 'A1110 A1120', 'Pier P1 cofferdam', 26],
  ['A1320', 'A1300', 'F-1 piles', 'Pile', 5, 'A1310', 'Pier P1 F-1 piles', 32],
  ['A1330', 'A1300', 'F-2 piles', 'Pile', 5, 'A1320', 'Pier P1 F-2 piles', 36],
  ['A1340', 'A1300', 'Pile integrity tests', 'Test', 1, 'A1330+6', 'Pier P1 pile tests', 44],
  ['A1350', 'A1300', 'F-1 pile cap', 'Pile Cap', 9, 'A1340', 'Pier P1 F-1 pile cap', 50],
  ['A1360', 'A1300', 'F-2 pile cap', 'Pile Cap', 9, 'A1340 A1350:SS+3', 'Pier P1 F-2 pile cap', 53],
  ['A1370', 'A1300', 'C-1 column', 'Column', 9, 'A1350+3', 'Pier P1 C-1 column', 59],
  ['A1380', 'A1300', 'C-2 column', 'Column', 9, 'A1360+3', 'Pier P1 C-2 column', 67],
  ['A1390', 'A1300', 'Bent cap', 'Bent Cap', 30, 'A1370+7 A1380+7', 'Pier P1 bent cap', 94],
  ['A1395', 'A1300', 'Bearings', 'Bearing', 3, 'A1390+7', 'Pier P1 bearings', 108],

  ['A1400', 'A1000', 'Pier P2'],
  ['A1410', 'A1400', 'Cofferdam and excavation', 'Excavation', 6, 'A1310:SS+13', 'Pier P2 cofferdam', 42],
  ['A1420', 'A1400', 'F-1 piles', 'Pile', 5, 'A1410 A1330+3', 'Pier P2 F-1 piles', 46],
  ['A1430', 'A1400', 'F-2 piles', 'Pile', 5, 'A1420', 'Pier P2 F-2 piles', 57],
  ['A1440', 'A1400', 'Pile integrity tests', 'Test', 1, 'A1430+6', 'Pier P2 pile tests', 64],
  ['A1450', 'A1400', 'F-1 pile cap', 'Pile Cap', 9, 'A1440', 'Pier P2 F-1 pile cap', 70],
  ['A1460', 'A1400', 'F-2 pile cap', 'Pile Cap', 9, 'A1440 A1450:SS+3', 'Pier P2 F-2 pile cap', 73],
  ['A1470', 'A1400', 'C-1 column', 'Column', 9, 'A1450+3', 'Pier P2 C-1 column', 79],
  ['A1480', 'A1400', 'C-2 column', 'Column', 9, 'A1460+3', 'Pier P2 C-2 column', 82],
  ['A1490', 'A1400', 'Bent cap', 'Bent Cap', 30, 'A1470+7 A1480+7 A1390:SS+13', 'Pier P2 bent cap', 126],
  ['A1495', 'A1400', 'Bearings', 'Bearing', 3, 'A1490+7', 'Pier P2 bearings', 134],

  ['A1500', 'A1000', 'Abutment A2'],
  ['A1510', 'A1500', 'Excavation', 'Excavation', 6, 'A1220+24', 'Abutment A2 excavation', 60],
  ['A1520', 'A1500', 'Footing', 'Footing', 8, 'A1510', 'Abutment A2 footing', 66],
  ['A1530', 'A1500', 'Front wall', 'Wall', 11, 'A1520+7', 'Abutment A2 front wall', 83],
  ['A1540', 'A1500', 'Bearing beam', 'Wall', 8, 'A1530', 'Abutment A2 bearing beam', 94],
  ['A1550', 'A1500', 'Wing walls', 'Wing Wall', 11, 'A1540+3', 'Abutment A2 wing walls', 103],
  ['A1560', 'A1500', 'Bearings', 'Bearing', 3, 'A1540+23', 'Abutment A2 bearings', 124],
  ['A1570', 'A1500', 'Back wall', 'Wall', 11, 'A1820+3', 'Abutment A2 back wall', null],
  ['A1580', 'A1500', 'Backfill', 'Earthworks', 9, 'A1570+14', 'Abutment A2 backfill', null],
  ['A1590', 'A1500', 'Approach slab', 'Approach Slab', 8, 'A1580 A2050:SS', 'Abutment A2 approach slab', null],

  ['A1600', 'A1000', 'Span 1'],
  ['A1610', 'A1600', 'Girders — stress, cast and release', 'Girder', 31, 'A1130+4', 'Span 1 girders (yard)', 69],
  ['A1620', 'A1600', 'Girders — erect', 'Girder', 4, 'A1260 A1395+5 A1390+15 A1610', 'Span 1 girders', 128],
  ['A1630', 'A1600', 'Diaphragms', 'Diaphragm', 11, 'A1620', 'Span 1 diaphragms', null],
  ['A1640', 'A1600', 'Deck slab', 'Deck', 14, 'A1630+7', 'Span 1 deck slab', null],
  ['A1650', 'A1600', 'Barriers and walkways', 'Side Wall', 11, 'A1640+10', 'Span 1 barriers', null],

  ['A1700', 'A1000', 'Span 2'],
  ['A1710', 'A1700', 'Girders — stress, cast and release', 'Girder', 35, 'A1610:SS+12', 'Span 2 girders (yard)', 81],
  ['A1720', 'A1700', 'Girders — erect', 'Girder', 4, 'A1495+3 A1490+13 A1620+7 A1710', 'Span 2 girders', null],
  ['A1730', 'A1700', 'Diaphragms', 'Diaphragm', 11, 'A1720', 'Span 2 diaphragms', null],
  ['A1740', 'A1700', 'Deck slab', 'Deck', 14, 'A1730+7 A1640+1', 'Span 2 deck slab', null],
  ['A1750', 'A1700', 'Barriers and walkways', 'Side Wall', 11, 'A1740+10', 'Span 2 barriers', null],

  ['A1800', 'A1000', 'Span 3'],
  ['A1810', 'A1800', 'Girders — stress, cast and release', 'Girder', 37, 'A1710:SS+12', 'Span 3 girders (yard)', 99],
  ['A1820', 'A1800', 'Girders — erect', 'Girder', 4, 'A1720+3 A1560 A1810', 'Span 3 girders', null],
  ['A1830', 'A1800', 'Diaphragms', 'Diaphragm', 11, 'A1820', 'Span 3 diaphragms', null],
  ['A1840', 'A1800', 'Deck slab', 'Deck', 14, 'A1830+19 A1740+1', 'Span 3 deck slab', null],
  ['A1850', 'A1800', 'Barriers and walkways', 'Side Wall', 11, 'A1840+10', 'Span 3 barriers', null],

  ['A2000', 'A1000', 'Approaches and river training'],
  ['A2010', 'A2000', 'A1 approach embankment', 'Earthworks', 30, '@150', 'Approach A1 embankment', null],
  ['A2020', 'A2000', 'A1 approach subbase and base', 'Pavement', 20, 'A2010', 'Approach A1 pavement', null],
  ['A2030', 'A2000', 'A1 approach asphalt', 'Road Surface', 5, 'A2020 A1920:SS', 'Approach A1 asphalt', null],
  ['A2040', 'A2000', 'A2 approach embankment', 'Earthworks', 30, 'A2010:SS+15', 'Approach A2 embankment', null],
  ['A2050', 'A2000', 'A2 approach subbase and base', 'Pavement', 20, 'A2040', 'Approach A2 pavement', null],
  ['A2060', 'A2000', 'A2 approach asphalt', 'Road Surface', 5, 'A2050:FF+4 A1920:SS', 'Approach A2 asphalt', null],
  ['A2070', 'A2000', 'Scour protection at P1 and P2', 'River Training', 24, '@150', 'Scour protection', null],
  ['A2080', 'A2000', 'Bank pitching at A1 and A2', 'River Training', 30, 'A2070', 'Bank pitching', null],
  ['A2090', 'A2000', 'Drainage and lighting', 'Services', 15, 'A2020', 'Drainage and lighting', null],

  ['A1900', 'A1000', 'Finishes and handover'],
  ['A1910', 'A1900', 'Expansion joints', 'Expansion Joint', 7, 'A1840+14', 'Expansion joints', null],
  ['A1920', 'A1900', 'Wearing course', 'Road Surface', 6, 'A1840+28 A1910', 'Wearing course', null],
  ['A1930', 'A1900', 'Road markings and furniture', 'Lane Divider', 5, 'A1920 A2030 A2060', 'Road markings', null],
  ['A1940', 'A1900', 'Load test', 'Test', 3, 'A1930+1', 'Load test', null],
  ['A1950', 'A1900', 'Handover and demobilisation', 'Preliminaries', 16, 'A1940', 'Handover', null],
];

/**
 * The overpass's programme: the same kind of table in a dozen activities,
 * every one declared complete on the day its last work was accepted. Day zero
 * is the commencement, 2025-01-06.
 */
const OVERPASS_PROGRAMME: readonly ProgrammeRow[] = [
  ['B1000', null, 'N-5 Overpass at km 1588'],
  ['B1100', 'B1000', 'Preliminaries'],
  ['B1110', 'B1100', 'Mobilisation', 'Preliminaries', 21, '', 'Site establishment', 20],
  ['B1200', 'B1000', 'Substructure'],
  ['B1210', 'B1200', 'Abutment footings', 'Footing', 20, 'B1110', 'Abutments A1 and A2 footings', 45],
  ['B1220', 'B1200', 'Piles', 'Pile', 20, 'B1110', 'Piers P1 and P2 piles', 42],
  ['B1230', 'B1200', 'Pile caps', 'Pile Cap', 18, 'B1220+7', 'Piers P1 and P2 pile caps', 68],
  ['B1240', 'B1200', 'Columns and bent caps', 'Bent Cap', 45, 'B1230+3', 'Piers P1 and P2 columns and bent caps', 128],
  ['B1250', 'B1200', 'Abutment walls', 'Wall', 40, 'B1210+7', 'Abutments A1 and A2 walls', 112],
  ['B1300', 'B1000', 'Superstructure'],
  ['B1310', 'B1300', 'Girders — stress, cast and release', 'Girder', 60, 'B1110+20', 'Girders (yard)', 115],
  ['B1320', 'B1300', 'Girders — erect', 'Girder', 8, 'B1240+28 B1250 B1310', 'All girders', 165],
  ['B1330', 'B1300', 'Deck slab', 'Deck', 40, 'B1320+7', 'Deck slab', 215],
  ['B1340', 'B1300', 'Barriers and footways', 'Side Wall', 25, 'B1330+10', 'Barriers', 245],
  ['B1900', 'B1000', 'Finishes and handover'],
  ['B1910', 'B1900', 'Wearing course', 'Road Surface', 10, 'B1340', 'Wearing course', 262],
  ['B1920', 'B1900', 'Handover', 'Preliminaries', 14, 'B1910', 'Handover', 282],
];

/**
 * A site's calendar: seven days a week, which is how the contractor works the
 * river season, and no holidays entered. Invented, and it says so.
 */
function siteCalendar(id: string): WorkCalendar {
  return {
    id: `${DEMO_SITE_PREFIX}calendar-${id}`,
    name: 'Site, seven days',
    week: '1111111',
    holidays: [],
    windows: [],
    provisional: true,
  };
}

/** A programme as the modeller would serialise it, for the solver to freeze. */
function scheduleOf(programme: readonly ProgrammeRow[], start: string): SerialisedSchedule {
  const leaves = programme.filter(isLeafRow);
  const links: NonNullable<SerialisedSchedule['links']> = [];
  const pinned = new Map<string, number>();
  for (const [id, , , , , logic] of leaves) {
    for (const token of logic.split(/\s+/).filter(Boolean)) {
      const at = /^@(\d+)$/.exec(token);
      if (at) {
        pinned.set(id, Number(at[1]));
        continue;
      }
      const m = /^([AB]\d+)(?::(FS|SS|FF|SF))?([+-]\d+)?$/.exec(token)!;
      links.push({ from: m[1]!, to: id, type: m[2] ?? 'FS', lagDays: Number(m[3] ?? 0) });
    }
  }
  return {
    projectStartIso: start,
    activities: programme.map((r) => ({
      id: r[0],
      name: r[2],
      parentId: r[1],
      children: programme.filter((c) => c[1] === r[0]).map((c) => c[0]),
      durationDays: isLeafRow(r) ? r[4] : 0,
      manualStartDays: pinned.get(r[0]) ?? null,
      componentKind: isLeafRow(r) ? r[3] : '',
    })),
    links,
  };
}

/**
 * The contract the programme is administered against. Invented: the contractor
 * is invented and says so, and the value is a figure of the size a three-span
 * river bridge on the N-5 would carry.
 */
function reliefContract(): ContractHeader {
  return {
    contractor: 'Indus Bridgeworks JV',
    contractNo: 'NHA/N5/IRB-02',
    value: 1_480_000_000,
    currency: 'PKR',
    commencement: reliefDay(0),
    completionDays: 245,
    milestones: [
      { dueOn: reliefDay(120), name: 'Substructure complete' },
      { dueOn: reliefDay(185), name: 'Deck complete' },
      { dueOn: reliefDay(244), name: 'Taking over' },
    ],
  };
}

/**
 * A frozen, accepted baseline and the matches that join it to the works: taken
 * by the planner, accepted by the approver, matched by the planner. Each match
 * carries the component id its works agree on, where they agree on one (LC-14).
 */
function programmeOf(
  bridgeId: string,
  key: string,
  programme: readonly ProgrammeRow[],
  day: (n: number) => string,
  contract: ContractHeader,
  works: readonly ProgressEntry[],
): { baseline: Baseline; matches: ScheduleMatch[] } {
  const calendar = siteCalendar(key);
  const schedule = scheduleOf(programme, day(0));
  const baseline: Baseline = {
    id: `${DEMO_SITE_PREFIX}baseline-${key}`,
    bridgeId,
    name: 'Contract programme, Rev 0',
    projectStart: day(0),
    modelId: null,
    takenBy: PEOPLE.planner,
    takenAt: `${day(-6)}T09:00:00Z`,
    note: '',
    /* Each with the component its row names: the place the Scheduler's Simulate
       reads, and "(yard)" for girders cast off the structure, not put up. */
    activities: baselineFrom(schedule, calendar, day(0)).map((a) => {
      const row = programme.find((r): r is ProgrammeLeaf => isLeafRow(r) && r[0] === a.activityId);
      return row ? { ...a, component: row[6] } : a;
    }),
    state: 'accepted',
    reason: null,
    acceptedBy: PEOPLE.approver,
    acceptedAt: `${day(-3)}T10:00:00Z`,
    calendarId: calendar.id,
    calendar,
    links: baselineLinks(schedule),
    contract,
  };
  const matches: ScheduleMatch[] = programme.filter(isLeafRow).map(([id, , name, , , , component, doneOn], i) => ({
    id: `${DEMO_SITE_PREFIX}match-${key}-${String(i + 1).padStart(3, '0')}`,
    bridgeId,
    activityId: id,
    activityName: name,
    component,
    componentId: idForLabel(works, component),
    completedOn: doneOn === null ? null : day(doneOn),
    matchedBy: PEOPLE.planner,
    matchedAt: `${day(0)}T09:30:00Z`,
  }));
  return { baseline, matches };
}

/** The Indus Relief Bridge's programme, and its two claims for more time. */
function reliefProgramme(): { baseline: Baseline; matches: ScheduleMatch[]; extensions: ContractExtension[] } {
  const { baseline, matches } = programmeOf(reliefId(), 'atk', RELIEF_PROGRAMME, reliefDay, reliefContract(), reliefWorks());
  /* The snowmelt flood that overtopped the P2 cofferdam (day 50), claimed and
     granted; and the late strand, claimed and still with the approver. */
  const extensions: ContractExtension[] = [
    {
      id: `${DEMO_SITE_PREFIX}eot-atk-1`,
      bridgeId: reliefId(),
      reason: 'P2 cofferdam overtopped by the Indus on snowmelt; channel works suspended.',
      days: 5,
      state: 'accepted',
      raisedBy: PEOPLE.planner,
      raisedAt: `${reliefDay(58)}T09:00:00Z`,
      decidedBy: PEOPLE.approver,
      decidedAt: `${reliefDay(66)}T11:00:00Z`,
    },
    {
      id: `${DEMO_SITE_PREFIX}eot-atk-2`,
      bridgeId: reliefId(),
      reason: 'Prestressing strand delivered a week late by the nominated supplier.',
      days: 4,
      state: 'proposed',
      raisedBy: PEOPLE.planner,
      raisedAt: `${reliefDay(96)}T09:00:00Z`,
      decidedBy: null,
      decidedAt: null,
    },
  ];
  return { baseline, matches, extensions };
}

/** Day `n` of the overpass contract. Not slid. */
function overpassDay(n: number): string {
  return addDays('2025-01-06', n);
}

/** The overpass's programme, all of it done. It ran twelve days past its 270. */
function overpassProgramme(): { baseline: Baseline; matches: ScheduleMatch[]; extensions: ContractExtension[] } {
  const contract: ContractHeader = {
    contractor: 'Margalla Civil Works',
    contractNo: 'NHA/N5/OVP-1588',
    value: 1_120_000_000,
    currency: 'PKR',
    commencement: overpassDay(0),
    completionDays: 270,
    milestones: [{ dueOn: overpassDay(269), name: 'Taking over' }],
  };
  const { baseline, matches } = programmeOf(overpassId(), 'ovp', OVERPASS_PROGRAMME, overpassDay, contract, overpassWorks());
  return { baseline, matches, extensions: [] };
}

/**
 * The fabricated daily returns, incidents, documents, tests and register items
 * for one structure.
 *
 * Slid by the same days as the works, so all of them stay on one calendar.
 * Newest first, which is the order the register sends them in.
 */
export function demoSiteRecordFor(
  tenant: string,
  bridgeId: string,
): { returns: SiteReturn[]; incidents: SiteIncident[]; documents: SiteDocument[]; tests: SiteTest[]; items: SiteItem[] } {
  const dpwh = tenant === 'ph-dpwh' ? dpwhSiteRecordFor(tenant, bridgeId) : null;
  if (dpwh) return dpwh;
  if (!demoEnabled() || tenant !== 'pk-fwo' || bridgeId !== reliefId()) {
    return { returns: [], incidents: [], documents: [], tests: [], items: [] };
  }
  const days = demoDayShift();
  const works = demoProgressFor(tenant, bridgeId);
  const slide = (iso: string) => addDays(iso, days);
  const newestFirst = <T>(on: (x: T) => string) => (a: T, b: T) => on(b).localeCompare(on(a));
  const tests = reliefTests(works);
  return {
    returns: reliefReturns(reliefWorks())
      .map((r) => ({
        ...r,
        returnedOn: slide(r.returnedOn),
        createdAt: slide(r.createdAt),
        updatedAt: slide(r.updatedAt),
        reviewedAt: r.reviewedAt === null ? null : slide(r.reviewedAt),
      }))
      .sort(newestFirst((r) => r.returnedOn)),
    incidents: reliefIncidents()
      .map((r) => ({
        ...r,
        occurredOn: slide(r.occurredOn),
        closedOn: r.closedOn ? slide(r.closedOn) : null,
        createdAt: slide(r.createdAt),
        updatedAt: slide(r.updatedAt),
      }))
      .sort(newestFirst((r) => r.occurredOn)),
    documents: reliefDocuments(works)
      .map((d) => ({
        ...d,
        datedOn: d.datedOn === null ? null : slide(d.datedOn),
        createdAt: slide(d.createdAt),
      }))
      .sort(newestFirst((d) => d.createdAt)),
    tests: tests
      .map((t) => ({ ...t, testedOn: slide(t.testedOn), createdAt: slide(t.createdAt) }))
      .sort(newestFirst((t) => t.testedOn)),
    items: reliefItems(tests)
      .map((i) => ({
        ...i,
        dueOn: i.dueOn ? slide(i.dueOn) : null,
        raisedAt: slide(i.raisedAt),
        answeredAt: i.answeredAt ? slide(i.answeredAt) : null,
        closedAt: i.closedAt ? slide(i.closedAt) : null,
        updatedAt: slide(i.updatedAt),
      }))
      .sort(newestFirst((i) => i.raisedAt)),
  };
}

/**
 * The fabricated programme for one structure, and the matches joining it to the
 * works — or null, which leaves whatever the register holds in place.
 *
 * The construction bridge's is slid by the same days as its works, so plan and
 * record stay on one calendar and the variance between them is the same
 * whichever day it is opened. The overpass's is history and is not.
 */
export function demoScheduleFor(
  tenant: string,
  bridgeId: string,
): { baseline: Baseline; matches: ScheduleMatch[]; extensions: ContractExtension[] } | null {
  if (tenant === 'ph-dpwh') return dpwhScheduleFor(tenant, bridgeId);
  if (!demoEnabled() || tenant !== 'pk-fwo') return null;
  if (bridgeId === overpassId()) return overpassProgramme();
  if (bridgeId !== reliefId()) return null;
  const days = demoDayShift();
  const { baseline, matches, extensions } = reliefProgramme();
  /* Sliding a solved plan is only the same plan because the site's calendar
     works every day: on a Mon–Sat calendar a slide of three days would put
     work on Sundays. */
  const contract = baseline.contract!;
  return {
    baseline: {
      ...baseline,
      projectStart: addDays(baseline.projectStart, days),
      takenAt: addDays(baseline.takenAt, days),
      acceptedAt: baseline.acceptedAt ? addDays(baseline.acceptedAt, days) : null,
      activities: baseline.activities.map((a) => ({
        ...a,
        plannedStart: addDays(a.plannedStart, days),
        plannedFinish: addDays(a.plannedFinish, days),
        ...(a.notBefore ? { notBefore: addDays(a.notBefore, days) } : {}),
      })),
      contract: {
        ...contract,
        commencement: addDays(contract.commencement, days),
        milestones: contract.milestones.map((m) => ({ ...m, dueOn: addDays(m.dueOn, days) })),
      },
    },
    matches: matches.map((m) => ({
      ...m,
      completedOn: m.completedOn === null ? null : addDays(m.completedOn, days),
      matchedAt: addDays(m.matchedAt, days),
    })),
    extensions: extensions.map((e) => ({
      ...e,
      raisedAt: addDays(e.raisedAt, days),
      decidedAt: e.decidedAt === null ? null : addDays(e.decidedAt, days),
    })),
  };
}

/* --------------------------- the phase and the handover --------------------------- */

/**
 * Each invented structure's phase history (LC-10): what `GET /api/bridges/:id/phase`
 * would answer had the rows been written. Attock was restricted by its rating;
 * the Indus Relief Bridge was created as planned and started on its baseline;
 * the overpass went on to its handover. Every other record here has none — in
 * service, not recorded, as any register imported without phases reads.
 */
function phaseRows(bridgeId: string): Omit<PhaseTransition, 'id' | 'bridgeId' | 'seq'>[] {
  if (bridgeId === 'PK-N5-1602') {
    const rating = attockRating();
    return [
      {
        fromPhase: 'in-service', toPhase: 'in-service', restricted: true, at: rating.acceptedAt!, by: rating.acceptedBy,
        reason: `Posted ${postingTonnes(rating.result)} t by accepted rating ${rating.id}.`, causeKind: 'rating', causeId: rating.id,
      },
    ];
  }
  if (bridgeId === reliefId()) {
    const { baseline } = reliefProgramme();
    const days = demoDayShift();
    return [
      { fromPhase: null, toPhase: 'planned', restricted: false, at: addDays(`${reliefDay(-40)}T08:30:00Z`, days), by: PEOPLE.planner, reason: null, causeKind: 'create', causeId: null },
      { fromPhase: 'planned', toPhase: 'construction', restricted: false, at: addDays(baseline.acceptedAt!, days), by: PEOPLE.approver, reason: null, causeKind: 'baseline', causeId: baseline.id },
    ];
  }
  if (bridgeId === overpassId()) {
    const { baseline } = overpassProgramme();
    return [
      { fromPhase: null, toPhase: 'planned', restricted: false, at: '2024-11-18T08:30:00Z', by: PEOPLE.planner, reason: null, causeKind: 'create', causeId: null },
      { fromPhase: 'planned', toPhase: 'construction', restricted: false, at: baseline.acceptedAt!, by: PEOPLE.approver, reason: null, causeKind: 'baseline', causeId: baseline.id },
      { fromPhase: 'construction', toPhase: 'in-service', restricted: false, at: OVERPASS_HANDOVER.acceptedAt!, by: OVERPASS_HANDOVER.acceptedBy, reason: null, causeKind: 'handover', causeId: OVERPASS_HANDOVER.id },
    ];
  }
  return [];
}

/** The phase history of one invented structure, oldest first, or none. */
export function demoPhaseFor(tenant: string, bridgeId: string): PhaseTransition[] {
  if (tenant === 'ph-dpwh') return dpwhPhaseFor(tenant, bridgeId);
  if (!demoEnabled() || tenant !== 'pk-fwo') return [];
  return phaseRows(bridgeId).map((row, i) => ({
    ...row,
    id: `${DEMO_SITE_PREFIX}phase-${bridgeId}-${i + 1}`,
    bridgeId,
    seq: i + 1,
  }));
}

/** An inventory value as the as-built model states it. */
function fromModel(value: number | string): HandoverValue {
  return { value, source: 'model', modelValue: value };
}

/**
 * The overpass's handover (LC-11): prepared by the resident engineer, accepted
 * by the approver, the as-built model its model of record, and every inventory
 * value the one the model fixture states — `npm test` reads the fixture through
 * `readCloudInventory` and holds these to it. Nothing was open to carry over.
 */
const OVERPASS_HANDOVER: Handover = {
  id: 'demo-site-handover-1588',
  bridgeId: 'PK-N5-1588',
  status: 'accepted',
  completedOn: '2025-10-15',
  dlpMonths: 12,
  dlpEndsOn: '2026-10-15',
  modelId: 'demo-model-1588-asbuilt',
  inventory: {
    spans: fromModel(3),
    length: fromModel(75),
    spanLength: fromModel(25),
    deckWidth: fromModel(11),
    girders: fromModel(5),
    bearings: fromModel('Alternating pin and roller'),
    abutmentFoundation: fromModel('Spread footing'),
    pierFoundation: fromModel('Two pile caps of 4 piles, 1.0 m diameter, 12.5 m long'),
  } satisfies Record<HandoverFieldKey, HandoverValue>,
  modelNotes: [],
  carry: [],
  waivers: [],
  note: 'Taking-over certificate issued on the completion date; no hold point or NCR open.',
  preparedBy: PEOPLE.resident,
  preparedAt: '2025-10-17T10:00:00Z',
  acceptedBy: PEOPLE.approver,
  acceptedAt: '2025-10-24T11:00:00Z',
  baselineDueOn: '2025-10-15',
  updatedAt: '2025-10-24T11:00:00Z',
};

/** The handover of one invented structure, as `GET /api/bridges/:id/handover` answers, or null. */
export function demoHandoverFor(tenant: string, bridgeId: string): HandoverView | null {
  if (!demoEnabled() || tenant !== 'pk-fwo' || bridgeId !== overpassId()) return null;
  return { handover: { ...OVERPASS_HANDOVER, inventory: { ...OVERPASS_HANDOVER.inventory } }, blocking: [], carry: [] };
}

/* --------------------------- the load rating --------------------------- */

/**
 * TWO LOAD RATINGS, AND THEY ARE INVENTED LIKE EVERYTHING ABOVE.
 *
 * ATTOCK BRIDGE. The record says a load restriction was brought down from 15 t
 * on the strength of the October 2024 principal inspection, and it carries the
 * defect that did it — strand corrosion in the soffit of exterior girder G1 at
 * midspan of span 2, nine of the girder's eighteen strands corroded through
 * (dfk0010). This is the rating that decision rested on.
 *
 * THE OVERPASS AT KM 1588. Its first rating, a month after its handover: the
 * same girder with nothing lost, recorded as inspected with no loss found at
 * the baseline inspection. It posts nothing.
 *
 * THE SECTION IS THE MODEL'S. Every value below is what "Fill from model" reads
 * from the model fixture — three 25 m spans of five AASHTO Type VI girders at
 * 2.5 m on an 11 m deck, f′c 6000 psi, eighteen pretensioned Grade 270 strands
 * of 15.2 mm — so the rating is of the structure the 3D view shows. The engine
 * is the real PCDG one (src/cbplr/pcdg.ts), not a hand method.
 *
 * WHAT IS INVENTED IS THE DAMAGE. Nine of eighteen strands is a 50.0% loss of
 * tendon section, and it is fitted to the story: it puts the lowest posting at
 * 14.2 t. Stirrups and web were found sound. Exterior girder, span 2, between
 * piers 1 and 2, at midspan — where the rating applies it.
 *
 * AND IT IS NOT COPIED BY HAND (LC-20). The tendon loss is what the rating form
 * fills from the register — defect dfk0010 on "Span-2 Girder G1 Soffit", read
 * through src/rating/damage.ts against the October 2024 principal inspection —
 * and the stirrups and web are recorded as inspected with no loss found. The
 * posting on the register is the one acceptance writes from it (LC-23). Rated
 * by the rater and accepted by the approver, never the same person (LC-08).
 */
const ATTOCK_ID = 'PK-N5-1602';
const ATTOCK_DAMAGE = damageFromInspection(
  FWO_DEMO_BRIDGES.find((b) => b.id === ATTOCK_ID)!,
  FWO_DEMO_DEFECTS,
  { family: 'PCDG', position: 'exterior', spanIndex: 2, girderCount: 5 },
);

/**
 * 18 strands × 139 mm² × 1396.2 MPa, less the model's 25% initial losses — the
 * working force AFTER losses, which is what the field asks for.
 */
const WORKING_FORCE = 2620;

/**
 * THE LOSS IS COUNTED ONCE (LC-71). The engine finds the tendon area as the
 * working force over (0.75 fu − loss), with the loss taken as this fraction of
 * the force (correction C19). The force above is already net of the model's
 * 25%, so the loss that divides it back to the strands is that same 25% of the
 * jacking stress — not the workbook's 0.03 on top of it, which left the rating
 * 1988 mm² of steel where the model has 2502. With this the engine's area is
 * the model's eighteen strands exactly.
 */
const LOSS_FRACTION = (0.25 * 1396.2) / WORKING_FORCE;

const ATTOCK_GIRDER: PcdgInput = {
  position: 'exterior',
  span: 25,
  girderSpacing: 2.5,
  girderCount: 5,
  /* Half the 2.5 m spacing plus the 0.5 m overhang: five girders on an 11 m deck. */
  slabWidth: 1.75,
  sidewalkWidth: 1.2,
  section: {
    height: 1.829,
    slabThickness: 0.225,
    upperFlangeWidth: 1.067,
    upperFlangeThickness: 0.127,
    taperDepth: 0.076,
    fillet: 0.102,
    webWidth: 0.203,
    lowerFillet: 0.254,
    lowerFlangeWidth: 0.711,
    lowerFlangeThickness: 0.203,
  },
  prestress: {
    workingForce: WORKING_FORCE,
    centroidFromSoffit: 66.5,
    ultimateStrength: 1861.6,
    yieldStrength: 1689.9,
    lossFraction: LOSS_FRACTION,
    /* Nine of eighteen strands corroded through — dfk0010, as the form fills it. */
    sectionLoss: ATTOCK_DAMAGE.fields['prestress.sectionLoss'] ?? 0,
  },
  girderStrength: 41.4,
  slabStrength: 27.6,
  rebarYield: 413.7,
  stirrupDiameter: 16,
  stirrupSpacing: 300,
  stirrupSectionLoss: 0,
  shearCrackDepth: 0,
  /* One 300 × 1426 mm midspan diaphragm over half the clear bay, as the line
     load with the same midspan moment. */
  diaphragm: 0.943,
  barrierRail: 8.03,
  wearingCourse: 50,
  concreteUnitWeight: 24,
  asphaltUnitWeight: 22,
};

/** The overpass's girder: the same section, with nothing lost. */
const OVERPASS_GIRDER: PcdgInput = {
  ...ATTOCK_GIRDER,
  prestress: { ...ATTOCK_GIRDER.prestress, sectionLoss: 0 },
};

/** Where each value came from: the model, except the inspection's own. */
function sourcesOf(input: PcdgInput): Record<string, string> {
  const sources: Record<string, string> = {};
  const walk = (o: object, prefix: string): void => {
    for (const [key, value] of Object.entries(o)) {
      if (key === 'position') continue;
      if (typeof value === 'object' && value !== null) walk(value, `${prefix}${key}.`);
      else sources[`${prefix}${key}`] = 'model';
    }
  };
  walk(input, '');
  sources['prestress.sectionLoss'] = 'inspection';
  sources['stirrupSectionLoss'] = 'inspection';
  sources['shearCrackDepth'] = 'inspection';
  return sources;
}

/** What the damage inputs rest on: the defect for the tendons, none found for the rest. */
const ATTOCK_BASIS: Record<string, string> = {
  ...ATTOCK_DAMAGE.basis,
  stirrupSectionLoss: NO_LOSS,
  shearCrackDepth: NO_LOSS,
};

/** One accepted demonstration rating of an exterior girder in span 2. */
function ratingOf(
  id: string,
  bridgeId: string,
  input: PcdgInput,
  damage: { inspectionId: string | null; defectIds: readonly string[]; basis: Record<string, string> },
  ratedAt: string,
  acceptedAt: string,
  note: string,
): RatingRecord {
  const result = ratePcdgSimple(input, DPWH_CBPLR).result;
  const g = result.governing;
  return {
    id,
    bridgeId,
    family: 'PCDG',
    continuity: 'simple',
    position: 'exterior',
    spanIndex: 2,
    profileId: DPWH_CBPLR.id,
    profileVersion: DPWH_CBPLR.version,
    engineVersion: 'pcdg-simple',
    exteriorDf: DPWH_CBPLR.exteriorDf,
    input,
    sources: sourcesOf(input),
    result,
    corrections: [],
    governingRf: g?.rf ?? null,
    governingCheck: g?.checkLabel ?? null,
    governingMethod: g?.method ?? null,
    governingLevel: g?.level ?? null,
    postingRequired: result.postingRequired,
    inspectionId: damage.inspectionId,
    defectIds: damage.defectIds,
    damageBasis: damage.basis,
    state: 'accepted',
    ratedBy: PEOPLE.rater,
    ratedAt,
    acceptedBy: PEOPLE.approver,
    acceptedAt,
    note,
  };
}

function attockRating(): RatingRecord {
  return ratingOf(
    'demo-rating-pk-1602-1',
    ATTOCK_ID,
    ATTOCK_GIRDER,
    { inspectionId: ATTOCK_DAMAGE.inspectionId, defectIds: ATTOCK_DAMAGE.defectIds, basis: ATTOCK_BASIS },
    '2024-11-01T09:30:00Z',
    '2024-11-06T15:10:00Z',
    'Span 2, exterior girder G1, section from the model. Tendon section taken at 50.0% loss — nine of ' +
      'eighteen strands corroded through at midspan (defect dfk0010).',
  );
}

function overpassRating(): RatingRecord {
  return ratingOf(
    'demo-rating-pk-1588-1',
    'PK-N5-1588',
    OVERPASS_GIRDER,
    {
      inspectionId: 'INSP-PK-1588-A',
      defectIds: [],
      basis: { 'prestress.sectionLoss': NO_LOSS, stirrupSectionLoss: NO_LOSS, shearCrackDepth: NO_LOSS },
    },
    '2025-11-10T09:30:00Z',
    '2025-11-17T14:00:00Z',
    'First rating after handover. Span 2, exterior girder G1, section from the as-built model; inspected at ' +
      'handover with no loss found.',
  );
}

/* THE POSTING ON THE REGISTER IS THE RATING'S (LC-23): the tonnes accepting it
   writes, marked as coming from it, and the restriction it made — set here,
   after both are defined, so the record and the rating cannot disagree. And
   every invented inspection names the inspector who made it (LC-72). */
{
  const rating = attockRating();
  const attock = FWO_DEMO_BRIDGES.find((b) => b.id === ATTOCK_ID)!;
  attock.loadRestriction = postingTonnes(rating.result);
  attock.posting = { source: 'rating', ratingId: rating.id, by: rating.acceptedBy, at: rating.acceptedAt };
  attock.phase = 'in-service';
  attock.restricted = rating.postingRequired;
  attock.phaseAt = rating.acceptedAt;
  attock.phaseBy = rating.acceptedBy;
  for (const b of FWO_DEMO_BRIDGES) for (const i of b.inspections) i.inspector ??= PEOPLE.inspector;
}

/** Whether a rating is one of this file's rather than the deployment's. */
export function isDemoRating(r: { id: string }): boolean {
  return r.id.startsWith('demo-rating-');
}

/**
 * The bundled ratings for one department, or none — added to what the deployment
 * holds by Bridge Rating's `loadRegister`, the way `hydrate` adds the register.
 */
export function demoRatingsFor(tenant: string): RatingRecord[] {
  if (tenant === 'ph-dpwh') return dpwhRatingsFor(tenant);
  if (!demoEnabled() || tenant !== 'pk-fwo') return [];
  return [attockRating(), overpassRating()];
}
